import { forAutomaticLayout } from '@/lib/photoDedup';
import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { FIVE_UP_TOP_FRAC, FOUR_MAIN_TOP_FRAC } from '@/lib/slotGeometry';
import { gapCss as photoGapCss, slotArs as photoGapSlotArs, photoGapOf, withThemedBook, framePaddingCss, twoRowFlexCss, evenRowPct, EVEN_CONTAINER, type PhotoGap } from '@/lib/photoGap';
import { isThemedBook, type BookPageLike } from '@/lib/bookStructure';
import { printedSpreads } from '@/lib/printPageNumbers';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTapToOpen } from '@/lib/tapToOpen';
import { useQuery } from '@tanstack/react-query';
import AlbumFlowPageV2 from './AlbumFlowPageV2';
import { useAuthStore } from '@/store/useAuthStore';
import { AlbumSubmittedScreen } from '@/components/AlbumSubmittedScreen';
import { isAwaitingConfirmation } from '@/lib/editorAccess';
import { normDest, destinationMatchScore } from '@/lib/coverIcons';
import { galleryAccept, isIosDevice } from '@/lib/iosPicker';
import { processForUpload, processForUploadWithDims } from '@/lib/imagePipeline';
import { PRINT_MAX_DIM, PRINT_JPEG_QUALITY, DISPLAY_MAX_DIM, DISPLAY_JPEG_QUALITY, TILE_MAX_DIM, TILE_JPEG_QUALITY } from '@/lib/imageLimits';
import { makeThumbnail } from '@/lib/makeThumbnail';
import { uploadViaTus } from '@/lib/tusUpload';
import { serverVerifyOriginals, describeMissingOriginals, confirmOriginalLanded, type MissingOriginal } from '@/lib/originalsGate';
import { savePendingOriginal, deletePendingOriginal, listPendingOriginals, gcPendingOriginals, getPendingPayload } from '@/lib/pendingOriginals';
import heic2any from 'heic2any';
import {
  ordersApi,
  sessionsApi,
  photosApi,
  coversApi,
  coverSearchesApi,
  coverPinsApi,
  settingsApi,
  aiApi,
  templatesApi,
  uploadToSignedUrl,
  type Order,
  type Photo,
  type TusBlock,
  type Cover,
  type CoverCategory,
  type AlbumSession,
  type LayoutPage,
  type LayoutPlan,
  type LayoutTemplate,
} from '@/lib/api';
import { analyzePhoto } from '@/lib/photoAnalyzer';
import { photosKey } from '@/lib/photosKey';
import { normalizeJpegOrientation } from '@/lib/normalizeOrientation';
import { sha256Hex } from '@/lib/contentHash';
import { type PdfGenProgress } from '@/lib/pdfGenerator';
import { cn, getCheckoutRedirect, getThankYouRedirect } from '@/lib/utils';
import { HelpFAB } from '@/components/HelpFAB';
import { detectDevice } from '@/lib/detectDevice';
import { isOutroPage } from '@/lib/outroPage';
import { computeOrderPrice, liveExtraPagesForSingleAlbum, resolveBasePagesPerAlbum, resolvePricePerPage } from '@/lib/orderPricing';
import { advanceSplit, codOptionCopy } from '@/lib/codAdvance';
import { useT } from '@/lib/i18n';
import { waSupportLink } from '@/lib/whatsappSupport';
import { setAdvancedMatching } from '@/lib/pixels';
import type { TextZoneOverride, ExtraTextZone } from '@/components/CoverThumbnail';
import { pickerZoneStyles, pickerFontLoadSpecs } from '@/lib/pickerCoverText';
import { pickerThumbUrl } from '@/lib/coverImageSource';
import { CoverSpreadPreview } from '@/components/CoverSpreadPreview';
import { TemplatedPhotoSlot, type PhotoFitInSlot } from '@/components/TemplatedPhotoSlot';
import { TemplatedPageWithSvgFetch } from '@/components/TemplatedPage';
import {
  composeFinalLayout,
  withSlotPositions,
  pdfMirrorBox,
  wideTopSlotARs,
  computeWideTopFrac,
  WIDE_TOP_AR_MIN,
  type ComposablePhoto,
} from './AlbumEditorPage';
import { type AlbumCategory } from '@/lib/api';
import { ThemedPageView } from '../../components/page/ThemedPageView';
import { soloLandscapeFitWidth } from '@/lib/soloFit';
import { supabase } from '@/lib/supabase';
import { IosUploadNote } from '@/components/IosUploadNote';
import { UploadedTileGrid } from '@/components/upload/UploadedTileGrid';
import { titlePrefill } from '@/lib/titleSource';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

// ─── Constants ───────────────────────────────────────────────────────────────

// Customer-flow color palette — pinned to the canonical brand tokens in
// `lib/brand.ts` so admin + customer renders match. Field names are kept
// local-friendly (gold/dark/muted/…) to minimize churn at the ~200+ call
// sites in this file.
const C = {
  gold:       '#C9A24A',  // BRAND.gold      (canonical)
  goldLight:  '#D4B86A',  // BRAND.goldLight
  goldDark:   '#8B6F26',  // BRAND.goldDark
  goldDeep:   '#A8842F',  // BRAND.goldDeep
  dark:       '#1F2937',  // BRAND.dark
  darkSoft:   '#2A2A2A',
  beige:      '#F5EFE0',  // BRAND.beige
  beigeLight: '#FAF6EC',  // BRAND.beigeLight
  white:      '#FFFFFF',
  muted:      '#6B7280',  // BRAND.muted
  danger:     '#EF4444',  // BRAND.error
  success:    '#10B981',  // BRAND.success
};

/** Full-screen "Préparation de votre album" overlay — shown ONLY when the
 * customer reaches the final click before the background print-quality
 * uploads finished. Replaces the bare spinner modal that customers read as
 * frozen (2026-07-08: a 55-photo album sat minutes at "15/55" with no time
 * estimate → abandon risk). Adds a live remaining-time estimate from the
 * observed landing rate + a slow-connection reassurance line when the count
 * hasn't moved for 20s. Module scope so parent re-renders don't remount it. */
function FinalizingOverlay({ done, total }: { done: number; total: number }) {
  // Rate samples: one entry per distinct `done` value, capped at the last 8.
  const samplesRef = useRef<Array<{ t: number; done: number }>>([]);
  const lastChangeRef = useRef<number>(Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 3000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const t = Date.now();
    const s = samplesRef.current;
    if (s.length === 0 || s[s.length - 1].done !== done) {
      s.push({ t, done });
      if (s.length > 8) s.shift();
      lastChangeRef.current = t;
    }
  }, [done]);
  const s = samplesRef.current;
  let eta: string | null = null;
  if (s.length >= 2) {
    const dt = s[s.length - 1].t - s[0].t;
    const dd = s[s.length - 1].done - s[0].done;
    if (dd > 0 && dt > 0) {
      const remainingMs = ((total - done) / dd) * dt;
      if (remainingMs < 65_000) eta = 'moins d’une minute restante';
      else if (remainingMs < 5 * 60_000) eta = `≈ ${Math.ceil(remainingMs / 60_000)} min restantes`;
      else eta = 'encore quelques minutes…';
    }
  }
  // 45s (was 20s): with 10 parallel sends, files land in CLUMPS — a 20s
  // quiet gap between clumps is normal on a slow uplink and made the
  // "connexion lente" line show during perfectly healthy sends.
  const stalled = now - lastChangeRef.current > 45_000;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        <div className="mx-auto w-16 h-16 mb-6 relative">
          <div className="absolute inset-0 rounded-full border-4" style={{ borderColor: `${C.gold}20` }} />
          <div className="absolute inset-0 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: C.gold, borderTopColor: 'transparent' }} />
        </div>
        <h3 className="text-lg font-bold mb-2" style={{ color: C.dark, fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" }}>
          Préparation de votre album
        </h3>
        <p className="text-sm mb-4" style={{ color: C.muted }}>
          Vos photos partent en pleine qualité pour l&rsquo;impression&hellip;
        </p>
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: `${C.gold}15` }}>
          <div className="h-full transition-all"
            style={{
              width: `${pct}%`,
              background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`,
            }} />
        </div>
        <p className="text-xs mt-4 font-semibold tabular-nums" style={{ color: C.gold }}>
          {done} / {total}{eta ? ` · ${eta}` : ''}
        </p>
        <p className="text-xs mt-3" style={{ color: C.muted }}>
          {stalled
            ? 'Connexion lente — l’envoi continue, merci de patienter…'
            : 'Merci de garder cette page ouverte.'}
        </p>
      </div>
    </div>
  );
}

/** Discreet, NON-blocking print-upload progress chip (2026-07-08, Option C).
 * Shown bottom-left during preview/checkout while background print originals
 * are still draining, so the eventual (rare) blocking overlay is never a
 * surprise. Progress is weighted by BYTES, not file count — smoother and
 * honest when photo sizes vary. Display-only: reads the tracker, never
 * touches the upload transport. */
function BgUploadChip({ doneBytes, totalBytes, doneCount, totalCount }: {
  doneBytes: number; totalBytes: number; doneCount: number; totalCount: number;
}) {
  if (totalCount === 0 || doneCount >= totalCount) return null;
  const pct = totalBytes > 0 ? Math.min(99, Math.round((doneBytes / totalBytes) * 100)) : 0;
  return (
    <div
      className="fixed bottom-4 left-4 z-40 rounded-full shadow-lg px-4 py-2.5 flex items-center gap-3 pointer-events-none"
      style={{ background: C.white, border: `1px solid ${C.gold}30`, maxWidth: 'calc(100vw - 32px)' }}
      role="status"
      aria-label={`Envoi des photos qualité impression : ${pct}%`}
    >
      <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin shrink-0"
        style={{ borderColor: C.gold, borderTopColor: 'transparent' }} />
      <div className="min-w-0">
        <p className="text-[11px] font-semibold leading-tight truncate" style={{ color: C.dark }}>
          Envoi qualité impression&hellip; {pct}%
        </p>
        <div className="w-28 h-1 rounded-full mt-1 overflow-hidden" style={{ background: `${C.gold}15` }}>
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})` }} />
        </div>
      </div>
    </div>
  );
}

export const MIN_PHOTOS = 20;
export const MAX_PHOTOS = 300;
// iOS RAM is the binding constraint: a single >30-photo pick of fresh HEIC
// originals (each decoded to a full bitmap during prep) blows the tab's memory
// and Safari silently reloads, losing the selection. So on iPhone/iPad we cap
// each PICK to 30 and ask the customer to add the rest in a follow-up batch.
// Non-iOS is unaffected (only MAX_PHOTOS applies). See Phase 4.
const IOS_PICK_CAP = 20;
// Raised 2 → 4. HEIC decode (heic2any) is main-thread, so two-at-a-time was a
// real bottleneck on Android (HEIC-heavy + slower CPU) — it serialized the
// batch behind the decode step. The #1 fix added a 20s HEIC_CONVERT_TIMEOUT so a
// hung HEIC variant no longer holds its permit forever; with that backstop in
// place 4 concurrent decodes keep the pipeline fed without pinning the UI
// indefinitely. iPhone is unaffected (it already kept up at 2).
const HEIC_CONCURRENT = 4;
// Bumped to 10 on all platforms (was mobile 2 / desktop 6). The mobile cap
// was set to keep the main thread breathing during client-side HEIC/canvas
// work — iPhone-test with a 50-photo batch and drop back if the spinner
// stutters or the tab silently reloads ("stopped at N" symptom).
const UPLOAD_PARALLEL = 10;
// FIX #2 — mobile upload concurrency. 10 parallel ~1-2MB 3600px PUTs starve a
// Samsung/Android uplink (each PUT goes slow → the whole batch crawls to N/N over
// minutes). On mobile we fire fewer-but-faster PUTs (4) so each completes at near
// line speed and progress climbs steadily; desktop keeps the full 10. The instant
// local previews already give per-photo feedback, so a lower network width costs
// no perceived responsiveness. Detected via the existing `detectDevice` (UA +
// maxTouchPoints) with a coarse `navigator.userAgentData.mobile` assist.
const isThrottledUpload = (): boolean => {
  // ONLY Android/Samsung needs the lower width — its weak uplink starves under
  // 10 parallel 1-2MB PUTs. iPhone/iPad have a fast uplink and uploaded at the
  // full width 10 before; throttling them to 4 made iPhone slow (regression).
  const d = detectDevice();
  if (d === 'Android') return true;
  if (d === 'Mobile') {
    // Generic mobile that isn't clearly iPhone/iPad — only throttle if the UA
    // confirms a mobile device (likely Android-class).
    try {
      const uaDataMobile = (navigator as unknown as { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile;
      if (uaDataMobile) return true;
    } catch { /* noop */ }
  }
  return false;
};
const UPLOAD_PARALLEL_MOBILE = 4;
// Effective network concurrency: 4 on ANDROID only, 10 on iPhone/iPad/desktop.
// Computed once at module load (device family doesn't change within a session).
const UPLOAD_PARALLEL_EFFECTIVE = isThrottledUpload() ? UPLOAD_PARALLEL_MOBILE : UPLOAD_PARALLEL;
// Background 3600px print-original concurrency (see comment above
// ORIGINAL_CONCURRENT's old site): 10 iPhone/desktop, 4 Android.
const ORIGINAL_CONCURRENT = isThrottledUpload() ? 4 : 10;
// CPU-resize throttle. UPLOAD_PARALLEL (10) governs the NETWORK PUTs, but the
// off-thread worker pool only has ≈3-6 workers; firing 10 simultaneous 1600px
// resize jobs at it saturates the queue and made every job wait past the
// per-job timeout (the no-thumbnail regression). We cap the CONCURRENT resize
// calls at 4 so the pool is fed at roughly its real width while the network
// stays at 10. The per-file timeout still guards each file, so this can't
// reintroduce the stall.
const RESIZE_CONCURRENT = 4;
// Yields control to the browser between CPU-bound file ops so the upload
// progress UI can repaint. Without this, 20 HEIC photos lock the main
// thread for ~10s and the spinner appears frozen even though work is
// happening.
const yieldToUi = () =>
  new Promise<void>((r) => setTimeout(r, 0));
// Wait for the browser to be idle before a heavy CPU task (face detection), so
// it runs in the gaps between frames instead of pinning the main thread long
// enough to trigger the "page unresponsive" dialog on low-end phones (Vivo etc).
const requestIdle = (): Promise<void> =>
  new Promise<void>((r) => {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
    if (typeof ric === 'function') ric(() => r(), { timeout: 1500 });
    else setTimeout(r, 50);
  });
// Longest-edge cap, aspect ratio ALWAYS preserved (never crops/reshapes).
// Sized by what the printed page can use, not by any one camera — see
// lib/imageLimits.ts. The PDF generator's adaptive size guard handles big
// albums against the storage size limit.
const MAX_PHOTO_DIM = PRINT_MAX_DIM;
const JPEG_QUALITY = PRINT_JPEG_QUALITY;
// FAST-UPLOAD display tier. The FOREGROUND upload (what advances the counter)
// sends this small ~1600px JPEG to the photo's `thumbnail_storage_path`; it's
// ~150-250KB so it lands in well under a second. The worker's signing fallback
// chain serves it (`signed_url = displayUrl(storage_path) ?? thumbnailUrl`)
// until the 3600px original's Cloudflare transform is warm, so tiles/editor
// show this immediately and upgrade later. The 3600px PRINT original uploads in
// the BACKGROUND (scheduleOriginal) and is gated before AI/PDF generation.
const THUMB_DIM = DISPLAY_MAX_DIM;
const THUMB_QUALITY = DISPLAY_JPEG_QUALITY;
// Background full-res uploads run while the customer picks layout / edits.
// Device-aware since 2026-07-08 (was a flat 3, which left a 55-photo album
// with ~40 originals still pending at Confirmer): 10 on iPhone/desktop —
// same ceiling the foreground display uploads already use — and 4 on
// Android, where >4 parallel multi-MB PUTs starve the uplink, slow the
// preview's image downloads and make the page janky (see
// UPLOAD_PARALLEL_EFFECTIVE below; value resolved after isThrottledUpload
// is defined).
// NOTE: there is deliberately no BASE_PAGES constant here any more. The
// billable page allowance is PER ORDER (`orders.base_pages_per_album`, frozen
// at creation by migration 082) — a module constant would silently reprice
// every historical order the day the default changes. Resolve it from the order
// row instead, via lib/orderPricing's resolveBasePagesPerAlbum.
// Phase 5 — local tile preview. The just-uploaded thumbnail tier (THUMB_DIM
// 1600px) is far too heavy to keep ~20-300 objectURLs of in memory, and the
// server `tray_signed_url` transform is COLD (blank tiles for ~2 min). So we
// build a TINY (~280px) objectURL per photo at upload time and render it
// instantly in the grid. Bounded memory: ~280px JPEG ≈ 15-30KB each, vs the
// 150-250KB 1600px thumb — and we NEVER keep the full File for this.
const LOCAL_PREVIEW_DIM = 280;
const LOCAL_PREVIEW_QUALITY = 0.7;
// Only downscale to a tiny preview when the source blob is meaningfully larger
// than the preview dim; otherwise reuse the blob's own objectURL.
const LOCAL_PREVIEW_DOWNSCALE_ABOVE = 600;

// Build a tiny objectURL preview from an already-prepared (upright) JPEG blob.
// Main-thread canvas decode bounded to LOCAL_PREVIEW_DIM — cheap because the
// input is the small thumbnail tier, not the full original. Falls back to the
// blob's own objectURL if anything goes wrong (still small enough at the thumb
// tier; never blocks the upload). Caller owns revoking the returned URL.
async function makeTinyPreviewUrl(blob: Blob, sourceDim: number): Promise<string | null> {
  try {
    // Small enough already → just wrap it (no second decode/encode).
    if (sourceDim <= LOCAL_PREVIEW_DOWNSCALE_ABOVE) {
      return URL.createObjectURL(blob);
    }
    const bmpSupported = typeof createImageBitmap === 'function';
    let bmp: ImageBitmap | HTMLImageElement;
    let w: number;
    let h: number;
    if (bmpSupported) {
      bmp = await createImageBitmap(blob);
      w = bmp.width; h = bmp.height;
    } else {
      const url0 = URL.createObjectURL(blob);
      try {
        const img = await new Promise<HTMLImageElement>((res, rej) => {
          const i = new Image();
          i.onload = () => res(i);
          i.onerror = rej;
          i.src = url0;
        });
        bmp = img; w = img.naturalWidth; h = img.naturalHeight;
      } finally {
        URL.revokeObjectURL(url0);
      }
    }
    const scale = Math.min(1, LOCAL_PREVIEW_DIM / Math.max(w, h));
    const tw = Math.max(1, Math.round(w * scale));
    const th = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = tw; canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      if ('close' in bmp && typeof bmp.close === 'function') bmp.close();
      return URL.createObjectURL(blob); // fall back to the (small) thumb blob
    }
    ctx.drawImage(bmp as CanvasImageSource, 0, 0, tw, th);
    if ('close' in bmp && typeof bmp.close === 'function') bmp.close();
    const tinyBlob = await new Promise<Blob | null>((res) =>
      canvas.toBlob((b) => res(b), 'image/jpeg', LOCAL_PREVIEW_QUALITY),
    );
    return tinyBlob ? URL.createObjectURL(tinyBlob) : URL.createObjectURL(blob);
  } catch {
    try { return URL.createObjectURL(blob); } catch { return null; }
  }
}

// ─── Pending-upload persistence (FIX #3) ─────────────────────────────────────
// The upload queue + remaining files live in memory only, so a refresh mid-batch
// silently dropped every not-yet-completed file (only server-completed photos
// re-listed). We persist the list of PENDING file identities (filename, keyed by
// session) as the batch runs and clear each on success. On load we reconcile the
// survivors against the server: any persisted-pending name with NO completed
// server row is surfaced as "X photos non terminées — réimportez-les", prompting
// a re-pick of ONLY those (the original File bytes can't survive a full reload).
const PENDING_UPLOADS_KEY = (sessionId: string) => `storiz_pending_uploads_${sessionId}`;
type PendingUpload = { name: string };

function readPendingUploads(sessionId: string): PendingUpload[] {
  try {
    const raw = sessionStorage.getItem(PENDING_UPLOADS_KEY(sessionId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((p) => p && typeof p.name === 'string') : [];
  } catch { return []; }
}
function writePendingUploads(sessionId: string, list: PendingUpload[]): void {
  try {
    if (list.length === 0) sessionStorage.removeItem(PENDING_UPLOADS_KEY(sessionId));
    else sessionStorage.setItem(PENDING_UPLOADS_KEY(sessionId), JSON.stringify(list));
  } catch { /* private mode / quota — non-fatal */ }
}
// Add names to the pending set (deduped, case-insensitive) when a batch enqueues.
function addPendingUploads(sessionId: string, names: string[]): void {
  if (!names.length) return;
  const have = new Set(readPendingUploads(sessionId).map((p) => p.name.trim().toLowerCase()));
  const merged = readPendingUploads(sessionId).slice();
  for (const n of names) {
    const key = (n ?? '').trim().toLowerCase();
    if (key && !have.has(key)) { have.add(key); merged.push({ name: n }); }
  }
  writePendingUploads(sessionId, merged);
}
// Clear ONE name from the pending set on its successful upload (or dedup).
function clearPendingUpload(sessionId: string, name: string): void {
  const key = (name ?? '').trim().toLowerCase();
  if (!key) return;
  const next = readPendingUploads(sessionId).filter((p) => p.name.trim().toLowerCase() !== key);
  writePendingUploads(sessionId, next);
}

// ─── Flow Steps ──────────────────────────────────────────────────────────────

type FlowStep =
  | 'design'     // Step 0: choose destination/design + album count
  | 'upload'     // Step 1: upload photos
  | 'mode'       // Step 2: AI vs manual
  | 'magic'      // Step 3: AI processing
  | 'preview'    // Step 4: scrolling album preview
  | 'editor'     // Step 6: full editor (navigates to AlbumEditorPage)
  | 'confirm'    // Step 7: pricing + confirm (legacy — superseded by 'checkout')
  | 'checkout'   // Step 8: IN-APP checkout (summary + payment mode; background
                 //         print uploads drain during it — 2026-07-08)
  | 'success';   // Done

const STEP_ORDER: FlowStep[] = ['design', 'upload', 'mode', 'magic', 'preview', 'confirm', 'checkout', 'success'];

// Steps a RELOAD can resume onto (excludes the transient 'magic'/'editor' and
// the terminal 'success'). Used to clamp a remembered step to what the saved
// data actually supports — so resume lands exactly where she was, never beyond
// her data, and never silently back at step 0.
const RESUMABLE_STEPS: FlowStep[] = ['design', 'upload', 'mode', 'preview', 'confirm', 'checkout'];
function clampResumeStep(want: FlowStep, hasCover: boolean, hasPages: boolean): FlowStep {
  const maxStep: FlowStep = hasPages ? 'checkout' : hasCover ? 'mode' : 'design';
  // 'magic'/'editor'/'success' aren't resumable surfaces → fall to the nearest
  // stable step her data supports.
  const w: FlowStep = RESUMABLE_STEPS.includes(want) ? want : hasPages ? 'preview' : hasCover ? 'upload' : 'design';
  const wi = RESUMABLE_STEPS.indexOf(w);
  const mi = RESUMABLE_STEPS.indexOf(maxStep);
  return wi < 0 ? maxStep : RESUMABLE_STEPS[Math.min(wi, mi)];
}

// Step labels — used for accessibility/future breadcrumbs
const _STEP_LABELS: Record<FlowStep, string> = {
  design: 'Design',
  upload: 'Photos',
  mode: 'Mode',
  magic: 'Création',
  preview: 'Aperçu',
  editor: 'Éditeur',
  confirm: 'Confirmation',
  checkout: 'Commande',
  success: 'Terminé',
};
void _STEP_LABELS; // suppress unused lint

// ─── HEIC Pipeline ───────────────────────────────────────────────────────────

class ConvSem {
  private s: number;
  private q: (() => void)[] = [];
  constructor(n: number) { this.s = n; }
  acquire(): Promise<void> { if (this.s > 0) { this.s--; return Promise.resolve(); } return new Promise<void>(r => this.q.push(r)); }
  release() { if (this.q.length) this.q.shift()!(); else this.s++; }
}
const heicSem = new ConvSem(HEIC_CONCURRENT);
const heicCache = new Map<string, Promise<File>>();
// Face/EXIF analysis runs in the background, OFF the upload critical path, so
// the progress bar completes on bytes-uploaded rather than waiting on
// TensorFlow face detection (~1s/photo). Bounded to 2 concurrent so it never
// thrashes a phone's single JS thread. Same global throughput as before (the
// old inline path was already capped at UPLOAD_PARALLEL=2 on mobile) — it just
// no longer holds the upload slot hostage.
// Serial (1) — two TensorFlow face detections at once pin both cores on a
// budget phone and freeze the page. One-at-a-time + an idle wait before each
// (see scheduleAnalysis) keeps the UI responsive; total throughput is fine
// because analysis is off the upload critical path.
const ANALYSIS_CONCURRENT = 1;
const analysisSem = new ConvSem(ANALYSIS_CONCURRENT);
// Background full-res ("original") upload throttle — declared after ConvSem.
const originalSem = new ConvSem(ORIGINAL_CONCURRENT);
// CPU-resize throttle around the off-thread worker pool (see RESIZE_CONCURRENT).
const resizeSem = new ConvSem(RESIZE_CONCURRENT);

// 15s ceiling on the decode step. Files picked from Google Photos on Android
// can be cloud-backed placeholders whose bytes stream in on demand — reading
// them into an <img> can take a long time or never fire onload/onerror at all.
// Without this cap that stalls the upload worker forever (the "it stopped"
// symptom). On timeout we fall back to uploading the original, unresized blob:
// slower than a resized JPEG, but it makes progress instead of hanging.
const RESIZE_DECODE_TIMEOUT_MS = 15_000;

// ── Stall backstops (THE freeze fix) ──────────────────────────────────────────
// A single hung file used to hold its pump slot forever (heic2any on an Android
// HEIC variant, a flaky cellular fetch that never settles, …). After ~10 hangs
// uploadActiveRef pinned at UPLOAD_PARALLEL and the whole queue froze. These
// bounds GUARANTEE every step settles so the slot ALWAYS frees and the pump
// keeps draining. Durations are generous so legit slow-cellular files still
// finish inside them.
const PER_FILE_TIMEOUT_MS = 90_000; // hard ceiling on a whole processUploadFile
const HEIC_CONVERT_TIMEOUT_MS = 20_000; // heic2any can hang on some HEIC variants
const HASH_TIMEOUT_MS = 10_000;     // sha256Hex of a big blob
const UPLOAD_URL_TIMEOUT_MS = 20_000; // getUploadUrl fetch on flaky cellular
const UPLOAD_MAX_RETRIES = 2;       // retry a hung/5xx/network file up to 2x
const UPLOAD_RETRY_BACKOFF_MS = [1_000, 3_000]; // backoff before retry 1, retry 2

/** A 4xx-from-the-API marker so the retry loop can tell a hard client error
 *  (already-uploaded / dedup / bad request) from a retryable hang/5xx/network. */
class NonRetryableUploadError extends Error {
  constructor(message: string) { super(message); this.name = 'NonRetryableUploadError'; }
}

/** Race `work` against a timeout. On timeout the returned promise REJECTS with
 *  a TimeoutError so the caller's catch/finally still runs (slot frees, permits
 *  release). The underlying work keeps running but no longer holds anything. */
function withTimeout<T>(work: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const e = new Error(`Timeout après ${ms}ms: ${label}`);
      e.name = 'TimeoutError';
      reject(e);
    }, ms);
  });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer));
}

async function resizeBlob(blob: Blob, maxDim: number, quality: number): Promise<Blob> {
  return new Promise(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    let settled = false;
    const done = (out: Blob) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(out);
    };
    const timer = setTimeout(() => done(blob), RESIZE_DECODE_TIMEOUT_MS);
    img.onload = () => {
      const { naturalWidth: w, naturalHeight: h } = img;
      if (w <= maxDim && h <= maxDim) { done(blob); return; }
      const s = maxDim / Math.max(w, h);
      const cv = document.createElement('canvas');
      cv.width = Math.round(w * s);
      cv.height = Math.round(h * s);
      cv.getContext('2d')!.drawImage(img, 0, 0, cv.width, cv.height);
      cv.toBlob(r => done(r ?? blob), 'image/jpeg', quality);
    };
    img.onerror = () => done(blob);
    img.src = url;
  });
}

function convertHeic(key: string, file: File): Promise<File> {
  if (heicCache.has(key)) return heicCache.get(key)!;
  const p = (async () => {
    await heicSem.acquire();
    try {
      // BOUND heic2any — some Android HEIC variants hang libheif forever, which
      // (without this) holds BOTH the heicSem permit and the pump slot. On
      // timeout we reject; the `finally` below ALWAYS releases the permit, and
      // the caller (processUploadFile) falls back to the original file.
      const raw = await withTimeout(
        heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 }),
        HEIC_CONVERT_TIMEOUT_MS,
        'heic2any',
      );
      const jpegBlob = Array.isArray(raw) ? raw[0] : raw;
      // Resize off-thread when possible; fall back to the main-thread canvas.
      const resized = (await processForUpload(jpegBlob, MAX_PHOTO_DIM, JPEG_QUALITY))
        ?? await resizeBlob(jpegBlob, MAX_PHOTO_DIM, JPEG_QUALITY);
      return new File([resized], file.name.replace(/\.(heic|heif)$/i, '') + '.jpg', { type: 'image/jpeg' });
    } finally { heicSem.release(); }
  })();
  // Don't keep a rejected (timed-out) conversion in the cache — let a retry of
  // the same file try again rather than re-failing instantly off a poisoned entry.
  p.catch(() => { if (heicCache.get(key) === p) heicCache.delete(key); });
  heicCache.set(key, p);
  return p;
}

async function resizeIfNeeded(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/heic') return file;
  try {
    const resized = await resizeBlob(file, MAX_PHOTO_DIM, JPEG_QUALITY);
    if (resized.size < file.size * 0.95) return new File([resized], file.name, { type: 'image/jpeg' });
  } catch { /* keep original */ }
  return file;
}

function isHeic(file: File) {
  return /\.(heic|heif)$/i.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif';
}

// ─── Layout Helpers ──────────────────────────────────────────────────────────

function getSlots(layout: string): number[][] {
  switch (layout) {
    case '1-up': return [[0]];
    case '2-up': return [[0], [1]];      // stacked top/bottom
    case '3-up': return [[0], [1, 2]];  // 1 top + 2 bottom
    case '4-up': return [[0, 1], [2, 3]]; // 2×2 grid
    // WIDE-TOP: wide group photo (slot 0) over a companion (slot 1). Same row
    // structure as 2-up; the row HEIGHTS come from `top_h_frac` in the render
    // (PreviewPageCard), not equal halves.
    case 'wide-top': return [[0], [1]];
    // 3-up-flip: 2 portraits top + 1 wide bottom. MUST match editor + PDF.
    case '3-up-flip': return [[0, 1], [2]];
    // 2-up-side (base rules): two portraits side by side. MUST match editor + PDF.
    case '2-up-side': return [[0, 1]];
    case '5-up': return [[0], [1, 2], [3, 4]];
    case '6-up': return [[0, 1], [2, 3], [4, 5]];
    case '4-up-main': return [[0], [1, 2, 3]];
    case '2-up-even': return [[0], [1]];
    default: return [[0]];
  }
}

// Kept for future use in overflow page generation
// function slotCount(layout: string): number {
//   return { '1-up': 1, '2-up': 2, '3-up': 3, '4-up': 4 }[layout] ?? 1;
// }

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Default export — flag-switcher between legacy + V2 ──────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// Solo orders ALWAYS render the legacy single-album flow regardless of the
// flag — V2 only changes Duo/Trio. We resolve the bundle from the order
// before mounting either branch so the V2 component isn't loaded for Solo
// customers at all (saves a chunk for the common case).
//
// The flag-switch is intentionally at the top of the component tree so a
// mid-flow flag flip doesn't crash a customer that's already mounted —
// React will unmount one branch and remount the other, which is safe
// because both branches read the same album_sessions rows.

export default function AlbumFlowPage() {
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string }>();
  const [searchParams] = useSearchParams();
  const { data: settingsData } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => settingsApi.get(),
  });
  const { data: orderData } = useQuery({
    queryKey: ['order', shopifyOrderId],
    queryFn: () => ordersApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
  });
  const isAdmin = useAuthStore((s) => s.profile?.role === 'admin');
  // Capture (once) which device the customer reached the flow from. The worker
  // only writes orders.device when still null, so the first customer device
  // wins. Admins excluded (they open the flow to preview). Fire-and-forget —
  // errors swallowed, never block the UI. Ref-tagged like the editor's
  // flow_step marker so it fires once per order, not on every re-render.
  const deviceRecordedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shopifyOrderId || isAdmin) return;
    if (deviceRecordedRef.current === shopifyOrderId) return;
    deviceRecordedRef.current = shopifyOrderId;
    ordersApi.recordDevice(shopifyOrderId, detectDevice()).catch(() => {});
  }, [shopifyOrderId, isAdmin]);
  const flagOn = settingsData?.settings?.flag_multi_album_v2 ?? false;
  const bundle = orderData?.order?.bundle ?? null;
  const isMultiAlbum = bundle === 'Duo' || bundle === 'Trio';
  // V2 hands off to Legacy via ?force_legacy=1 once all covers are committed
  // (see AlbumFlowPageV2.handleNext). The override also lets admins jump
  // straight to Legacy for debugging without flipping the global flag.
  const forceLegacy = searchParams.get('force_legacy') === '1';
  // Wait until both order + settings have loaded before deciding which branch
  // to render — otherwise we'd flicker Legacy → V2 the moment the queries
  // settle, which would tear down + remount the entire flow state.
  if (orderData === undefined || settingsData === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.beige }}>
        <div className="text-center">
          <div className="mx-auto w-12 h-12 mb-4 rounded-full border-4 animate-spin" style={{ borderColor: `${C.gold}40`, borderTopColor: C.gold }} />
          <p className="text-sm" style={{ color: C.muted }}>Chargement…</p>
        </div>
      </div>
    );
  }
  // Once submitted, the customer can't re-enter the flow to edit — show the
  // "soumis" screen for both the Legacy and V2 branches. Admins bypass it.
  const dispatchOrder = orderData?.order;
  if (dispatchOrder && isAwaitingConfirmation(dispatchOrder.status) && !isAdmin) {
    return <AlbumSubmittedScreen destination={dispatchOrder.destination_1} />;
  }
  // FUNNEL V2 × MULTI-ALBUM (2026-07-24): when the new funnel is ON, Duo/Trio
  // stay in Legacy too — its funnel-v2 branch now LOOPS the cover step per
  // album (Album 1 → Album 2 → [Album 3] → checkout). The batched V2 picker
  // remains the multi-album experience only while the new funnel is OFF
  // (it hands off to the old 8-step flow, which would defeat funnel v2).
  const funnelV2On = settingsData?.settings?.funnel_v2_enabled ?? false;
  if (flagOn && isMultiAlbum && !forceLegacy && !funnelV2On) {
    return <AlbumFlowPageV2 />;
  }
  return <AlbumFlowPageLegacy />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Main Flow Page (Legacy — Solo + pre-flag Duo/Trio) ──────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function AlbumFlowPageLegacy() {
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string }>();
  const [searchParams] = useSearchParams();
  // `?next=<url>` is set by the Shopify COD form so we know where to send the
  // customer once they confirm (typically /pages/checkout-storiz on storiz.ma).
  // Persisted in sessionStorage to survive editor side-trips and reloads.
  const nextUrl = (() => {
    const fromQuery = searchParams.get('next');
    if (fromQuery) {
      try { sessionStorage.setItem('storiz_post_submit_next', fromQuery); } catch { /* ignore */ }
      return fromQuery;
    }
    try { return sessionStorage.getItem('storiz_post_submit_next'); } catch { return null; }
  })();
  const navigate = useNavigate();

  // ── Order data ──
  const { data: orderData, isLoading: orderLoading } = useQuery({
    queryKey: ['order', shopifyOrderId],
    queryFn: () => ordersApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
  });
  const order = orderData?.order ?? null;

  // ── Funnel v2 toggle (2026-07-22) ──
  // Single global admin switch (Réglages → Tunnel de commande). When ON, the
  // flow is: cover → storefront checkout (upsells) → thank-you (WhatsApp CTA +
  // Purchase pixel), and the photo/AI/preview steps happen AFTER, via the
  // continue link the team sends on WhatsApp. When OFF (default): classic
  // 8-step funnel, byte-identical to before. Settings GET is public.
  const { data: funnelSettingsData } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => settingsApi.get(),
    staleTime: 60_000,
  });
  const funnelV2 = !!funnelSettingsData?.settings?.funnel_v2_enabled;

  // ── SIMPLIFIED FUNNEL: no cover step (2026-08-07) ────────────────────────
  // The funnel is now COD form → checkout → thank-you. The customer never picks
  // a cover; the team chooses it once the order lands. /flow therefore has no
  // job left in this funnel, so send the customer straight on rather than
  // showing them a step and asking them to press Continue through it.
  //
  // Where the cover NAME used to travel to checkout and the thank-you WhatsApp
  // message, we now send the destination TITLE the customer typed into the COD
  // form — the one thing they actually chose, and the thing that identifies
  // their album. Same query keys, so the storefront needs no change.
  //
  // Guards, in order: only the simplified funnel; only once (a redirect that
  // re-fires would trap the customer in a loop); only while the order is still
  // being built — a submitted, locked or terminal order renders its own screen
  // above and must not be bounced to checkout.
  const funnelSkipRedirectedRef = useRef(false);
  useEffect(() => {
    if (!funnelV2) return;
    if (funnelSkipRedirectedRef.current) return;
    if (!order?.shopify_order_id) return;
    if (order.status && order.status !== 'pending-album') return;
    funnelSkipRedirectedRef.current = true;
    window.location.href = getCheckoutRedirect(nextUrl, {
      shopify_order_id: order.shopify_order_id,
      customer_name:    order.customer_name,
      customer_phone:   order.customer_phone,
      bundle:           order.bundle,
      page_count:       order.page_count,
      destination_1:    order.destination_1,
      total_price:      order.total_price,
      // Titles from the COD form, not cover names — nothing has been chosen yet.
      cover:            order.destination_1 ?? null,
      cover_2:          order.destination_2 ?? null,
      cover_3:          order.destination_3 ?? null,
    });
  }, [funnelV2, order?.shopify_order_id, order?.status, nextUrl,
      order?.customer_name, order?.customer_phone, order?.bundle,
      order?.page_count, order?.destination_1, order?.destination_2,
      order?.destination_3, order?.total_price]);

  // Refresh the cross-origin resume pointer (Domain=.storiz.ma cookie) while the
  // customer is actively building, so reopening app.storiz.ma later surfaces the
  // resume card. Only set it for in-progress (pending-album) orders.
  useEffect(() => {
    const sid = (order as { shopify_order_id?: string } | null)?.shopify_order_id;
    if (!sid) return;
    if (order?.status && order.status !== 'pending-album') return;
    try {
      const v = encodeURIComponent(JSON.stringify({ o: String(sid), b: order?.bundle ?? '', t: Date.now() }));
      document.cookie = 'storiz_draft=' + v + '; Domain=.storiz.ma; Path=/; Max-Age=86400; SameSite=Lax; Secure';
    } catch { /* ignore */ }
  }, [order?.shopify_order_id, order?.status, order?.bundle]);

  // ── Album count from order bundle ──
  const totalAlbums = useMemo(() => {
    if (!order?.bundle) return 1;
    return { Solo: 1, Duo: 2, Trio: 3 }[order.bundle] ?? 1;
  }, [order?.bundle]);

  // ── Current album index (1-based) ──
  // Reads `?album=N` from the URL so other surfaces (the editor's Confirmer
  // for non-last albums in Duo/Trio) can hand the customer back into the
  // wizard at the right album without resetting them to album 1.
  const [currentAlbum, setCurrentAlbum] = useState(() => {
    const fromUrl = parseInt(searchParams.get('album') ?? '1', 10);
    return Number.isFinite(fromUrl) && fromUrl >= 1 ? fromUrl : 1;
  });

  // FUNNEL V2 × MULTI-ALBUM (2026-07-24): cover NAMES chosen per album while
  // looping the design step (index 0 = album 1). The storefront checkout is
  // hydrated from URL params only, so the final redirect must carry every
  // album's cover; selectedCover resets on each advance, hence the ref.
  const funnelCoversRef = useRef<string[]>([]);

  // ── Flow step ──
  const [step, setStep] = useState<FlowStep>('design');

  // ── Browser-back handling ──
  // Without this the customer's iOS edge-swipe (or browser back) leaves the
  // album flow entirely and lands back on the storiz.ma COD form — which is
  // the leading cause of duplicate orders (they assume the album wasn't
  // recorded and re-submit). Push a history entry on every FORWARD step;
  // `popstate` then steps backward through STEP_ORDER. From `design` (the
  // first step) we let the browser do its normal exit so customers aren't
  // trapped.
  const stepRef = useRef<FlowStep>(step);
  useEffect(() => { stepRef.current = step; }, [step]);
  // Forward navigation is pure state now. The deterministic back-trap below
  // (a single re-armed history sentinel) handles every back gesture, so we no
  // longer push per-forward-step entries — those could under-flow on resume
  // jumps (which call setStep directly) and let back escape to the COD form.
  const goToStep = useCallback((next: FlowStep) => {
    if (next === stepRef.current) return;
    setStep(next);
  }, []);

  // Deterministic back-trap. One sentinel history entry sits on top while the
  // customer is in the builder. Each back press (header, hardware, edge-swipe)
  // pops it → we step back one FlowStep (skipping the transient 'magic') and
  // re-arm the sentinel. Only the first step ('design') lets the browser exit
  // to the storefront. This never relies on history DEPTH, so resume-jumps
  // can't under-flow and escape to the COD form (the duplicate-order cause).
  const trapArmedRef = useRef(false);
  useEffect(() => {
    if (!trapArmedRef.current) {
      trapArmedRef.current = true;
      try { window.history.pushState({ storizTrap: true }, ''); } catch { /* ignore */ }
    }
    const onPop = () => {
      const curIdx = STEP_ORDER.indexOf(stepRef.current);
      if (curIdx <= 0) {
        // First step → allow the real exit (back past the /flow entry).
        try { window.history.back(); } catch { /* ignore */ }
        return;
      }
      let prevIdx = curIdx - 1;
      // Skip transient/legacy steps: 'magic' (transient) and 'confirm' (the
      // legacy pricing screen superseded by the in-app 'checkout' step) —
      // back from 'checkout' must land on 'preview'.
      while (prevIdx > 0 && (STEP_ORDER[prevIdx] === 'magic' || STEP_ORDER[prevIdx] === 'confirm')) prevIdx -= 1;
      setStep(STEP_ORDER[prevIdx]);
      try { window.history.pushState({ storizTrap: true }, ''); } catch { /* ignore */ }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // ── Settings (pricing) ──
  const { data: settingsData } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => settingsApi.get(),
  });
  // THIS order's frozen extra-page price (migration 230), never the live setting.
  const pricePerPage = resolvePricePerPage(order);

  // ── Covers + categories ──
  const { data: coversData } = useQuery({
    queryKey: ['covers-flow'],
    queryFn: () => coversApi.list(),
  });
  const { data: categoriesData } = useQuery({
    queryKey: ['cover-categories-flow'],
    queryFn: () => coversApi.categories(),
  });
  // Pinned cover for THIS order (Task 4 part E). Set when the admin added a
  // previously-missing cover this client searched for; we float it to the top
  // of the picker for them only. Null for everyone else → normal popularity
  // order (covers already arrive usage_count-desc from the worker).
  const { data: coverPinData } = useQuery({
    queryKey: ['cover-pin', shopifyOrderId],
    queryFn: () => coverPinsApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
  });
  const pinnedCoverId = coverPinData?.cover_id ?? null;

  const coversRaw = coversData?.covers ?? [];
  // Covers already arrive popularity-ranked (usage_count desc) from the worker.
  // If a cover is pinned for this order, move it to the very front.
  const covers = useMemo(() => {
    if (!pinnedCoverId) return coversRaw;
    const idx = coversRaw.findIndex((c) => c.id === pinnedCoverId);
    if (idx <= 0) return coversRaw;
    const copy = coversRaw.slice();
    const [pinned] = copy.splice(idx, 1);
    return [pinned, ...copy];
  }, [coversRaw, pinnedCoverId]);
  const categories = categoriesData?.categories ?? [];

  // ── Per-album state ──
  const [selectedCover, setSelectedCover] = useState<Cover | null>(null);
  // True once this album's saved session state is KNOWN (fetched, or confirmed
  // absent). Gates the cover auto-select so it can't fire before we know
  // whether a saved cover_id should win instead.
  const [sessionResolved, setSessionResolved] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [session, setSession] = useState<AlbumSession | null>(null);
  const [photos, setPhotos] = useState<(Photo & { signed_url: string | null; tray_signed_url?: string | null })[]>([]);
  // Phase 5 — instant tiles. session Map<photoId, tiny objectURL>: built the
  // MOMENT a thumbnail upload lands, so the grid renders that tile immediately
  // instead of waiting on the cold server transform. Ref = source of truth for
  // revoke-on-unmount; the state bump (version int) just forces a re-render so
  // the grid picks up the newly-added URL. Tiny previews only (~280px) — we
  // never store the full File here, so memory stays bounded even at 300 photos.
  const localPreviewsRef = useRef<Map<string, string>>(new Map());
  const [localPreviewVersion, setLocalPreviewVersion] = useState(0);
  // Build a stable snapshot for the grid; re-derived only when a preview is
  // added/merged (localPreviewVersion bump).
  const localPreviews = useMemo(
    () => new Map(localPreviewsRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localPreviewVersion],
  );
  useEffect(() => () => {
    // Revoke EVERY local preview objectURL on unmount so they don't leak.
    for (const url of localPreviewsRef.current.values()) {
      try { URL.revokeObjectURL(url); } catch { /* already revoked */ }
    }
    localPreviewsRef.current.clear();
  }, []);
  // Optimistically merge a just-uploaded photo into `photos` by id (so the grid
  // + count grow PER PHOTO, not at batch end). Never double-counts (dedup by
  // id), never overwrites a richer server row that already arrived. Also seeds
  // its tiny local preview under the same id so the tile renders instantly.
  const addOptimisticPhoto = useCallback((
    photoId: string,
    fields: { album_session_id: string; original_filename: string; signed_url?: string | null; tray_signed_url?: string | null },
    previewUrl: string | null,
  ) => {
    if (previewUrl) {
      const prev = localPreviewsRef.current.get(photoId);
      if (prev && prev !== previewUrl) { try { URL.revokeObjectURL(prev); } catch { /* noop */ } }
      localPreviewsRef.current.set(photoId, previewUrl);
      setLocalPreviewVersion((v) => v + 1);
    }
    setPhotos((prev) => {
      if (prev.some((p) => p.id === photoId)) return prev; // already present — no dup
      const optimistic = {
        id: photoId,
        album_session_id: fields.album_session_id,
        original_filename: fields.original_filename,
        storage_path: '',
        preview_storage_path: null,
        width: null,
        height: null,
        file_size_kb: null,
        exif_date: null,
        exif_gps_lat: null,
        exif_gps_lng: null,
        face_count: 0,
        face_data: null,
        is_blurry: false,
        aspect_ratio: null,
        face_spread: null,
        analyzed_at: null,
        created_at: new Date().toISOString(),
        signed_url: fields.signed_url ?? null,
        tray_signed_url: fields.tray_signed_url ?? null,
      } as Photo & { signed_url: string | null; tray_signed_url?: string | null };
      return [optimistic, ...prev]; // PREPEND — new photos at the TOP of the grid so the client watches them land, not hidden below earlier batches
    });
  }, []);
  // Revoke + forget a photo's local preview objectURL (on delete) so it doesn't
  // leak. No re-render bump needed — the row is leaving `photos` anyway.
  const dropLocalPreview = useCallback((photoId: string) => {
    const url = localPreviewsRef.current.get(photoId);
    if (url) { try { URL.revokeObjectURL(url); } catch { /* noop */ } localPreviewsRef.current.delete(photoId); }
  }, []);
  const [layout, setLayout] = useState<LayoutPlan | null>(null);
  const [uploadFiles, setUploadFiles] = useState<{ name: string; progress: number; status: 'pending' | 'converting' | 'uploading' | 'done' | 'error' }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // Background face/EXIF analysis — decoupled from the upload slot so the
  // progress bar finishes on bytes-uploaded, not on face detection. We still
  // gate the AI layout on these settling (see prewarm guard + handleAiMagic)
  // so face-aware placement / hero scoring quality is unaffected.
  const pendingAnalysesRef = useRef<Set<Promise<void>>>(new Set());
  const [pendingAnalysisCount, setPendingAnalysisCount] = useState(0);
  // Each photo's full-res original ("print track") upload, keyed by photoId. The
  // submit step (finalizeOriginals) ensures EVERY one has landed before the PDF,
  // so the compressed thumbnail is never used for print.
  const originalsRef = useRef<Map<string, { photoId: string; working: File | null; uploadUrl: string; tus: TusBlock | null; done: boolean; running: Promise<void> | null; bytes: number }>>(new Map());
  // Fix 1 — what the last server-truth sweep flagged as missing, so the submit
  // blockers can tell the customer exactly WHICH photos to re-upload.
  const missingOriginalsRef = useRef<MissingOriginal[]>([]);
  // Drives the "Finalisation des photos X/N" state when the user reaches submit
  // before the background originals finished.
  const [finalizingOriginals, setFinalizingOriginals] = useState<{ done: number; total: number } | null>(null);
  // Once the AI layout is generated, queued face analyses are useless (the
  // layout already consumed what was ready) — flip this so the remaining ones
  // skip instead of janking the preview/editor on a big batch.
  const stopAnalysisRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  // AI layout prewarm — fires /ai/layout in the background as soon as the
  // customer has enough photos and isn't actively uploading. By the time
  // they click "Magie AI" on the mode step, the DeepSeek call (30-60s) is
  // either done or close to done, making the perceived wait near-zero.
  // Mirror of the same pattern in AlbumEditorPage's regen button. Keyed by
  // `${session.id}::${photos.length}` so adding more photos invalidates.
  const prewarmRef = useRef<Promise<{ layout: LayoutPlan }> | null>(null);
  const prewarmKeyRef = useRef<string | null>(null);
  // Fix D (AI Magic resilience): single-finalize guard. A generation can be
  // recovered TWO ways — the awaited fetch resolving, OR the resilience poll
  // finding the worker-persisted pending result after a phone-backgrounding.
  // Whichever wins first runs the post-passes + save EXACTLY ONCE; this ref
  // holds the photos_key already finalized so the loser is a no-op. Keyed by
  // photos_key so a NEW photo set (different generation) can finalize afresh.
  const finalizedKeyRef = useRef<string | null>(null);
  // True while finalize is mid-flight, so a concurrent poll/fetch tick doesn't
  // start a second finalize before finalizedKeyRef is set (it's set at the very
  // start of finalize, but this adds belt-and-braces against re-entrancy).
  const finalizingRef = useRef(false);
  // ── Fix D (loader teardown) ──────────────────────────────────────────────
  // Aborts the in-flight /ai/layout fetch that handleAiMagic is awaiting. When
  // ANY resume path (poll, resume-on-load effect, visibilitychange/focus)
  // finalizes the worker-persisted pending result, we abort() this so the
  // original (likely dead, post-backgrounding) fetch stops awaiting — its
  // progress loop ends and it can't later clobber the step back to 'magic'.
  const aiAbortRef = useRef<AbortController | null>(null);
  // Shared "the active AI-Magic generation has been finalized" flag. Lives in a
  // ref (not the closure-local `settled` in handleAiMagic) so a resume-path
  // finalize is VISIBLE to handleAiMagic's frozen-then-resumed poll/fetch loops
  // — they read this, see it's done, and exit instead of polling to deadline.
  // Keyed by photos_key so a fresh photo set starts a fresh generation.
  const magicSettledKeyRef = useRef<string | null>(null);
  // Path 1 (Confirmer on preview) now runs the same cover.pdf + interior.pdf
  // pipeline the editor uses. The progress object drives a full-screen overlay
  // so the customer sees "Génération…" instead of a frozen UI for 15-30s while
  // pdf-lib + canvas crunch.
  const [pdfProgress, setPdfProgress] = useState<PdfGenProgress | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Upload queue + re-entrancy guard (Phase 2c) ──
  // The flow upload used to build a private scheduler per handleUpload call, so
  // two rapid picks each rebuilt `uploadFiles` from scratch and clobbered each
  // other's progress. Now ONE persistent queue + a single pump: a second pick
  // mid-upload appends its work items and tops up the in-flight pump instead of
  // restarting. Mirrors MagicUploadPage (runningRef/activeRef/queueRef).
  type UploadWork = { idx: number; file: File };
  const uploadQueueRef = useRef<UploadWork[]>([]);
  const uploadRunningRef = useRef(false);   // one pump loop scheduling at a time
  const uploadActiveRef = useRef(0);         // in-flight count = source of truth for "done"
  const uploadSessionRef = useRef<AlbumSession | null>(null); // resolved session, shared across picks
  // Stable mirror of `uploadFiles` so the pump's per-file status writes don't
  // depend on a stale closure of the photos/uploadFiles state.
  const uploadFilesRef = useRef<{ name: string; progress: number; status: 'pending' | 'converting' | 'uploading' | 'done' | 'error' }[]>([]);
  // Retained picked source Files keyed by global slot index, so a one-tap
  // Réessayer can re-run a failed slot without forcing a re-pick.
  const retrySourcesRef = useRef<Map<number, File>>(new Map());
  // Customer-facing toast for the upload step (dedup notice, iOS cap, aggregate
  // failure). PhotoUploadPage uses the same setAddNotice pattern.
  const [addNotice, setAddNotice] = useState<string | null>(null);
  const addNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashNotice = useCallback((msg: string, persist = false) => {
    if (addNoticeTimerRef.current) { clearTimeout(addNoticeTimerRef.current); addNoticeTimerRef.current = null; }
    setAddNotice(msg);
    if (!persist) addNoticeTimerRef.current = setTimeout(() => setAddNotice(null), 6000);
  }, []);
  useEffect(() => () => { if (addNoticeTimerRef.current) clearTimeout(addNoticeTimerRef.current); }, []);
  // Keep the live-session mirror in sync so a second pick reuses the session
  // the first pick already resolved.
  useEffect(() => { uploadSessionRef.current = session; }, [session]);

  // ── Destination from order ──
  const destination = useMemo(() => {
    if (!order) return '';
    const dests = [order.destination_1, order.destination_2, order.destination_3];
    return dests[currentAlbum - 1] ?? dests[0] ?? '';
  }, [order, currentAlbum]);

  const year = useMemo(() => {
    if (!order) return String(new Date().getFullYear());
    const years = [order.year_1, order.year_2, order.year_3];
    return years[currentAlbum - 1] ?? years[0] ?? String(new Date().getFullYear());
  }, [order, currentAlbum]);

  // ── Load existing session for current album ──
  // If the session already has any saved work (cover picked, photos uploaded,
  // or a layout) we skip the onboarding wizard entirely and jump straight
  // into the full editor — that's the "Google Docs" behaviour: re-opening
  // an order always picks up where the last save left off, never re-asks
  // the customer/admin to choose a design.
  // Pin the bounce decision to the (order, album) tuple so refetches of the
  // covers query (a useQuery dep) don't re-trigger it. Without this, the
  // useEffect would re-fire after Confirm — when session.status flips to
  // 'submitted' on the server — and navigate('/editor') would race against
  // the window.location.href to checkout, losing for the customer.
  const bouncedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shopifyOrderId) return;
    const tag = `${shopifyOrderId}#${currentAlbum}`;
    if (bouncedForRef.current === tag) return;
    bouncedForRef.current = tag;
    setSessionResolved(false); // new album → saved-cover state unknown again
    let cancelled = false;
    (async () => {
      // Retry the session fetch. Previously a transient failure was swallowed
      // (.catch(() => {})), silently leaving the customer at step 0 with an
      // empty wizard after a reload. Retry a few times so a momentary network
      // blip on resume doesn't lose her place.
      let res: Awaited<ReturnType<typeof sessionsApi.get>> | null = null;
      for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
        try { res = await sessionsApi.get(shopifyOrderId, currentAlbum); break; }
        catch { if (attempt < 2) await new Promise((r) => setTimeout(r, 600 * (attempt + 1))); }
      }
      // Saved-state now KNOWN (session found or genuinely absent) — the cover
      // auto-select (C1) waits on this so it can never race a saved cover_id.
      // Tag check (not `cancelled`): under React StrictMode the first effect
      // run is cancelled while its twin early-returns on bouncedForRef, so a
      // cancelled-flag guard would leave this false forever. Same-tag means
      // the fetched result is still for the CURRENT album, cancelled or not.
      if (bouncedForRef.current === tag) setSessionResolved(true);
      if (cancelled || !res?.session) return;
      const s = res.session;
      const hasPages   = Array.isArray((s.pages as any)?.pages) && (s.pages as any).pages.length > 0;
      const hasCover   = !!s.cover_id;
      const isAdvanced = ['layout-generated', 'submitted', 'locked'].includes(s.status);

      // Hydrate whatever the server has — cover + layout — REGARDLESS of step.
      // The photos effect is keyed on session.id, so setting the session here
      // also reloads her already-uploaded photos. From preview the customer can
      // Modifier (→ /edit) or Confirmer; the wizard hydrates from session.pages.
      setSession(s);
      // selectedCover is resolved by the dedicated effect below (keyed on
      // session.cover_id + covers). Do NOT look it up here — depending on
      // `covers` made THIS effect re-run when the covers query resolved, and the
      // bouncedForRef guard then short-circuited the re-run while the original
      // run was already cancelled → setStep never fired → stuck on 'design'.
      const sessionLayout = (s.pages as { pages?: LayoutPage[]; unassigned_photos?: string[] } | null) ?? null;
      if (sessionLayout?.pages?.length) {
        setLayout({ pages: sessionLayout.pages, unassigned_photos: sessionLayout.unassigned_photos ?? [] });
      }

      // Resume to the EXACT step she was on (persisted in sessionStorage on every
      // step change), clamped to what her saved data supports. With no persisted
      // step this reproduces the old behaviour: preview (layout) / upload (cover)
      // / design — never a silent reset to step 0.
      const dataDefault: FlowStep = (hasPages || isAdvanced) ? 'preview' : hasCover ? 'upload' : 'design';
      // Resume priority:
      //   1. sessionStorage (THIS tab's last position) is AUTHORITATIVE on an
      //      in-tab reload — a customer who went BACK to 'upload' to add more
      //      photos must reload onto 'upload', NOT the furthest step they once
      //      reached (the "reload jumps to preview" bug). The persist effect's
      //      mount write is SKIPPED (firstPersistRef) so this only ever holds a
      //      GENUINE step the customer navigated to, never the initial 'design'.
      //   2. else the FURTHEST of dataDefault + the monotonic server flow_step —
      //      the source of truth for a fresh / cross-device / closed-tab resume
      //      (the WhatsApp continue link). This is the D232 cover-resume fix.
      // clampResumeStep then caps to what her saved data supports.
      const advance = (cur: FlowStep, cand: FlowStep | null | undefined): FlowStep =>
        (cand && STEP_ORDER.includes(cand) && STEP_ORDER.indexOf(cand) > STEP_ORDER.indexOf(cur)) ? cand : cur;
      let want: FlowStep = dataDefault;
      if (typeof s.flow_step === 'string') want = advance(want, s.flow_step as FlowStep);
      let hadInTabStep = false;
      try {
        const raw = sessionStorage.getItem(`storiz_flow_${shopifyOrderId}`);
        if (raw) {
          const p = JSON.parse(raw) as { album?: number; step?: FlowStep };
          if (p && p.album === currentAlbum && typeof p.step === 'string' && STEP_ORDER.includes(p.step)) {
            want = p.step;        // in-tab position wins outright (not furthest)
            hadInTabStep = true;
          }
        }
      } catch { /* ignore */ }
      // Directed entry (e.g. the magic-link "Créer mon album" button →
      // ?step=mode): land the customer on the requested step so they choose
      // AI-Magic vs Manual instead of resuming straight to preview. Only on a
      // FRESH landing (no in-tab position) so a later in-tab reload still wins.
      if (!hadInTabStep) {
        try {
          const sp = new URLSearchParams(window.location.search).get('step') as FlowStep | null;
          if (sp && STEP_ORDER.includes(sp)) want = sp;
        } catch { /* ignore */ }
      }
      void hadInTabStep;
      setStep(clampResumeStep(want, hasCover || isAdvanced, hasPages || isAdvanced));
    })();
    return () => { cancelled = true; };
    // NOT `covers` — see note above: a covers-driven re-run cancels this run
    // before setStep fires, leaving the resume stuck on the initial 'design'
    // step (D232 bug: cover chosen + flow_step='upload' but landed on design).
  }, [shopifyOrderId, currentAlbum, navigate]);

  // Persist the wizard step + album so a reload (e.g. an accidental
  // pull-to-refresh, or iOS reclaiming the tab) resumes EXACTLY here instead of
  // resetting to step 0. The server (cover/photos/layout) remains the source of
  // truth for what's reachable; this only remembers WHERE she was. Skipped for
  // the terminal 'success' step.
  const firstPersistRef = useRef(true);
  useEffect(() => {
    if (!shopifyOrderId || step === 'success') return;
    // Skip the FIRST run (mount, step = initial 'design'): persisting it would
    // poison sessionStorage before the resume effect sets the real step, and the
    // resume reads sessionStorage as authoritative. Only record GENUINE steps the
    // customer actually navigated to.
    if (firstPersistRef.current) { firstPersistRef.current = false; return; }
    try {
      sessionStorage.setItem(`storiz_flow_${shopifyOrderId}`, JSON.stringify({ album: currentAlbum, step }));
    } catch { /* private mode / quota — non-fatal */ }
    // ALSO persist the furthest step server-side (monotonic) so the resume /
    // continue link resumes on the EXACT step even after the tab is closed
    // (sessionStorage is gone). Records design/upload/mode/preview/confirm;
    // 'editor' is written separately by AlbumEditorPage on mount. The worker
    // only advances, never regresses. Fire-and-forget — never blocks the UI.
    sessionsApi.advanceFlowStep(shopifyOrderId, currentAlbum, step);
  }, [shopifyOrderId, currentAlbum, step]);

  // ── Load photos when session exists ──
  useEffect(() => {
    if (!session?.id) return;
    const sid = session.id;
    photosApi.list(sid).then((res) => {
      const all = res.photos ?? [];
      // RELIABILITY — a row whose original object is absent/0-bytes (a failed
      // upload that was nonetheless marked done in a prior visit) must NOT count
      // as an added photo (it'd be a silent blank in the grid, the count, the AI
      // layout and the PDF). Keep byteless rows OUT of `photos` and surface them
      // as retryable error tiles instead. The bytes are gone after a reload, so
      // the retry path re-prompts for a re-pick (handled by retryFailedUploads).
      const server = all.filter((p) => !p.original_missing);
      const byteless = all.filter((p) => p.original_missing);
      setPhotos(server);
      if (byteless.length > 0) {
        const errItems = byteless.map((p) => ({
          name: p.original_filename ?? 'photo',
          progress: 0,
          status: 'error' as const,
        }));
        setUploadFiles((prev) => {
          const haveNames = new Set(prev.map((u) => u.name.trim().toLowerCase()));
          const fresh = errItems.filter((e) => !haveNames.has(e.name.trim().toLowerCase()));
          const next = [...prev, ...fresh];
          uploadFilesRef.current = next;
          return next;
        });
        flashNotice(
          `${byteless.length} photo${byteless.length > 1 ? 's' : ''} : original manquant — à re-téléverser.`,
          true,
        );
      }
      // FIX #3 — reconcile the persisted pending-upload trail against the server.
      // Skip while THIS tab is mid-batch (those files are legitimately still in
      // flight, not lost). A persisted name with NO matching completed server row
      // is a file that vanished on a refresh — surface it so the customer can
      // re-pick ONLY those, instead of silently losing track of them. The File
      // bytes are unrecoverable after a full reload, so we re-prompt (not re-do).
      if (uploadActiveRef.current > 0 || uploadQueueRef.current.length > 0) return;
      const pending = readPendingUploads(sid);
      if (pending.length === 0) return;
      const serverNames = new Set(server.map((p) => (p.original_filename ?? '').trim().toLowerCase()).filter(Boolean));
      const unfinished = pending.filter((p) => !serverNames.has(p.name.trim().toLowerCase()));
      // Drop the now-accounted-for names; keep only the genuinely-unfinished ones
      // so a later resume doesn't re-warn about files that did land.
      writePendingUploads(sid, unfinished);
      if (unfinished.length > 0) {
        flashNotice(
          `${unfinished.length} photo${unfinished.length > 1 ? 's' : ''} non terminée${unfinished.length > 1 ? 's' : ''} — réimportez-${unfinished.length > 1 ? 'les' : 'la'}.`,
          true,
        );
      }
    }).catch(() => {});
  }, [session?.id, flashNotice]);

  // ── Resolve selectedCover whenever session AND covers are both ready ────────
  // The bouncedForRef effect runs once per (order, album) and may fire before
  // covers list resolves; if so, `covers.find()` returned undefined and
  // selectedCover stayed null. The preview step then renders the fallback
  // text overlay instead of the cover image — visible bug Yahya reported
  // ("at first the cover was not showing"). Decoupling the cover lookup
  // from the bounce effect means it converges no matter which side loads
  // first.
  useEffect(() => {
    if (selectedCover) return;
    if (!session?.cover_id) return;
    if (covers.length === 0) return;
    const c = covers.find(cv => cv.id === session.cover_id);
    if (c) setSelectedCover(c);
  }, [selectedCover, session?.cover_id, covers]);

  // ── Ensure session exists (create on first save) ──
  const ensureSession = useCallback(async () => {
    if (session) return session;
    if (!shopifyOrderId) throw new Error('No order');
    const res = await sessionsApi.upsert(shopifyOrderId, currentAlbum, {
      destination,
      year,
      cover_id: selectedCover?.id !== '__blank__' ? selectedCover?.id : null,
      status: 'draft' as any,
    });
    setSession(res.session);
    return res.session;
  }, [session, shopifyOrderId, currentAlbum, destination, year, selectedCover]);

  // ── Save cover selection ──
  const handleSelectCover = useCallback(async (cover: Cover) => {
    setSelectedCover(cover);
    if (shopifyOrderId) {
      try {
        const res = await sessionsApi.upsert(shopifyOrderId, currentAlbum, {
          // The occasion only for a NEW album; an existing one keeps its own.
          ...(session ? {} : { destination }),
          year,
          cover_id: cover.id !== '__blank__' ? cover.id : null,
          // ⚠️ THE ALBUM'S TITLE, not the order's copy (2026-10-06): this put
          // the order words back on the front at every pick, undoing a title
          // changed in the chat. lib/titleSource.ts.
          cover_destination_text: titlePrefill(session, destination),
          cover_year_text: year,
        });
        setSession(res.session);
      } catch { /* will retry on next save */ }
    }
  }, [shopifyOrderId, currentAlbum, destination, year, session]);

  // ── Background photo analysis (face detection + EXIF) ──
  // Fired-and-forgotten off the upload critical path so the progress bar
  // completes on bytes-uploaded, not on TensorFlow face detection (~1s/photo
  // on a phone). Each task takes a slot from analysisSem (max 2 concurrent),
  // is tracked in pendingAnalysesRef, and self-removes on settle. The AI
  // layout still waits on these (awaitPendingAnalyses) so face-aware
  // placement / hero scoring reads complete DB metadata — quality unchanged.
  const scheduleAnalysis = useCallback((file: File, photoId: string) => {
    const task = (async () => {
      await analysisSem.acquire();
      try {
        // Skip if the layout is already generated — the analysis would only jank
        // the preview/editor for no benefit.
        if (stopAnalysisRef.current) return;
        // Yield to the browser before the heavy face-detection pass so it lands
        // in idle time and never blocks the UI long enough to trip the
        // "page unresponsive" dialog on low-end phones.
        await requestIdle();
        if (stopAnalysisRef.current) return;
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const authHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        if (authSession?.access_token) authHeaders['Authorization'] = `Bearer ${authSession.access_token}`;
        await analyzePhoto(file, photoId, API_BASE, authHeaders);
      } catch { /* best-effort — analysis is non-critical */ } finally {
        analysisSem.release();
      }
    })();
    pendingAnalysesRef.current.add(task);
    if (mountedRef.current) setPendingAnalysisCount((c) => c + 1);
    void task.finally(() => {
      pendingAnalysesRef.current.delete(task);
      if (mountedRef.current) setPendingAnalysisCount((c) => Math.max(0, c - 1));
    });
  }, []);

  // Block until every in-flight analysis settles (or a safety ceiling fires).
  // Called right before the AI layout so the worker reads complete face/EXIF
  // metadata. The ceiling guarantees a stuck analysis can't hang the magic
  // button — the layout still runs, just without that one photo's tags.
  const awaitPendingAnalyses = useCallback(async (maxWaitMs = 20_000) => {
    const tasks = Array.from(pendingAnalysesRef.current);
    if (tasks.length === 0) return;
    await Promise.race([
      Promise.allSettled(tasks),
      new Promise<void>((r) => setTimeout(r, maxWaitMs)),
    ]);
  }, []);

  // ── Full-res original ("print track") upload ──
  // Foreground only uploads the small thumbnail (fast); this sends the full-res
  // 3600px original to storage_path off the critical path while the customer
  // picks a layout / edits. Idempotent + dedupes via entry.running, so the
  // submit gate (finalizeOriginals) can safely re-trigger any that haven't
  // landed. NOTE: this resizes to MAX_PHOTO_DIM (3600px) — the print track —
  // not the 1600px compressed/editor track.
  // `working` is the HELD source File for the print original. It is the only
  // heavy byte buffer we keep per photo, and it is released (set to null) ONLY
  // after `done` flips true — i.e. after the original's bytes are CONFIRMED in
  // storage. `tus` is the resumable block from the upload-url mint (null →
  // signed-URL PUT only). `uploadUrl` is the signed-URL PUT fallback target.
  type OrigEntry = {
    photoId: string;
    working: File | null;
    uploadUrl: string;
    tus: TusBlock | null;
    done: boolean;
    running: Promise<void> | null;
    /** Source-file size, kept after `working` is released — the progress chip
     *  weights per-photo progress by bytes so one 8MB photo doesn't read the
     *  same as one 300KB photo. Display-only; transport untouched. */
    bytes: number;
  };
  const uploadOriginal = useCallback((entry: OrigEntry): Promise<void> => {
    if (entry.done) return Promise.resolve();
    if (entry.running) return entry.running;
    // Bytes were released only after a confirmed upload, so a null `working`
    // on a not-done entry can't normally happen. Guard anyway: never claim
    // success without the file, so finalizeOriginals stays honest.
    if (!entry.working) {
      console.warn('[upload] original source already released but not done — cannot re-upload');
      return Promise.resolve();
    }
    entry.running = (async () => {
      await originalSem.acquire();
      try {
        const source = entry.working;
        if (!source) return; // released between the guard and acquire — bail safe
        // ISSUE 4 (2026-07-05) — D258 forensics: originals failed SCATTERED
        // (neighbours fine, all thumbnails fine) and a single failure used to
        // wait silently until submit. One attempt per session ≈ mass loss on a
        // stressed iPhone. Now: (a) the 3600px encode NEVER loses the photo —
        // worker pool → main-thread → the SOURCE FILE AS-IS as last resort
        // (bigger upload, zero quality loss); (b) the whole build+upload+
        // confirm runs up to 3 attempts with short backoff.
        const buildFullFile = async (): Promise<File> => {
          try {
            const out = await processForUpload(source, MAX_PHOTO_DIM, JPEG_QUALITY);
            if (out) return new File([out], source.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
          } catch { /* worker pool crashed — fall through */ }
          try {
            let f = await normalizeJpegOrientation(source);
            f = await resizeIfNeeded(f);
            return f;
          } catch { /* main-thread pipeline failed too — fall through */ }
          return source; // last resort: never lose the print file over an encode error
        };
        const attemptOnce = async () => {
          const fullFile = await buildFullFile();
          const contentType = fullFile.type || 'image/jpeg';
          // RESUMABLE FIRST — on ANY TUS error, fall back to the signed-URL
          // PUT. Both write the SAME bytes to the SAME storage object.
          if (entry.tus) {
            const tusPath = entry.tus.object_path;
            try {
              await uploadViaTus(fullFile, {
                storageHost: entry.tus.storage_host,
                anonKey: entry.tus.anon_key,
                token: entry.tus.token,
                bucket: entry.tus.bucket,
                objectPath: entry.tus.object_path,
                contentType,
                getFreshToken: () => withTimeout(photosApi.tusRefresh(tusPath), 15_000, 'tus-refresh'),
              });
            } catch {
              await uploadToSignedUrl(entry.uploadUrl, fullFile, contentType);
            }
          } else {
            await uploadToSignedUrl(entry.uploadUrl, fullFile, contentType);
          }
          // Fix 2 — transport "success" is not proof; SERVER byte-check gates done.
          const confirmed = await confirmOriginalLanded(
            () => withTimeout(photosApi.verifyOriginal(entry.photoId), 15_000, 'verify-original'),
          );
          if (!confirmed) throw new Error('original not confirmed in storage');
        };
        let lastErr: unknown = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 1500 * attempt));
          try {
            await attemptOnce();
            lastErr = null;
            break;
          } catch (err) { lastErr = err; }
        }
        if (lastErr) throw lastErr;
        entry.done = true;
        entry.working = null;
        // Confirmed in storage → its persisted resume-bytes are no longer needed.
        void deletePendingOriginal(entry.photoId);
      } catch (e) {
        // NOT released — `working` stays held; the standing retry pump +
        // visibility kick + submit gate all re-attempt this exact file.
        console.warn('[upload] full-res original failed (will retry):', (e as Error)?.message);
      } finally {
        originalSem.release();
        entry.running = null;
      }
    })();
    return entry.running;
  }, []);

  // Register + kick off ONE photo's background full-res original upload. Called
  // from attemptUploadFile right after the FOREGROUND thumbnail lands: it stores
  // the held source File + signed-URL/TUS targets in originalsRef (keyed by
  // photoId) so finalizeOriginals can guarantee + retry it, then fires the
  // upload in the background (bounded by originalSem so it trickles without
  // saturating the uplink). Idempotent per photoId — a second register for the
  // same id (e.g. a retry) reuses the existing entry. NEVER blocks the caller.
  const scheduleOriginal = useCallback((
    photoId: string,
    source: File,
    uploadUrl: string,
    tus: TusBlock | null,
  ) => {
    const existing = originalsRef.current.get(photoId);
    if (existing) {
      if (existing.done) return;            // already landed — nothing to do
      if (existing.running) return;          // already uploading — let it finish
      if (!existing.working) existing.working = source; // re-arm a released-but-not-done entry
      void uploadOriginal(existing);
      return;
    }
    const entry: OrigEntry = { photoId, working: source, uploadUrl, tus, done: false, running: null, bytes: source.size };
    originalsRef.current.set(photoId, entry);
    // RESUME SAFETY NET — persist the source bytes in IndexedDB so a refresh /
    // tab-kill / iOS eviction doesn't lose them forever. Deleted the moment the
    // byte-check confirms the original landed.
    // PERF (2026-07-05 — "uploads got slow"): the Blob is stored AS-IS (zero
    // main-thread byte copy — v1's arrayBuffer() read of every multi-MB source
    // competed with the live uploads) and the write is deferred to browser
    // IDLE time so it never runs on the upload hot path. Best-effort as
    // always: quota/private mode → in-memory behaviour + submit gate.
    const sid = uploadSessionRef.current?.id;
    if (sid) {
      const persist = () => {
        void savePendingOriginal({
          photoId,
          sessionId: sid,
          name: source.name,
          type: source.type || 'image/jpeg',
          lastModified: source.lastModified,
          uploadUrl,
          tus,
          savedAt: Date.now(),
        }, source);
      };
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(persist, { timeout: 4000 });
      } else {
        setTimeout(persist, 300);
      }
    }
    // Fire-and-forget — the background original trickles via originalSem. The
    // submit/generation gate (finalizeOriginals) awaits the running promise +
    // retries any that haven't landed before AI layout / PDF generation runs.
    void uploadOriginal(entry);
  }, [uploadOriginal]);

  // STRICT gate before PDF: ensure EVERY full-res original has uploaded. Waits
  // for the in-flight ones and retries any that failed, surfacing "X/N" progress
  // via finalizingOriginals. Returns false only if some still couldn't upload
  // after several rounds — the caller must then NOT generate the PDF, so a
  // compressed image can never end up in print.
  const finalizeOriginals = useCallback(async (opts?: { silent?: boolean }): Promise<boolean> => {
    const pairs = Array.from(originalsRef.current.entries()); // [photoId, entry]
    const total = pairs.length;
    // NOTE (Fix 1): an EMPTY tracker is NOT a pass. After a refresh/resume the
    // in-memory map is empty while originals may be missing in storage — the
    // old `total === 0 → true` shortcut is exactly how thumbnail-only orders
    // reached print. We skip only the in-memory retry rounds when empty; the
    // SERVER-TRUTH sweep below always runs and is the only thing that can
    // return true.
    // silent mode → no "Finalisation de vos photos" modal. Used by the
    // Magie-AI step, where the print originals aren't needed yet (the AI works
    // from the already-uploaded display tier); the submit + PDF gates stay
    // BLOCKING so a compressed image still can never reach print.
    const show = (v: { done: number; total: number } | null) => {
      if (!opts?.silent && mountedRef.current) setFinalizingOriginals(v);
    };
    const doneCount = () => pairs.filter(([, e]) => e.done).length;
    const MAX_ROUNDS = 6;
    // Per-file bound per round — an entry whose in-flight promise never settles
    // (Android drops the socket after backgrounding; the bytes often DID land
    // server-side but the client promise is lost) used to freeze the round —
    // and therefore the modal — forever at N/total. On timeout we clear
    // entry.running so the NEXT round starts a fresh attempt (safe: TUS
    // resumes from the persisted fingerprint; the PUT overwrites the same
    // storage object — both idempotent).
    const PER_FILE_ROUND_TIMEOUT_MS = 60_000;
    for (let round = 0; round < MAX_ROUNDS && total > 0; round++) {
      const notDone = pairs.filter(([, e]) => !e.done);
      show({ done: total - notDone.length, total });
      if (notDone.length === 0) break; // in-memory track clean → server sweep decides
      await Promise.allSettled(
        notDone.map(([, e]) =>
          withTimeout(uploadOriginal(e), PER_FILE_ROUND_TIMEOUT_MS, 'original-finalize')
            .then(() => { show({ done: doneCount(), total }); })
            .catch(() => { e.running = null; }),
        ),
      );
      // SERVER RECONCILIATION — the missing half of the old gate. For entries
      // the client still thinks are pending, ask the worker whether the bytes
      // are actually in storage (GET /photos/:id/verify — cheap byte probe).
      // Catches the lost-promise case above WITHOUT re-uploading: server truth
      // wins over client memory, exactly what a manual refresh proved.
      const stillPending = pairs.filter(([, e]) => !e.done);
      if (stillPending.length > 0) {
        await Promise.allSettled(
          stillPending.map(([photoId, e]) =>
            withTimeout(photosApi.verifyOriginal(photoId), 10_000, 'verify-original')
              .then((r) => {
                if (r?.ok) { e.done = true; e.working = null; }
              })
              .catch(() => { /* unknown — keep retrying via the next round */ }),
          ),
        );
        show({ done: doneCount(), total });
      }
    }
    // In-memory rounds exhausted with entries still not landed → we already
    // know the gate fails; skip the server round-trip and report honestly.
    if (!pairs.every(([, e]) => e.done)) {
      missingOriginalsRef.current = pairs
        .filter(([, e]) => !e.done)
        .map(([photoId]) => ({ photo_id: photoId, original_filename: null, has_thumbnail: true }));
      show(null);
      return false;
    }

    // ── SERVER-TRUTH SWEEP (Fix 1 — the actual gate) ─────────────────────────
    // ALWAYS runs, including when originalsRef is EMPTY (post-refresh/resume).
    // Asks the worker to byte-check EVERY photo of the session in storage.
    // FAIL CLOSED: no server verdict → no submit. Never re-downloads a photo —
    // the probe is a server-side storage-metadata check.
    const sess = uploadSessionRef.current;
    if (!sess?.id) { show(null); return total === 0 ? true : false; } // no session → nothing uploaded yet
    const verdict = await serverVerifyOriginals(
      () => withTimeout(photosApi.originalsStatus(sess.id), 20_000, 'originals-status'),
    );
    if (verdict.ok) {
      missingOriginalsRef.current = [];
      show(null);
      return true;
    }
    if (!verdict.unavailable && verdict.missing.length > 0) {
      // One recovery pass: any flagged photo whose bytes we STILL hold in this
      // tab gets one more upload attempt, then a single re-sweep. Bytes lost to
      // a refresh can't be recovered here — those stay missing and block.
      const recoverable = verdict.missing
        .map((m) => originalsRef.current.get(m.photo_id))
        .filter((e): e is NonNullable<typeof e> => !!e && (!!e.working || !!e.running));
      if (recoverable.length > 0) {
        recoverable.forEach((e) => { e.done = false; });
        await Promise.allSettled(
          recoverable.map((e) => withTimeout(uploadOriginal(e), PER_FILE_ROUND_TIMEOUT_MS, 'original-recover')
            .catch(() => { e.running = null; })),
        );
        const recheck = await serverVerifyOriginals(
          () => withTimeout(photosApi.originalsStatus(sess.id), 20_000, 'originals-status'),
          { retries: 1 },
        );
        if (recheck.ok) {
          missingOriginalsRef.current = [];
          show(null);
          return true;
        }
        if (!recheck.unavailable) {
          missingOriginalsRef.current = recheck.missing;
          show(null);
          return false;
        }
      }
      missingOriginalsRef.current = verdict.missing;
      show(null);
      return false;
    }
    // Server unreachable → fail closed with no specific list (retryable).
    missingOriginalsRef.current = [];
    show(null);
    return false;
  }, [uploadOriginal]);

  // EARLY SILENT FINALIZE (2026-07-08 — conversion): the blocking
  // "Préparation de vos photos" wait at Confirmer is exactly the originals
  // still pending at that moment. The customer spends minutes on the preview
  // and pricing steps anyway, so run the same wait+retry+server-verify pump
  // SILENTLY as soon as they get there — by the final click the tail is
  // usually zero and no overlay ever appears. Print-integrity gates at
  // submit/PDF stay blocking and unchanged.
  useEffect(() => {
    if (step === 'preview' || step === 'confirm') {
      void finalizeOriginals({ silent: true });
    }
  }, [step, finalizeOriginals]);

  // ── RESUME after leave/return (refresh, tab-kill, app-switch) ─────────────
  // Once per session load: read the persisted pending originals from
  // IndexedDB, ask the SERVER which of them still lack bytes, and re-schedule
  // ONLY those (already-landed ones are just cleaned up — never re-sent). The
  // signed PUT fallback URL is re-minted (the stored one is likely expired);
  // TUS resumes from the persisted fingerprint offset, so a half-sent file
  // continues where it stopped instead of restarting.
  const resumedSessionsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const sid = session?.id;
    if (!sid || resumedSessionsRef.current.has(sid)) return;
    resumedSessionsRef.current.add(sid);
    void (async () => {
      void gcPendingOriginals();
      const pending = await listPendingOriginals(sid);
      if (pending.length === 0) return;
      // Server truth first: which of these are genuinely still missing?
      let missingSet: Set<string> | null = null;
      try {
        const st = await withTimeout(photosApi.originalsStatus(sid), 20_000, 'originals-status');
        missingSet = new Set(st.missing.map((m) => m.photo_id));
      } catch { /* unknown → resume them all; uploads are idempotent overwrites */ }
      let resumed = 0;
      for (const e of pending) {
        if (missingSet && !missingSet.has(e.photoId)) {
          void deletePendingOriginal(e.photoId); // landed in a past life — done
          continue;
        }
        if (originalsRef.current.get(e.photoId)?.done) continue;
        // Fresh PUT fallback URL — the persisted one expires after 1h.
        let uploadUrl = e.uploadUrl;
        try {
          const ov = await withTimeout(photosApi.overwriteUrl(e.photoId), 15_000, 'overwrite-url');
          if (ov?.upload_url) uploadUrl = ov.upload_url;
        } catch { /* keep the stored URL; TUS is primary anyway */ }
        // PERF v2 — bytes are fetched per-photo, only for the ones that
        // genuinely need a resume (listing above was metadata-only).
        const payload = await getPendingPayload(e.photoId);
        if (!payload) { void deletePendingOriginal(e.photoId); continue; }
        const file = new File([payload], e.name, { type: e.type, lastModified: e.lastModified });
        scheduleOriginal(e.photoId, file, uploadUrl, (e.tus as TusBlock | null) ?? null);
        resumed++;
      }
      if (resumed > 0 && mountedRef.current) {
        flashNotice(`Reprise de l'envoi de ${resumed} photo${resumed > 1 ? 's' : ''} en arrière-plan…`);
      }
    })();
  }, [session?.id, scheduleOriginal, flashNotice]);

  // ── Auto-resume when the customer comes BACK (unlock, app-switch return,
  //     bfcache restore, network back). A web page cannot upload while fully
  //     backgrounded — the browser suspends it — so the honest contract is:
  //     pause on leave, resume on return, lose nothing. Entries with a live
  //     `running` promise are left alone (their stall watchdogs already
  //     abort+resume); this kicks the ones whose attempt died while away.
  useEffect(() => {
    const kick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const stalled = Array.from(originalsRef.current.values())
        .filter((e) => !e.done && e.working && !e.running);
      if (stalled.length === 0) return;
      flashNotice(`Reprise de l'envoi de ${stalled.length} photo${stalled.length > 1 ? 's' : ''}…`);
      stalled.forEach((e) => { void uploadOriginal(e); });
    };
    document.addEventListener('visibilitychange', kick);
    window.addEventListener('pageshow', kick);
    window.addEventListener('online', kick);
    // ISSUE 4 — standing retry pump: a failed background original used to sit
    // idle until submit (D258: 49/69 lost that way). While the tab is open
    // and visible, re-kick any not-done entry every 45 s — attempts are
    // idempotent overwrites and entry.running dedupes in-flight ones.
    const pump = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      for (const e of originalsRef.current.values()) {
        if (!e.done && e.working && !e.running) void uploadOriginal(e);
      }
    }, 45_000);
    return () => {
      document.removeEventListener('visibilitychange', kick);
      window.removeEventListener('pageshow', kick);
      window.removeEventListener('online', kick);
      clearInterval(pump);
    };
  }, [uploadOriginal, flashNotice]);

  // ── Upload handler — UPLOAD_PARALLEL concurrent workers ──
  // Was a serial for-loop, taking 5+ min for 50 photos. Now schedules up to
  // 6 in flight at once (same pattern as MagicUploadPage), bringing 50 photos
  // down to ~30-60s depending on network.
  // Per-file status writer — patches one slot in the shared uploadFiles array
  // (kept in uploadFilesRef so the persistent pump never reads a stale closure).
  const setUploadStatus = useCallback((idx: number, patch: Partial<{ status: 'pending' | 'converting' | 'uploading' | 'done' | 'error'; progress: number }>) => {
    setUploadFiles(prev => {
      const next = prev.map((p, j) => j === idx ? { ...p, ...patch } : p);
      uploadFilesRef.current = next;
      return next;
    });
  }, []);

  // Process ONE picked file at its global index. HEIC→JPEG, mint upload-url,
  // upload the small thumbnail (fast/foreground), then schedule the full-res
  // original (TUS) + face analysis in the background. Pipeline + tiers + print
  // quality are UNCHANGED from the prior implementation — only the scheduler
  // around it changed (persistent queue) and the original now uploads via TUS.
  const lastPctRef = useRef<number[]>([]);
  // ONE upload attempt. Each potentially-hanging step is bounded so this whole
  // function always settles; a hang → reject → the caller's retry/finally runs
  // and the pump slot frees. Throws NonRetryableUploadError on a hard 4xx.
  const attemptUploadFile = useCallback(async (idx: number, original: File) => {
    let working = original; // source the tiers derive from (HEIC → its JPEG)
    const onProg = (pct: number) => {
      const prev = lastPctRef.current[idx] ?? -100;
      if (pct >= 100 || pct - prev >= 10) { lastPctRef.current[idx] = pct; setUploadStatus(idx, { progress: pct }); }
    };
    {
      const sess = uploadSessionRef.current;
      if (!sess) throw new Error('Pas de session');
      // HEIC must become a JPEG first — both tiers derive from it. convertHeic
      // is already bounded (heic2any vs HEIC_CONVERT_TIMEOUT_MS) and releases
      // its heicSem permit on every outcome; on failure we fall back to the
      // original blob so the file still makes progress (server-side HEIC path).
      if (isHeic(original)) {
        setUploadStatus(idx, { status: 'converting' });
        await yieldToUi();
        try { working = await convertHeic(original.name, original); } catch { working = original; }
      }

      setUploadStatus(idx, { status: 'uploading' });
      const content_hash = await withTimeout(sha256Hex(working), HASH_TIMEOUT_MS, 'sha256')
        .catch(() => null);
      let resp;
      try {
        // No AbortController on getUploadUrl/apiFetch, so RACE the promise: a
        // hung fetch on flaky cellular rejects here instead of holding the slot.
        resp = await withTimeout(
          photosApi.getUploadUrl(
            sess.id, working.name, working.type || 'image/jpeg',
            { content_hash, file_size_kb: Math.round(working.size / 1024) },
          ),
          UPLOAD_URL_TIMEOUT_MS,
          'upload-url',
        );
      } catch (e) {
        // A 4xx is a hard client error (bad request / dedup conflict) — don't
        // retry it. 5xx / network / timeout fall through as retryable.
        const status = (e as { status?: number }).status;
        if (typeof status === 'number' && status >= 400 && status < 500) {
          throw new NonRetryableUploadError((e as Error).message);
        }
        throw e;
      }
      // Silent dedup: worker rejected a re-upload — count it, no PUT.
      if (resp.duplicate) {
        setUploadStatus(idx, { status: 'done', progress: 100 });
        clearPendingUpload(sess.id, original.name); // FIX #3 — this file is accounted for
        return;
      }
      if (!resp.upload_url || !resp.photo_id) throw new Error('Réponse upload-url invalide');
      const { upload_url, photo_id, tus, thumbnail_upload_url, thumbnail_storage_path, tile_upload_url, tile_storage_path } = resp;

      // ── FAST-UPLOAD MODEL ────────────────────────────────────────────────
      // FOREGROUND = a SMALL ~1600px display image (~150-250KB) → uploads in
      // well under a second → the counter advances FAST. BACKGROUND = the full
      // 3600px PRINT original → scheduled off the critical path (the customer is
      // usually on the cover/mode steps by the time it finishes), and GATED by
      // finalizeOriginals before any AI layout / PDF generation runs. This is the
      // pre-server-rework feel: the customer never waits on the heavy upload.
      //
      // Print quality is UNCHANGED — the background original is still
      // MAX_PHOTO_DIM (3600px) / JPEG_QUALITY and lands at `storage_path`. The
      // 1600px lands at `thumbnail_storage_path`; the worker's signing fallback
      // chain (signed_url = displayUrl(storage_path) ?? thumbnailUrl) serves it
      // until the 3600px transform is warm, so tiles/editor show it immediately.

      // Build the small 1600px display JPEG. Prefer the off-thread worker (gives
      // us source dims too); fall back to the canvas thumbnailer. Bounded by
      // resizeSem so a bulk pick doesn't saturate the worker pool.
      let displayBlob: Blob | null = null;
      let dispW = 0;
      let dispH = 0;
      await resizeSem.acquire();
      try {
        const proc = await processForUploadWithDims(working, THUMB_DIM, THUMB_QUALITY);
        if (proc) {
          displayBlob = proc.blob;
          dispW = proc.width;
          dispH = proc.height;
        } else {
          // Worker pool unavailable → canvas thumbnailer (always resolves; may
          // return null on a non-decodable blob, handled below).
          displayBlob = await makeThumbnail(working, THUMB_DIM, THUMB_QUALITY);
        }
      } finally { resizeSem.release(); }

      const display = displayBlob
        ? new File([displayBlob], 'thumbnail.jpg', { type: 'image/jpeg' })
        : null;
      const contentType = 'image/jpeg';

      // FOREGROUND PUT of the small display image. TRUST the 2xx — no /verify on
      // this path (uploadToSignedUrl resolves only on a genuine 2xx, with its own
      // stall-watchdog + retry, so the body is already in storage). If we somehow
      // couldn't build a thumbnail (non-decodable source), fall back to PUTting
      // the original to its storage_path in the FOREGROUND so the photo still
      // lands + displays (rare; keeps the old guarantee for odd files).
      if (display && thumbnail_upload_url && thumbnail_storage_path) {
        await uploadToSignedUrl(thumbnail_upload_url, display, contentType, onProg);

        // MIGRATION 128 — the ~320px tray tile, built from the SMALL display
        // blob rather than the source: it is already decoded, already the
        // right aspect, and re-decoding a 12MP original on a phone for an
        // 80px tile is exactly the memory spike this flow avoids everywhere
        // else. Replaces a BILLED image-transform (worker lib/photoTiers.ts);
        // best-effort, and a failure just means the tray transforms as before.
        let tileOk = false;
        if (tile_upload_url && tile_storage_path) {
          try {
            const tileBlob = await makeThumbnail(display, TILE_MAX_DIM, TILE_JPEG_QUALITY);
            if (tileBlob) {
              await uploadToSignedUrl(tile_upload_url, new File([tileBlob], 'tile.jpg', { type: 'image/jpeg' }), contentType);
              tileOk = true;
            }
          } catch { /* non-fatal */ }
        }

        // Stamp thumbnail_storage_path (+ dims) so the list endpoint serves the
        // thumbnail until the background original's transform warms up.
        try {
          await photosApi.thumbnailDone(
            photo_id,
            thumbnail_storage_path,
            dispW && dispH ? { width: dispW, height: dispH } : undefined,
            tileOk ? tile_storage_path : null,
          );
        } catch { /* best-effort — display still works via thumbnail path */ }

        // Bytes CONFIRMED → mark the photo DONE + advance the counter NOW. The
        // heavy 3600px original is uploaded in the BACKGROUND below.
        setUploadStatus(idx, { status: 'done', progress: 100 });
        clearPendingUpload(sess.id, original.name); // FIX #3 — accounted for

        // BACKGROUND = the full 3600px print original → storage_path. Registered
        // in originalsRef so finalizeOriginals guarantees + retries it before
        // generation. We hand `working` (the upright source) to scheduleOriginal,
        // which builds the 3600px JPEG itself (one held buffer per photo, released
        // on confirmed upload). NEVER blocks the foreground.
        scheduleOriginal(photo_id, working, upload_url, tus ?? null);
      } else {
        // FALLBACK: no usable thumbnail → upload the 3600px original in the
        // FOREGROUND (the pre-fast-upload behaviour) so this odd file still lands.
        let full: File;
        await resizeSem.acquire();
        let out: Blob | null;
        try { out = await processForUpload(working, MAX_PHOTO_DIM, JPEG_QUALITY); }
        finally { resizeSem.release(); }
        if (out) {
          full = new File([out], working.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
        } else {
          await yieldToUi();
          let f = await normalizeJpegOrientation(working);
          await yieldToUi();
          f = await resizeIfNeeded(f);
          full = f;
        }
        const ct = full.type || 'image/jpeg';
        try {
          await uploadToSignedUrl(upload_url, full, ct, onProg);
        } catch (putErr) {
          if (tus) {
            await uploadViaTus(full, {
              storageHost: tus.storage_host,
              anonKey: tus.anon_key,
              token: tus.token,
              bucket: tus.bucket,
              objectPath: tus.object_path,
              contentType: ct,
              getFreshToken: () => photosApi.tusRefresh(tus.object_path),
              onProgress: onProg,
            });
          } else {
            throw putErr;
          }
        }
        // Fix 2 — byte-check BOTH transports before "done" (a TUS success can be
        // byteless, and even a PUT 2xx is cheap to confirm on this rare path).
        const landed = await confirmOriginalLanded(
          () => withTimeout(photosApi.verifyOriginal(photo_id), UPLOAD_URL_TIMEOUT_MS, 'verify-original'),
        );
        if (!landed) throw new Error('Original byteless after upload — retrying');
        setUploadStatus(idx, { status: 'done', progress: 100 });
        clearPendingUpload(sess.id, original.name);
        // The original IS the storage_path here → mark it done in originalsRef so
        // finalizeOriginals doesn't try to re-upload it.
        originalsRef.current.set(photo_id, { photoId: photo_id, working: null, uploadUrl: upload_url, tus: tus ?? null, done: true, running: null, bytes: original.size });
        // (no thumbnail) — display comes from the storage_path transform
      }

      // INSTANT TILE — tiny (~280px) objectURL from the small display JPEG (or
      // the source) so the grid + count grow PER PHOTO and the tile renders
      // immediately, not when the server display lands. Memory stays bounded.
      const previewSource = display ?? working;
      let previewUrl = await makeTinyPreviewUrl(previewSource, display ? THUMB_DIM : MAX_PHOTO_DIM);
      if (!previewUrl) { try { previewUrl = URL.createObjectURL(previewSource); } catch { previewUrl = null; } }
      if (mountedRef.current) {
        addOptimisticPhoto(photo_id, { album_session_id: sess.id, original_filename: working.name }, previewUrl);
      } else if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
      }
      scheduleAnalysis(working, photo_id); // face/vision (deferred), off the critical path
    }
  }, [setUploadStatus, scheduleAnalysis, addOptimisticPhoto, scheduleOriginal]);

  // Process ONE file with a HARD per-file backstop + retry. The backstop
  // (PER_FILE_TIMEOUT_MS) GUARANTEES this resolves so the pump's `.finally`
  // ALWAYS runs and the slot frees — even if every sub-step bound somehow
  // failed to fire. A hung/5xx/network attempt retries up to UPLOAD_MAX_RETRIES
  // with backoff; a hard 4xx (NonRetryableUploadError) fails immediately. A
  // final failure leaves status:'error' so the failed-batch notice + Réessayer
  // UI surface it. NEVER throws — so the slot can't be lost on an unexpected path.
  const processUploadFile = useCallback(async (idx: number, original: File) => {
    for (let attempt = 0; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
      try {
        await withTimeout(
          attemptUploadFile(idx, original),
          PER_FILE_TIMEOUT_MS,
          `processUploadFile ${original.name}`,
        );
        return; // success — slot frees via the pump's .finally
      } catch (err) {
        const nonRetryable = err instanceof NonRetryableUploadError;
        const isLast = attempt >= UPLOAD_MAX_RETRIES || nonRetryable;
        console.warn(
          `[upload] photo ${nonRetryable ? 'failed (no retry)' : `attempt ${attempt + 1} failed`}:`,
          original.name, err,
        );
        if (isLast) {
          setUploadStatus(idx, { status: 'error' });
          return; // give up — stays errored for the Réessayer button
        }
        // Backoff before the next attempt (does NOT block other slots — this is
        // already per-slot; other files keep uploading in their own slots).
        const backoff = UPLOAD_RETRY_BACKOFF_MS[attempt] ?? 3_000;
        setUploadStatus(idx, { status: 'pending', progress: 0 });
        lastPctRef.current[idx] = -100;
        await new Promise((r) => setTimeout(r, backoff));
      }
    }
  }, [attemptUploadFile, setUploadStatus]);

  // Persistent pump — drains uploadQueueRef at up to UPLOAD_PARALLEL in flight.
  // runningRef guarantees ONE scheduling loop: a second handleUpload mid-upload
  // just tops up free slots instead of restarting (Phase 2c re-entrancy guard).
  // When the queue drains AND nothing is in flight, flips isUploading off and
  // refreshes the photos list + surfaces any aggregate failure.
  const pumpUploadsRef = useRef<(() => void) | null>(null);
  const finishUploadBatch = useCallback(async () => {
    const sess = uploadSessionRef.current;
    if (sess) {
      try {
        const refreshed = await photosApi.list(sess.id);
        const server = refreshed.photos ?? [];
        // RECONCILE (not replace) — the optimistic per-photo adds already grew
        // the grid + count during the batch. Now MERGE the authoritative server
        // rows in by id: for ids we already have, fill the richer server fields
        // (real signed_url / tray_signed_url / dimensions / analysis) while
        // KEEPING any optimistic-only entry the server hasn't surfaced yet
        // (e.g. eventual consistency). De-dupe by id so nothing double-counts.
        // FIX #4b — collect names of rows the server confirms byteless but for
        // which we hold NO local preview (i.e. NOT a freshly-PUT optimistic row,
        // but a genuinely-missing one re-surfacing). These are surfaced as
        // retry/error tiles below — consistent with the load-path `byteless`
        // handling — instead of being kept as silent blank placed tiles. We never
        // count them as added.
        const missingRetryNames: string[] = [];
        setPhotos((prev) => {
          const serverById = new Map(server.map((p) => [p.id, p]));
          const seen = new Set<string>();
          const merged: (Photo & { signed_url: string | null; tray_signed_url?: string | null })[] = [];
          // 1. Walk existing (optimistic + previously-loaded) in order, upgrading
          //    each to its server row when one exists. Preserves tile ordering.
          //    We KEEP optimistic rows even if the server flags original_missing
          //    SO LONG AS we still hold their local preview: each passed inline
          //    via a trusted PUT, so a missing flag is just the storage `list`
          //    lagging the just-completed PUT (eventual consistency), and the
          //    preview proves we have the photo. But a server-confirmed-missing
          //    row with NO local preview (a prior-visit failure re-surfacing) is
          //    a real loss → drop it and surface it as a retry tile (FIX #4b).
          for (const p of prev) {
            const s = serverById.get(p.id);
            seen.add(p.id);
            if (s?.original_missing && !localPreviewsRef.current.has(p.id)) {
              missingRetryNames.push((s.original_filename ?? p.original_filename ?? 'photo'));
              continue; // not placed, not counted
            }
            merged.push(s ? { ...p, ...s } : p);
          }
          // 2. Append any server rows we didn't already have (e.g. dedup path,
          //    or photos uploaded in a prior visit not yet in state) — but DROP
          //    byteless ones (a prior-visit failed upload): they'd be silent
          //    blanks. They're surfaced as retry tiles below (FIX #4b); here we
          //    just never count them as added.
          for (const s of server) {
            if (!seen.has(s.id)) {
              seen.add(s.id);
              if (!s.original_missing) merged.push(s);
              else missingRetryNames.push(s.original_filename ?? 'photo');
            }
          }
          return merged;
        });
        // Surface the byteless rows as retryable error tiles (mirrors the
        // load-effect `byteless` path). De-duped by filename against any tile
        // we already track so a retried file doesn't show twice.
        if (missingRetryNames.length > 0) {
          setUploadFiles((prevU) => {
            const haveNames = new Set(prevU.map((u) => u.name.trim().toLowerCase()));
            const fresh = missingRetryNames
              .filter((n) => !haveNames.has(n.trim().toLowerCase()))
              .map((n) => ({ name: n, progress: 0, status: 'error' as const }));
            if (fresh.length === 0) return prevU;
            const next = [...prevU, ...fresh];
            uploadFilesRef.current = next;
            return next;
          });
        }
      } catch (err) { console.error('Upload refresh error:', err); }
    }
    setIsUploading(false);
    // Aggregate failure surfacing — if any file ended in error, tell the
    // customer + offer retry (the per-file error chips + Réessayer button).
    const failed = uploadFilesRef.current.filter((f) => f.status === 'error').length;
    if (failed > 0) {
      flashNotice(`${failed} photo${failed > 1 ? 's' : ''} échouée${failed > 1 ? 's' : ''} — appuyez sur Réessayer.`, true);
    }
  }, [flashNotice]);
  const pumpUploads = useCallback(() => {
    if (uploadRunningRef.current) return; // re-entrancy guard
    uploadRunningRef.current = true;
    const launch = () => {
      while (uploadActiveRef.current < UPLOAD_PARALLEL_EFFECTIVE && uploadQueueRef.current.length > 0) {
        const work = uploadQueueRef.current.shift()!;
        uploadActiveRef.current++;
        void processUploadFile(work.idx, work.file).finally(() => {
          uploadActiveRef.current--;
          if (uploadActiveRef.current === 0 && uploadQueueRef.current.length === 0) {
            void finishUploadBatch();
          } else {
            launch();
          }
        });
      }
    };
    launch();
    uploadRunningRef.current = false;
  }, [processUploadFile, finishUploadBatch]);
  useEffect(() => { pumpUploadsRef.current = pumpUploads; }, [pumpUploads]);

  // ── Upload handler — appends a pick to the shared queue (Phase 2c) ──
  // Dedupe against photos already uploaded for this session, cap iOS picks at
  // 30 (Phase 4), kill the silent all-duplicates return (Phase 3), then append
  // the new files to the persistent queue and pump. A second pick mid-upload
  // joins the in-flight queue rather than clobbering progress.
  const handleUpload = useCallback(async (files: FileList) => {
    const rawArr = Array.from(files).slice(0, MAX_PHOTOS);
    // [FIX #1] The batch cap is a SUGGESTION, NEVER a hard limit. A big single
    // pick is heavier on iPhone (HEIC RAM), so when the customer picks more than
    // ~20 we HINT that smaller batches are faster — but we accept and upload
    // EVERY selected photo on EVERY device. Nothing is ever truncated/dropped
    // (the old iOS `slice(0,20)` silently lost the rest AND dead-ended re-picks).
    if (isIosDevice() && rawArr.length > IOS_PICK_CAP) {
      flashNotice(`${rawArr.length} photos ajoutées · astuce : par lots de ~${IOS_PICK_CAP} c'est plus rapide.`);
    }
    // [FIX #2] Dedupe by what ACTUALLY LANDED, not by row-existence. A file is
    // skipped only when a photo with the same name already has its display tier
    // (`thumbnail_storage_path`) — i.e. it really uploaded. Byteless / failed
    // rows are NOT treated as done, so re-picking the same camera roll re-uploads
    // the MISSING/failed files instead of refusing them. Also skip files still
    // in-flight (non-errored slots) so a rapid double-pick doesn't double-add.
    // A re-picked file that matches a previously-FAILED slot is RETRIED in place
    // (re-queued on its existing tile — no duplicate tile, no stale error).
    const completedNames = new Set(
      photos.filter((p) => !!p.thumbnail_storage_path)
        .map((p) => (p.original_filename ?? '').trim().toLowerCase()).filter(Boolean),
    );
    const inFlightNames = new Set(
      uploadFilesRef.current.filter((u) => u.status !== 'error')
        .map((u) => u.name.trim().toLowerCase()).filter(Boolean),
    );
    const failedByName = new Map<string, number>();
    uploadFilesRef.current.forEach((u, i) => {
      if (u.status === 'error') {
        const k = u.name.trim().toLowerCase();
        if (k && !failedByName.has(k)) failedByName.set(k, i);
      }
    });
    const retrySlots: Array<{ idx: number; file: File }> = [];
    const fileArr = rawArr.filter((f) => {
      const key = f.name.trim().toLowerCase();
      if (completedNames.has(key) || inFlightNames.has(key)) return false; // already done / in-flight
      const failedIdx = failedByName.get(key);
      if (failedIdx != null) { retrySlots.push({ idx: failedIdx, file: f }); failedByName.delete(key); return false; }
      return true; // genuinely new
    });
    const skippedCount = rawArr.length - fileArr.length - retrySlots.length;
    if (fileArr.length === 0 && retrySlots.length === 0) {
      // Everything in this pick is already uploaded — tell the customer so the
      // picker dismissal isn't a silent dead end.
      if (skippedCount > 0) flashNotice('Ces photos sont déjà ajoutées.');
      return;
    }
    if (skippedCount > 0) {
      flashNotice(`${skippedCount} déjà ajoutée${skippedCount > 1 ? 's' : ''} ignorée${skippedCount > 1 ? 's' : ''}.`);
    }

    setIsUploading(true);
    // Append NEW slots to the SHARED uploadFiles array — never replace it, so a
    // pick arriving mid-upload doesn't clobber in-flight progress. (Retries reuse
    // their existing slot index, so they are NOT appended here.)
    const baseIdx = uploadFilesRef.current.length;
    const newItems = fileArr.map(f => ({ name: f.name, progress: 0, status: 'pending' as const }));
    if (newItems.length > 0) {
      setUploadFiles(prev => {
        const next = [...prev, ...newItems];
        uploadFilesRef.current = next;
        return next;
      });
    }
    // Give the browser a paint frame so the "Importation..." spinner is visible
    // before the ensureSession network call + the HEIC decode loop hog the
    // main thread.
    await yieldToUi();

    try {
      // Resolve the session ONCE and share it across picks via the ref so a
      // concurrent pick reuses it (idempotent upsert anyway).
      const sess = await ensureSession();
      uploadSessionRef.current = sess;
      // FIX #3 — persist these pending file identities NOW (keyed by session) so a
      // refresh mid-batch leaves a trail to reconcile against the server. Each is
      // cleared on its own successful upload (see attemptUploadFile / dedup path).
      addPendingUploads(sess.id, [...fileArr.map((f) => f.name), ...retrySlots.map((r) => r.file.name)]);
    } catch (err) {
      console.error('Upload session error:', err);
      // Mark the just-added slots as errored so the customer can retry.
      newItems.forEach((_, k) => setUploadStatus(baseIdx + k, { status: 'error' }));
      flashNotice('Connexion impossible — appuyez sur Réessayer.', true);
      // Still let the pump settle isUploading if nothing else is running.
      if (uploadActiveRef.current === 0 && uploadQueueRef.current.length === 0) setIsUploading(false);
      return;
    }

    // Retry the matched FAILED slots IN PLACE (re-queue on their existing tile),
    // then enqueue the genuinely-new files, and (re-)pump. The guard inside
    // pumpUploads makes a concurrent call a no-op that just tops up slots. Retain
    // each source File in retrySourcesRef so a one-tap Réessayer can also re-run
    // a failed slot without a re-pick.
    for (const { idx, file } of retrySlots) {
      retrySourcesRef.current.set(idx, file);
      setUploadStatus(idx, { status: 'pending', progress: 0 });
      uploadQueueRef.current.push({ idx, file });
    }
    fileArr.forEach((f, k) => {
      const idx = baseIdx + k;
      retrySourcesRef.current.set(idx, f);
      uploadQueueRef.current.push({ idx, file: f });
    });
    pumpUploadsRef.current?.();
  }, [ensureSession, photos, flashNotice, setUploadStatus]);

  // Retry every errored file in place — re-queue their slots and re-pump from
  // the retained source Files. If a source is gone (shouldn't happen — kept
  // until the page leaves the step), surface a re-pick prompt instead.
  const retryFailedUploads = useCallback(() => {
    const failedIdxs = uploadFilesRef.current
      .map((f, i) => (f.status === 'error' ? i : -1))
      .filter((i) => i >= 0);
    if (failedIdxs.length === 0) return;
    let requeued = 0;
    for (const idx of failedIdxs) {
      const file = retrySourcesRef.current.get(idx);
      if (!file) continue; // source not retained → needs a re-pick (rare)
      setUploadStatus(idx, { status: 'pending', progress: 0 });
      uploadQueueRef.current.push({ idx, file });
      requeued++;
    }
    if (requeued === 0) {
      flashNotice('Ces photos doivent être re-sélectionnées.');
      return;
    }
    setAddNotice(null);
    setIsUploading(true);
    pumpUploadsRef.current?.();
  }, [setUploadStatus, flashNotice]);

  // ── Prewarm the /ai/layout call as soon as the customer has enough
  //     photos and isn't actively uploading. The promise resolves in the
  //     background while they read the "Magie AI / Mode manuel" choice,
  //     so the click feels near-instant. Re-keyed on photo count so
  //     adding more photos invalidates the stale prewarm.
  useEffect(() => {
    if (!session?.id || !order) return;
    if (photos.length < MIN_PHOTOS) return;
    if (isUploading) return;
    // Hold the prewarm until background face/EXIF analysis settles. Firing
    // /ai/layout while tags are still landing would prewarm against stale
    // metadata and waste the call (the key wouldn't change, so it'd never
    // re-fire with the complete data). When the count hits 0 this effect
    // re-runs and prewarms against fully-analyzed photos.
    if (pendingAnalysisCount > 0) return;
    const key = `${session.id}::${photos.length}`;
    if (prewarmKeyRef.current === key) return;
    const MAX_PAGE_COUNT = 300;
    const requiredPages = Math.min(
      MAX_PAGE_COUNT,
      Math.max(order.page_count ?? 30, Math.ceil(photos.length / 2)),
    );
    const pref = order.layout || 'balanced';
    prewarmKeyRef.current = key;
    // FIX #4 (2026-07-05): AI Magic ALWAYS uses the upload sequence — first
    // uploaded photo → first page (worker sorts by created_at, instructs the
    // AI to keep the exact order, then ENFORCES it post-AI; templates kept).
    prewarmRef.current = aiApi.generateLayout(session.id, requiredPages, pref, { shopify_order_id: shopifyOrderId ?? '', use_upload_order: true });
    // Swallow rejection so an unhandled-promise warning doesn't fire; the
    // user-clicked path will rerun the call if the prewarm errored.
    prewarmRef.current.catch(() => {
      prewarmRef.current = null;
      prewarmKeyRef.current = null;
    });
  }, [session?.id, order, photos.length, isUploading, pendingAnalysisCount, shopifyOrderId]);

  // ── Delete an uploaded photo (X icon on the upload-step thumbnail grid).
  //     Optimistically removes from local state, then calls the worker. On
  //     failure rolls the photo back so the customer notices the operation
  //     didn't take. No confirm dialog — the icon is intentionally small +
  //     corner-positioned to require deliberate touch.
  const handleDeletePhoto = useCallback(async (photoId: string) => {
    const previous = photos;
    setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    try {
      await photosApi.delete(photoId);
      dropLocalPreview(photoId); // gone for good → release its preview URL
      // Drop its background-original tracker so finalizeOriginals never waits on
      // (or re-uploads to) a deleted photo's storage path.
      originalsRef.current.delete(photoId);
      void deletePendingOriginal(photoId); // and its persisted resume bytes
    } catch (err) {
      console.error('Failed to delete photo:', err);
      setPhotos(previous); // restored → keep its preview
    }
  }, [photos, dropLocalPreview]);

  // Bulk delete — remove many selected photos at once (multi-select on the
  // upload step). Optimistic: drop them all from view immediately, then delete
  // in bounded parallel so a 50-photo purge isn't 50 serial round-trips. Any
  // that fail server-side are restored so the customer sees they're still there.
  const handleDeletePhotos = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const previous = photos;
    setPhotos((prev) => prev.filter((p) => !idSet.has(p.id)));
    const LIMIT = 8;
    const failed: string[] = [];
    for (let i = 0; i < ids.length; i += LIMIT) {
      const chunk = ids.slice(i, i + LIMIT);
      const res = await Promise.allSettled(chunk.map((id) => photosApi.delete(id)));
      res.forEach((r, j) => {
        if (r.status === 'rejected') failed.push(chunk[j]);
        else {
          dropLocalPreview(chunk[j]); // deleted for good → release its preview
          originalsRef.current.delete(chunk[j]); // drop its background-original tracker
          void deletePendingOriginal(chunk[j]); // and its persisted resume bytes
        }
      });
    }
    if (failed.length > 0) {
      const failedSet = new Set(failed);
      const restore = previous.filter((p) => failedSet.has(p.id));
      setPhotos((prev) => {
        const have = new Set(prev.map((p) => p.id));
        const add = restore.filter((p) => !have.has(p.id));
        return add.length ? [...prev, ...add] : prev;
      });
      console.error(`[upload] ${failed.length} photo(s) failed to delete`);
    }
  }, [photos, dropLocalPreview]);

  // ── Generate AI layout — lands on the in-flow PREVIEW (full cover spread
  //     + interior pages + Modifier / Ajouter 2 pages / Confirmer buttons).
  //     Modifier hands off to the full editor; Confirmer goes straight to
  //     checkout. Previous version skipped the preview and dumped the
  //     customer in the editor — they explicitly want a preview pass first.
  //
  //  D75 architectural fix (2026-05-20): this used to ship the worker's
  //  RAW AI response straight to the preview, bypassing the entire client
  //  post-pass pipeline (safety net, force-solo, slow-start/end, priority
  //  swap, face-aware translates, compactPages, etc.). Result: customer
  //  landed in the editor with 13+ unplaced photos and had to manually
  //  click Régénérer IA to repair it. Now we run `composeFinalLayout`
  //  on the worker response — the SAME shared pipeline the editor's
  //  Régénérer IA button uses — so the preview is the final state and
  //  the editor never needs a "repair regen".
  // ── Finalize ONE AI generation (run post-passes + save + land on preview).
  //     Reused by BOTH recovery paths — the awaited fetch resolving, and the
  //     resilience poll recovering the worker-persisted pending result after a
  //     backgrounding. Guarded by finalizedKeyRef so it runs EXACTLY ONCE per
  //     photo set: the loser of the fetch-vs-poll race is a no-op.
  //
  //     `expectedKey` = the photos_key the CURRENT photo set hashes to. We only
  //     finalize a rawLayout whose generation matched this set; a stale pending
  //     (different set — customer added/removed a photo since) is rejected by
  //     the caller before reaching here.
  const finalizeLayoutResult = useCallback(async (
    rawLayout: LayoutPlan,
    expectedKey: string,
  ): Promise<boolean> => {
    if (!session?.id) return false;
    // Single-finalize guard: this exact photo set already finalized (or is
    // mid-finalize) → no-op so we never double-run post-passes or double-save.
    if (finalizedKeyRef.current === expectedKey) return false;
    if (finalizingRef.current) return false;
    finalizingRef.current = true;
    finalizedKeyRef.current = expectedKey;
    try {
      // Refresh photos so the post-passes see latest vision_tags + dims.
      let composablePhotos: ComposablePhoto[] = photos as ComposablePhoto[];
      try {
        const photosRes = await photosApi.list(session.id);
        // Exclude byteless originals so the layout never places a blank slot.
        // …and no held similar shot (owner, 2026-10-24, migration 267): this is
        // the flow's FIRST build, nothing is on a page yet. Hidden duplicates
        // never reach this list (the worker leaves them out).
        composablePhotos = forAutomaticLayout((photosRes.photos ?? []).filter(
          (p) => !p.original_missing,
        ), new Set<string>()) as ComposablePhoto[];
      } catch { /* fall back to in-memory photos */ }

      // Refresh session for album_category + cover state from worker.
      let categoryFromSession: AlbumCategory | null = null;
      try {
        const sessRes = await sessionsApi.get(shopifyOrderId!, currentAlbum);
        if (sessRes.session) {
          setSession(sessRes.session);
          categoryFromSession =
            ((sessRes.session as { album_category?: AlbumCategory | null }).album_category) ?? null;
        }
      } catch { /* non-blocking */ }

      // Run the full client-side pipeline (same as editor's Régénérer IA).
      const composed = composeFinalLayout(
        rawLayout,
        composablePhotos,
        categoryFromSession,
        // FIX #4 — upload order is active on this path: skip every
        // reordering post-pass (hero promotion, anti-monotone, priority
        // swaps) exactly like the editor's "Ordre d'upload" regenerate.
        { trace: true, useUploadOrder: true },
      );

      // Persist the composed layout so the editor — when the customer clicks
      // Modifier — loads the post-passed state, not the raw AI plan.
      try {
        await sessionsApi.upsert(shopifyOrderId!, currentAlbum, {
          pages: {
            pages: withSlotPositions(composed.pages, composed.slotPositions),
            unassigned_photos: composed.unassigned,
          },
        });
      } catch (e) {
        console.warn('[magic] failed to persist composed layout:', (e as Error).message);
      }

      // Consumed — clear the worker's pending row so a later poll/reload never
      // re-finalizes it. Fire-and-forget; photos_key would reject it anyway.
      aiApi.clearPendingLayout(session.id);

      // Layout is generated — stop the remaining face analyses so they don't
      // jank the preview (they've served their purpose).
      stopAnalysisRef.current = true;
      setLayout({
        pages: withSlotPositions(composed.pages, composed.slotPositions),
        unassigned_photos: composed.unassigned,
      });
      // ── Centralized loader teardown (Fix D) ────────────────────────────────
      // This is the SINGLE sink every finalize path flows through (the awaited
      // fetch resolving, the in-flight poll, the resume-on-load effect, AND the
      // visibilitychange/focus handler), and it's guarded by finalizedKeyRef so
      // it runs EXACTLY ONCE per photo set. Tear the loader down here so no
      // caller can leave the 'magic' overlay frozen:
      //   1. mark this set settled so handleAiMagic's (possibly resumed-from-
      //      suspension) fetch/poll loops read it and exit instead of polling on;
      //   2. abort the in-flight /ai/layout fetch so handleAiMagic stops awaiting
      //      a dead request, its progress animation ends, and it can't re-assert
      //      'magic' afterward;
      //   3. leave 'magic' for 'preview' (dismisses StepMagic + its fake bar).
      // Idempotent: a second finalize never reaches here (finalizedKeyRef no-op).
      magicSettledKeyRef.current = expectedKey;
      try { aiAbortRef.current?.abort(); } catch { /* already aborted/settled */ }
      aiAbortRef.current = null;
      goToStep('preview');
      return true;
    } finally {
      finalizingRef.current = false;
    }
  }, [session?.id, photos, shopifyOrderId, currentAlbum, goToStep]);

  // ── Fix D: consume a worker-persisted pending generation ──────────────────
  //     Shared by handleAiMagic's restart guard, the resume-on-load effect, AND
  //     the visibilitychange/focus handler. Polls /ai/layout/pending; if a
  //     pending result MATCHES the current photo set (photos_key), runs the FULL
  //     finalize (post-passes + save + → preview) WITHOUT re-calling /ai/layout.
  //     A stale pending (different photo set — customer added/removed a photo
  //     since the run started) is correctly ignored → the customer re-triggers a
  //     fresh run.
  //
  //     `pollWhileGenerating` keeps polling (4s) while a run is still in flight
  //     (ai_generating_at recent) until the pending lands or the deadline hits.
  //     Returns true once a matching pending has been finalized (so callers can
  //     skip any restart). finalizeLayoutResult's finalizedKeyRef guard makes a
  //     double-consume (multiple callers racing) a no-op.
  const consumePendingRef = useRef(false); // re-entrancy guard for the poller
  const tryConsumePending = useCallback(async (
    opts: { pollWhileGenerating: boolean; isCancelled?: () => boolean },
  ): Promise<boolean> => {
    const sid = session?.id;
    if (!sid) return false;
    if (photos.length < MIN_PHOTOS) return false;
    const expectedKey = photosKey(photos.map((p) => p.id));
    // Already finalized this exact set → nothing to consume.
    if (finalizedKeyRef.current === expectedKey) return false;
    if (consumePendingRef.current) return false; // one poller at a time
    const cancelled = () => opts.isCancelled?.() ?? false;

    consumePendingRef.current = true;
    try {
      let info: { pending: { result: LayoutPlan; photos_key: string } | null; generating: boolean };
      try {
        info = await aiApi.pendingLayout(sid);
      } catch { return false; }
      if (cancelled()) return false;

      // A matching pending already exists → finalize it straight away. This
      // wins even if a STALE saved `layout` was hydrated on load (e.g. the
      // session carried pages from a prior smaller run): a fresh, in-flight
      // generation for the CURRENT photo set takes precedence over the stale
      // album. (A genuinely-final album with no in-flight run never produces a
      // matching, fresher pending — clearPendingLayout wiped it on finalize.)
      if (info.pending && info.pending.photos_key === expectedKey) {
        goToStep('magic');
        return await finalizeLayoutResult(info.pending.result, expectedKey);
      }

      // No pending yet but a run is in flight → poll until it lands.
      if (info.generating && opts.pollWhileGenerating) {
        goToStep('magic');
        const POLL_INTERVAL_MS = 4_000;
        const DEADLINE = Date.now() + 4 * 60_000;
        while (!cancelled() && Date.now() < DEADLINE) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (cancelled()) return false;
          try {
            const { pending } = await aiApi.pendingLayout(sid);
            if (cancelled()) return false;
            if (pending && pending.photos_key === expectedKey) {
              return await finalizeLayoutResult(pending.result, expectedKey);
            }
          } catch { /* transient — keep polling */ }
        }
      }
      return false;
    } finally {
      consumePendingRef.current = false;
    }
  }, [session?.id, photos, goToStep, finalizeLayoutResult]);

  const handleAiMagic = useCallback(async () => {
    if (!session?.id) return;
    const sessionId = session.id;
    goToStep('magic');
    // New click for THIS photo set ⇒ allow finalize to run once.
    const expectedKey = photosKey(photos.map((p) => p.id));
    if (finalizedKeyRef.current !== expectedKey) finalizedKeyRef.current = null;
    // Fresh generation for this set ⇒ clear any prior "settled" mark + abort a
    // stray earlier controller, then arm a new AbortController the resume paths
    // can use to interrupt this run's (possibly soon-to-be-dead) fetch.
    if (magicSettledKeyRef.current !== expectedKey) magicSettledKeyRef.current = null;
    try { aiAbortRef.current?.abort(); } catch { /* ignore */ }
    const aiAbort = new AbortController();
    aiAbortRef.current = aiAbort;
    // True once THIS run (or a resume path acting for the same set) has
    // finalized — read from the ref so a resume-path finalize that happened
    // while this function was frozen (backgrounded tab) is visible here.
    const isSettled = () => magicSettledKeyRef.current === expectedKey;
    try {
      // ── Restart guard (Fix D) ────────────────────────────────────────────
      // Before issuing a FRESH /ai/layout, check whether the worker already
      // persisted a pending result for THIS exact photo set (e.g. the customer
      // backgrounded mid-run, the fetch died, the worker finished via
      // waitUntil, and they returned + re-clicked Magie AI). If so, finalize
      // the persisted result and return — NEVER re-call DeepSeek for a set the
      // worker already laid out. A stale pending (different set) is ignored by
      // photos_key and we fall through to a legitimate fresh run.
      if (await tryConsumePending({ pollWhileGenerating: false })) return;

      // FAST-UPLOAD GATE: the FOREGROUND upload only lands the small 1600px
      // display image; the 3600px PRINT originals upload in the BACKGROUND. The
      // AI layout + the eventual PDF need every full-res original IN STORAGE, so
      // ensure they ALL landed before generation. Usually a no-op here (the
      // customer spent time on the cover/mode steps while the background
      // originals finished); if any are still in flight this shows
      // "Finalisation des photos X/N" + retries the failed ones. NEVER let
      // generation proceed with a missing 3600px original.
      // NON-BLOCKING at the Magie-AI step: the AI lays out from the display
      // tier, so the customer shouldn't stare at a "Finalisation" modal here.
      // The originals keep uploading silently in the background; the SUBMIT
      // and PDF gates below remain blocking, so print quality is untouched.
      void finalizeOriginals({ silent: true });

      // Quality guard: ensure background face/EXIF analysis has settled before
      // the layout runs, so face-aware placement + hero scoring read complete
      // DB metadata. Bounded by awaitPendingAnalyses' internal ceiling so a
      // stuck analysis can't hang the magic button.
      // ISSUE 2 (2026-07-05): ceiling 20s→10s. Analyses normally settle in
      // 1-5s; past 10s it's a straggler/hang and the layout runs with the tags
      // that DID land (same behaviour the 20s cap already had — just 10s less
      // dead waiting in the worst case). The AI call itself dominates; the
      // prewarm (fires while the customer reads the AI/Manuel choice) remains
      // the main latency hider.
      await awaitPendingAnalyses(10_000);
      const MAX_PAGE_COUNT = 300;
      const requiredPages = Math.min(MAX_PAGE_COUNT, Math.max(order?.page_count ?? 30, Math.ceil(photos.length / 2)));
      const pref = order?.layout || 'balanced';
      // Reuse the background prewarm if it matches the current photo set —
      // typically saves 30–60s of perceived wait because DeepSeek has been
      // running while the customer read the mode-choice page.
      const prewarmKey = `${sessionId}::${photos.length}`;
      const usePrewarm = prewarmRef.current && prewarmKeyRef.current === prewarmKey;

      // ── Race: the awaited fetch vs the resilience poll. ──────────────────
      // On iOS, backgrounding the tab suspends JS → the fetch dies and never
      // resolves. The worker, kept alive by waitUntil, still finishes DeepSeek
      // and persists pending_ai_layout. The poll (resumes when the tab wakes)
      // recovers it. Whichever path returns a USABLE layout first finalizes;
      // finalizeLayoutResult's guard makes the second a no-op.
      const fetchPath = (async () => {
        const res = usePrewarm
          ? await prewarmRef.current!
          : await aiApi.generateLayout(sessionId, requiredPages, pref, { shopify_order_id: shopifyOrderId ?? '', signal: aiAbort.signal, use_upload_order: true });
        if (usePrewarm) { prewarmRef.current = null; prewarmKeyRef.current = null; }
        // eslint-disable-next-line no-console
        console.log(`[magic] /ai/layout ${usePrewarm ? 'prewarmed' : 'cold'} returned for ${photos.length} photos`);
        if (isSettled()) return;
        await finalizeLayoutResult(res.layout, expectedKey);
      })().catch((e) => {
        // Two EXPECTED non-errors here, both silent exits (no error screen, no
        // restart): (1) the fetch was ABORTED because a resume path already
        // finalized the worker-persisted pending — magicSettledKeyRef is set, so
        // we just stop; (2) the fetch DIED from backgrounding — the poll covers
        // it. Either way the teardown lives in finalizeLayoutResult, not here.
        if (aiAbort.signal.aborted || isSettled()) return;
        console.warn('[magic] fetch path error (poll will cover):', (e as Error)?.message);
      });

      const pollPath = (async () => {
        // Poll every 4s for the worker-persisted pending result. Capped so a
        // never-persisting run (e.g. worker killed pre-waitUntil) eventually
        // bails to the fetch path's outcome instead of polling forever.
        const POLL_INTERVAL_MS = 4_000;
        const POLL_DEADLINE = Date.now() + 4 * 60_000;
        while (!isSettled() && Date.now() < POLL_DEADLINE) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (isSettled()) return;
          try {
            const { pending } = await aiApi.pendingLayout(sessionId);
            if (isSettled()) return;
            if (!pending) continue;
            // STALENESS GUARD: only use a pending result whose generation
            // matched the CURRENT photo set. A mismatch means the customer
            // changed photos since the run started — ignore it.
            if (pending.photos_key !== expectedKey) {
              console.warn('[magic] pending result is stale (photos_key mismatch) — ignoring');
              continue;
            }
            await finalizeLayoutResult(pending.result, expectedKey);
            // finalize sets magicSettledKeyRef → loop guard exits next check.
            if (isSettled()) return;
          } catch { /* transient — keep polling */ }
        }
      })();

      await Promise.race([fetchPath, pollPath]);
      // Give the slower path a brief beat to finalize if the race winner didn't
      // (e.g. fetch returned but finalize was already claimed by the poll).
      await Promise.allSettled([fetchPath, pollPath]);

      // Only fall back to the mode choice if NOTHING finalized this set — and
      // crucially NOT when a resume path (visibilitychange/resume effect) did
      // the finalize while we were frozen: magicSettledKeyRef OR finalizedKeyRef
      // matching means the loader was already torn down to 'preview'.
      if (!isSettled() && finalizedKeyRef.current !== expectedKey && !aiAbort.signal.aborted) {
        // Neither path produced a usable layout (generation never landed).
        console.error('[magic] AI layout did not complete (fetch + poll both empty)');
        goToStep('mode');
      }
    } catch (err) {
      // An aborted fetch (resume path took over) is a silent exit — the teardown
      // already routed to 'preview'; never show an error or restart.
      if (aiAbort.signal.aborted || isSettled()) return;
      console.error('AI layout failed:', err);
      if (finalizedKeyRef.current !== expectedKey) goToStep('mode');
    }
  }, [session?.id, order?.page_count, order?.layout, photos, awaitPendingAnalyses, finalizeLayoutResult, goToStep, tryConsumePending, finalizeOriginals, flashNotice, shopifyOrderId]);

  // ── Fix D: resume a generation that was in flight when the customer left ──
  //     If the customer reloads / returns mid-AI-Magic (their phone killed the
  //     tab), and either a pending result already exists OR a generation is
  //     still in flight (ai_generating_at recent), re-enter the loading screen
  //     and POLL for the result instead of restarting DeepSeek. Fires once per
  //     session once photos are loaded.
  //
  //     NOTE: we deliberately do NOT bail on `if (layout)` here. A stale saved
  //     layout (e.g. 58 pages from a prior run) is hydrated on load and would
  //     have suppressed the resume — the bug where a fresh, worker-finished
  //     115-photo pending sat UNCONSUMED while the customer saw the old album.
  //     tryConsumePending only acts when a pending MATCHES the current set (or a
  //     run is in flight), so it can't override a legitimately-final album.
  const resumeMagicCheckedRef = useRef<string | null>(null);
  useEffect(() => {
    const sid = session?.id;
    if (!sid) return;
    if (photos.length < MIN_PHOTOS) return;            // need the photo set to key on
    if (resumeMagicCheckedRef.current === sid) return; // once per session
    if (step === 'confirm' || step === 'success') return; // past the magic gate / terminal
    resumeMagicCheckedRef.current = sid;

    // Poll-while-generating (re-enters the loading screen) ONLY at the magic
    // gate, so a customer who navigated BACK to 'upload' to add photos isn't
    // yanked into a loader. A matching pending that's ALREADY landed is still
    // finalized regardless of step (it's the finished album for the current
    // set — see tryConsumePending), it just won't sit and poll on 'upload'.
    const atMagicGate = step === 'mode' || step === 'magic';
    const expectedKey = photosKey(photos.map((p) => p.id));
    let cancelled = false;
    (async () => {
      const consumed = await tryConsumePending({
        pollWhileGenerating: atMagicGate,
        isCancelled: () => cancelled,
      });
      // Timed out / nothing to resume → only fall back to the mode choice if we
      // started a loader (step 'magic') and never finalized. Never yank a
      // customer already sitting on a real preview/upload step into 'mode'.
      if (!consumed && !cancelled && step === 'magic' &&
          finalizedKeyRef.current !== expectedKey) {
        goToStep('mode');
      }
    })();
    return () => { cancelled = true; };
  }, [session?.id, photos, step, goToStep, tryConsumePending]);

  // ── Fix D: poll the instant the tab becomes visible / regains focus ───────
  //     iOS suspends JS when the tab backgrounds — the setTimeout poll loops in
  //     handleAiMagic / the resume effect are frozen and may not resume for many
  //     seconds. The moment the customer returns, fire an IMMEDIATE pending
  //     check + finalize-if-ready so the finished album appears at once instead
  //     of waiting for a suspended interval tick. Single-flight via
  //     consumePendingRef; idempotent via finalizedKeyRef. Only runs at/near the
  //     magic gate so we don't disturb a customer on upload/confirm/success.
  useEffect(() => {
    const onWake = () => {
      if (document.hidden) return;
      if (step !== 'mode' && step !== 'magic') return;
      void tryConsumePending({ pollWhileGenerating: false });
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [step, tryConsumePending]);

  // ── Fix D: self-heal a frozen 'magic' loader ──────────────────────────────
  //     Last line of defence for the exact #D237 symptom: the layout for the
  //     CURRENT photo set is already finalized (finalizedKeyRef/magicSettledKey
  //     set + saved pages) yet `step` is still 'magic' (a goToStep('preview')
  //     was dropped or a stray re-assert pinned the loader). If a finalize has
  //     happened for this set, leave 'magic' for 'preview' immediately. Purely
  //     idempotent — it only fires while genuinely stuck on the loader.
  useEffect(() => {
    if (step !== 'magic') return;
    if (photos.length < MIN_PHOTOS) return;
    const expectedKey = photosKey(photos.map((p) => p.id));
    if (finalizedKeyRef.current === expectedKey || magicSettledKeyRef.current === expectedKey) {
      try { aiAbortRef.current?.abort(); } catch { /* ignore */ }
      aiAbortRef.current = null;
      goToStep('preview');
    }
  }, [step, photos, layout, goToStep]);

  // ── Computed (before callbacks that need them) ──
  const totalPhotos = photos.length;
  const layoutPages = layout?.pages ?? [];
  // Billed against the allowance this ORDER was sold under, not today's default
  // — an order created before 2026-08-12 keeps its 50 free pages (migration 082).
  const extraPages = liveExtraPagesForSingleAlbum(layoutPages.length, order?.base_pages_per_album);
  const extraCost = extraPages * pricePerPage;

  // ── Render + upload PDFs for an arbitrary album (1..totalAlbums) ──
  // Thin wrapper around the shared multiAlbumPdf helper, so both this page
  // and the editor's Confirmer hit the same DB-loading + render + upload +
  // finalize pipeline. The closure-state path above only handles the album
  // currently in memory.
  // Client-side album PDF render REMOVED — the print PDF now renders server-side
  // in the background (the worker's /submit fires runServerRender via a headless
  // robot browser). The customer no longer waits ~30s at confirm. The pdfProgress
  // modal below is retained but no longer triggered on this path.

  // ── Submit album ──
  // Returns the freshly-submitted order row (with recomputed total_price +
  // persisted extra_pages) when this is the final album; the post-submit
  // redirect uses that to hydrate the Shopify checkout page with current
  // values instead of the stale pre-submit useQuery snapshot. Returns null
  // if there are still more albums to walk through.
  // opts.skipSuccessStep (2026-07-08): the in-app checkout step calls this and
  // then redirects straight to the storefront thank-you — the legacy in-app
  // 'success' countdown screen must not flash in between.
  const handleSubmit = useCallback(async (opts?: { skipSuccessStep?: boolean }): Promise<Order | null> => {
    if (!shopifyOrderId || !session) return null;
    // Full-res originals MUST all be uploaded before submit — the print PDF
    // (client- or worker-rendered) embeds them, never the editor thumbnails.
    // Normally already finished while choosing the layout / reviewing; if not,
    // this shows "Finalisation des photos X/N" and retries. Block submit if any
    // truly couldn't upload, so a compressed image never reaches print.
    const originalsOk = await finalizeOriginals();
    if (!originalsOk) {
      alert(describeMissingOriginals(missingOriginalsRef.current));
      return null;
    }
    try {
      // Persist the full album state to the session so the admin can open
      // the order in the editor and see exactly what the customer shipped.
      // Path 1 used to only flip status='submitted' which left pages/cover
      // unsaved — the admin would open the order to a blank editor.
      const existingCust = (session.cover_customizations as Record<string, unknown> | null) ?? {};
      await sessionsApi.upsert(shopifyOrderId, currentAlbum, {
        status: 'submitted' as any,
        cover_id: selectedCover?.id === '__blank__' ? null : (selectedCover?.id ?? null),
        cover_destination_text: titlePrefill(session, destination) || null,
        cover_year_text:        (session.cover_year_text as string | null)        ?? year ?? null,
        cover_customizations: {
          ...existingCust,
          validated_at: new Date().toISOString(),
        } as any,
        pages: layout ? { pages: layout.pages, unassigned_photos: layout.unassigned_photos } as any : undefined,
      });

      // If more albums to go, advance. This album's originals are confirmed
      // uploaded (finalizeOriginals passed above), so clear the tracker — the
      // next album starts its "Finalisation X/N" count from zero, and
      // stopAnalysisRef resets so the next album's faces get analysed.
      if (currentAlbum < totalAlbums) {
        originalsRef.current.clear();
        stopAnalysisRef.current = false;
        setCurrentAlbum(prev => prev + 1);
        setStep('design');
        setSelectedCover(null);
        setSession(null);
        setPhotos([]);
        setLayout(null);
        return null;
      }
      // All albums done — submit via customer endpoint with pricing data
      let submitted: Order | null = null;
      try {
        const res = await ordersApi.submit(shopifyOrderId, {
          page_count: layoutPages.length,
          extra_pages: extraPages,
          extra_pages_cost: extraCost,
        });
        submitted = res?.order ?? null;
      } catch (err) {
        // SERVER-SIDE ORIGINALS GATE (2026-07-06, policy reworked 2026-07-13):
        // the worker now refuses ONLY when a photo PLACED on a page has no
        // printable copy at all (degraded thumbnail-only photos pass and print
        // at reduced quality). Do NOT show the success screen — surface the
        // message and REDIRECT to the album that needs fixing so the customer
        // lands on the exact place to replace/remove the broken photo.
        const e = err as { status?: number; body?: { message?: string; missing_count?: number; missing?: Array<{ album_index?: number }> } };
        if (e?.status === 422 && e.body?.message) {
          alert(e.body.message);
          const fixAlbum = e.body.missing?.find((m) => Number.isFinite(m?.album_index))?.album_index;
          if (fixAlbum && shopifyOrderId) {
            navigate(`/editor/${shopifyOrderId}/${fixAlbum}/edit`);
          }
          return null;
        }
        console.error('Submit to Shopify failed:', err);
      }
      if (!opts?.skipSuccessStep) goToStep('success');
      return submitted;
    } catch (err) {
      console.error('Submit error:', err);
      return null;
    }
  }, [shopifyOrderId, session, currentAlbum, totalAlbums, layoutPages.length, extraPages, extraCost, selectedCover, destination, year, layout, finalizeOriginals]);

  // ── In-app checkout (2026-07-08) ──
  // Background print-upload progress for the chip + the checkout step's
  // reassurance bar. Cheap poll of the in-memory tracker (display only, byte-
  // weighted); runs only on the steps that show it.
  const [bgUpload, setBgUpload] = useState({ doneCount: 0, totalCount: 0, doneBytes: 0, totalBytes: 0 });
  useEffect(() => {
    if (step !== 'preview' && step !== 'confirm' && step !== 'checkout') return;
    const read = () => {
      let doneCount = 0, totalCount = 0, doneBytes = 0, totalBytes = 0;
      originalsRef.current.forEach((e) => {
        totalCount += 1;
        totalBytes += e.bytes;
        if (e.done) { doneCount += 1; doneBytes += e.bytes; }
      });
      setBgUpload((prev) =>
        prev.doneCount === doneCount && prev.totalCount === totalCount && prev.doneBytes === doneBytes && prev.totalBytes === totalBytes
          ? prev
          : { doneCount, totalCount, doneBytes, totalBytes });
    };
    read();
    const id = setInterval(read, 1500);
    return () => clearInterval(id);
  }, [step]);

  // ── KEEP-ALIVE while print originals are still uploading (2026-07-21) ──
  // Root cause A of the missing-originals saga: the customer locks the phone /
  // leaves and the browser suspends the tab, killing the background 3600px
  // uploads (the IDB resume net only helps if they come BACK). Two mitigations,
  // both transport-neutral:
  //  (a) Screen Wake Lock (iOS 16.4+ / Chrome) — keeps the screen awake while
  //      uploads drain so the tab is never suspended mid-transfer. The OS
  //      auto-releases it on backgrounding; we re-acquire on return. Best-effort:
  //      unsupported/denied → behaviour identical to today.
  //  (b) beforeunload guard — refreshing/closing with uploads pending shows the
  //      native "quit?" prompt. Warning first loses fewer originals than
  //      recovering after.
  useEffect(() => {
    type Sentinel = { release(): Promise<void>; addEventListener?: (t: string, fn: () => void) => void };
    let lock: Sentinel | null = null;
    let disposed = false;
    const pendingCount = () => {
      let n = 0;
      originalsRef.current.forEach((e) => { if (!e.done) n += 1; });
      return n;
    };
    const syncLock = async () => {
      const want = pendingCount() > 0 && document.visibilityState === 'visible';
      const wl = (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<Sentinel> } }).wakeLock;
      if (want && !lock && wl) {
        try {
          const l = await wl.request('screen');
          if (disposed || pendingCount() === 0) { void l.release().catch(() => {}); return; }
          lock = l;
          l.addEventListener?.('release', () => { if (lock === l) lock = null; });
        } catch { /* low battery / unsupported — resume net still covers */ }
      } else if (!want && lock) {
        void lock.release().catch(() => {});
        lock = null;
      }
    };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (pendingCount() === 0) return;
      e.preventDefault();
      e.returnValue = ''; // Chrome requires returnValue to show the prompt
    };
    const onVisibility = () => { void syncLock(); };
    const id = setInterval(onVisibility, 3000);
    void syncLock();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      disposed = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (lock) { void lock.release().catch(() => {}); lock = null; }
    };
  }, []);

  // Final "Confirmer ma commande" click on the in-app checkout step. Order of
  // operations preserved from the live system (gate → /submit → draft
  // completion → thank-you); only the CUSTOMER's wait moved — the checkout
  // dwell time now overlaps the background uploads instead of following them.
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const handleCheckoutConfirm = useCallback(async (method: 'cod' | 'virement') => {
    if (checkoutSubmitting || !shopifyOrderId) return;
    setCheckoutSubmitting(true);
    setCheckoutError(null);
    try {
      // PRINT-INTEGRITY GATE — unchanged. Usually a no-op by now (the whole
      // checkout dwell time just drained the background queue); shows the
      // "Préparation" overlay only for a residue, and blocks on true failure.
      const originalsOk = await finalizeOriginals();
      if (!originalsOk) {
        setCheckoutError(describeMissingOriginals(missingOriginalsRef.current));
        return;
      }
      // /submit — server re-verifies every original (422 alerts + returns
      // null), persists pages/pricing, fires the SERVER-side PDF render.
      const submitted = await handleSubmit({ skipSuccessStep: true });
      if (!submitted) {
        setCheckoutError('La confirmation n’a pas abouti. Vérifiez votre connexion puis réessayez.');
        return;
      }
      // Draft → real order + payment method, server-side (best-effort like
      // the storefront page: a failure here never strands the customer —
      // admin can complete the draft manually from Shopify).
      let finalOrderId: string | null = null;
      try {
        const res = await ordersApi.checkoutConfirm(shopifyOrderId, method);
        finalOrderId = res?.final_order_id ?? null;
      } catch (err) {
        console.warn('[checkout] checkout-confirm failed (continuing to thank-you):', err);
      }
      try { sessionStorage.removeItem('storiz_post_submit_next'); } catch { /* ignore */ }
      window.location.href = getThankYouRedirect(submitted, finalOrderId, method);
    } finally {
      setCheckoutSubmitting(false);
    }
  }, [checkoutSubmitting, shopifyOrderId, finalizeOriginals, handleSubmit]);

  const doneUploads = uploadFiles.filter(f => f.status === 'done').length;
  const uploadProgress = uploadFiles.length > 0 ? Math.round((doneUploads / uploadFiles.length) * 100) : 0;

  // Meta match quality (2026-07-16): once the order is known, hand the
  // customer's phone/name to the pixel (manual advanced matching — hashed by
  // the pixel itself). Best-effort; runs once per order id.
  useEffect(() => {
    if (order?.customer_phone || order?.customer_name) {
      setAdvancedMatching({ phone: order.customer_phone, name: order.customer_name, email: order.customer_email });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // ── Loading ──
  if (orderLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.beige }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-3 border-t-transparent animate-spin" style={{ borderColor: C.gold, borderTopColor: 'transparent' }} />
          <span className="text-sm tracking-wide" style={{ color: C.muted, fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" }}>Chargement...</span>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.beige }}>
        <div className="text-center px-6">
          <div className="text-5xl mb-4">📦</div>
          <h2 className="text-lg font-semibold mb-2" style={{ color: C.dark, fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" }}>Commande introuvable</h2>
          <p className="text-sm mb-4" style={{ color: C.muted }}>Vérifiez le lien ou retournez au tableau de bord.</p>
          <button
            onClick={() => { window.location.href = 'https://storiz.ma'; }}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{ background: C.gold, color: C.white }}
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  const LOCKED_STATUSES = ['in-production', 'printed', 'ready-to-deliver', 'shipped', 'delivered'];
  const isLocked = LOCKED_STATUSES.includes(order.status);

  // ── Design-step "continue" — ONE handler for BOTH buttons (2026-07-24) ─────
  // The bottom "Continuer" and the header "Suivant" must do the same thing on
  // the design step. Before this, the header Suivant always stepped to
  // 'upload' (old-funnel import-photos), so in funnel v2 it BYPASSED the
  // album loop + checkout redirect (Yahya's report). FUNNEL V2: loop Duo/Trio
  // covers (Album 1 → 2 → [3]) then redirect to the storefront checkout with
  // every album's cover. V1: classic → upload.
  const handleDesignContinue = () => {
    if (!selectedCover) return;
    if (funnelV2) {
      funnelCoversRef.current[currentAlbum - 1] = selectedCover?.name ?? '';
      if (currentAlbum < totalAlbums) {
        setCurrentAlbum((prev) => prev + 1);
        setSelectedCover(null);
        setSession(null); // next album's session (re)loads via the currentAlbum effect
        window.scrollTo({ top: 0 });
        return;
      }
      const chosen = funnelCoversRef.current;
      window.location.href = getCheckoutRedirect(nextUrl, {
        shopify_order_id: shopifyOrderId,
        customer_name:    order?.customer_name,
        customer_phone:   order?.customer_phone,
        bundle:           order?.bundle,
        page_count:       order?.page_count,
        destination_1:    order?.destination_1 ?? destination,
        total_price:      order?.total_price,
        cover:            chosen[0] || (selectedCover?.name ?? null),
        cover_2:          chosen[1] || null,
        cover_3:          chosen[2] || null,
      });
      return;
    }
    goToStep('upload');
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ minHeight: '100dvh', background: C.beige }}>
      {/* ── PDF generation overlay (Path 1 Confirmer) ─────────────────
            Shown while cover.pdf + interior.pdf render + upload. Mirrors
            the editor's submit modal so the experience is identical
            regardless of which Confirm path the customer used. */}
      {/* Finishing the full-res originals before the PDF (shown only if the
          customer reaches submit before the background uploads finished). */}
      {finalizingOriginals && (
        <FinalizingOverlay done={finalizingOriginals.done} total={finalizingOriginals.total} />
      )}
      {/* Non-blocking print-upload chip (Option C) — preview only; the
          checkout step has its own inline reassurance bar, and the blocking
          overlay supersedes it when visible. Sits ABOVE the Aide pill (both
          live bottom-left; the chip is transient, the pill permanent). */}
      {step === 'preview' && !finalizingOriginals && (
        <BgUploadChip doneBytes={bgUpload.doneBytes} totalBytes={bgUpload.totalBytes} doneCount={bgUpload.doneCount} totalCount={bgUpload.totalCount} />
      )}
      {/* Unified "Aide" widget (2026-07-08) — bottom-left, z-40, under every
          sheet/overlay. The design step's full-width bottom CTA needs the
          pill lifted above it; success step redirects, no widget needed. */}
      {step !== 'success' && (
        <HelpFAB bottomOffset={step === 'design' ? 118 : 16} />
      )}
      {pdfProgress && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="mx-auto w-16 h-16 mb-6 relative">
              <div className="absolute inset-0 rounded-full border-4" style={{ borderColor: `${C.gold}20` }} />
              <div className="absolute inset-0 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: C.gold, borderTopColor: 'transparent' }} />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: C.dark, fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" }}>
              Génération de votre album
            </h3>
            <p className="text-sm mb-4" style={{ color: C.muted }}>{pdfProgress.message}</p>
            {pdfProgress.total > 1 && (
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: `${C.gold}15` }}>
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${Math.round((pdfProgress.current / pdfProgress.total) * 100)}%`,
                    background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`,
                  }}
                />
              </div>
            )}
            <p className="text-xs mt-4" style={{ color: C.muted }}>
              Cela peut prendre jusqu'à 30 secondes...
            </p>
          </div>
        </div>
      )}

      {/* ── Top Bar ── */}
      <header className="shrink-0 sticky top-0 z-50" style={{ background: C.white, borderBottom: `1px solid ${C.gold}20` }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo + order name */}
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Londrina Solid', sans-serif", color: C.dark }}>
              STORIZ
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${C.gold}15`, color: C.goldDark }}>
              {order.shopify_order_name}
            </span>
          </div>

          {/* Multi-album stepper */}
          {totalAlbums > 1 && (
            <div className="flex items-center gap-2">
              {Array.from({ length: totalAlbums }).map((_, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                    style={{
                      background: i + 1 < currentAlbum ? C.success : i + 1 === currentAlbum ? C.gold : `${C.gold}20`,
                      color: i + 1 <= currentAlbum ? C.white : C.muted,
                    }}
                  >
                    {i + 1 < currentAlbum ? '✓' : i + 1}
                  </div>
                  {i < totalAlbums - 1 && (
                    <div className="w-6 h-0.5 rounded-full" style={{ background: i + 1 < currentAlbum ? C.success : `${C.gold}20` }} />
                  )}
                </div>
              ))}
              <span className="text-xs font-medium ml-2" style={{ color: C.muted }}>
                Album {currentAlbum} / {totalAlbums}
              </span>
            </div>
          )}

          {/* Back / Suivant — paired controls so a customer who hit Retour
              by mistake can step forward without re-running AI Magic or
              re-uploading photos. Forward is only enabled when the next
              step's required state is already saved (cover picked /
              photos uploaded / layout generated), so we don't drop them
              into an empty preview. State persists in React + Supabase
              between hops; nothing is wiped on either direction. */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                // State-driven back: step one FlowStep back (skipping the
                // transient 'magic'); only the first step exits the builder.
                // The history sentinel trap handles hardware/edge-swipe back
                // independently, so we don't touch history here.
                const idx = STEP_ORDER.indexOf(step);
                if (idx > 0) {
                  let prevIdx = idx - 1;
                  // Skip 'magic' (transient) + 'confirm' (legacy, superseded
                  // by the in-app 'checkout' step) — back from checkout must
                  // land on the preview.
                  while (prevIdx > 0 && (STEP_ORDER[prevIdx] === 'magic' || STEP_ORDER[prevIdx] === 'confirm')) prevIdx -= 1;
                  setStep(STEP_ORDER[prevIdx]);
                } else {
                  window.location.href = 'https://storiz.ma';
                }
              }}
              // NAV visibility (2026-07-04): was a bare text-xs muted label —
              // customers missed it. Now a real ghost button: chevron icon,
              // visible border, 44px-min tap target. Behaviour unchanged.
              className="inline-flex items-center gap-1 text-[13px] font-semibold px-3 rounded-full transition-all hover:bg-gray-50 active:scale-95"
              style={{ color: C.dark, border: `1.5px solid ${C.gold}45`, background: C.white, minHeight: 40 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M15 18l-6-6 6-6" />
              </svg>
              Retour
            </button>
            {(() => {
              const idx = STEP_ORDER.indexOf(step);
              const nextStep = STEP_ORDER[idx + 1];
              // 'magic' is a transient loading step — skip it when stepping
              // forward; 'success' is the post-submit screen and has no
              // step-forward semantics.
              if (!nextStep || nextStep === 'magic' || step === 'preview' || step === 'success' || step === 'confirm') {
                return null;
              }
              const canMoveForward =
                (step === 'design' && !!selectedCover) ||
                // Lock forward nav while a batch uploads — the optimistic adds
                // grow photos.length mid-batch, so without !isUploading this
                // header "Suivant →" could let an accidental tap skip into AI/
                // preview before the thumbnails finish. Re-enables at batch end.
                (step === 'upload' && photos.length >= MIN_PHOTOS && !isUploading) ||
                (step === 'mode'   && !!layout);
              if (!canMoveForward) return null;
              return (
                <button
                  onClick={() => {
                    // DESIGN step: same behaviour as the bottom "Continuer" —
                    // in funnel v2 that means album loop → checkout, NOT the
                    // old-funnel photo-import step (2026-07-24 fix).
                    if (step === 'design') {
                      handleDesignContinue();
                      return;
                    }
                    // If layout already exists and user came back from
                    // preview, skip the 'magic' loading state and jump
                    // straight to preview. handleAiMagic only runs when
                    // there's no layout yet.
                    if (step === 'mode' && layout) {
                      goToStep('preview');
                      return;
                    }
                    goToStep(nextStep);
                  }}
                  // NAV visibility (2026-07-04): promoted from a text link to
                  // the primary-action pill (same family as the main CTAs) so
                  // the way forward is unmissable. Behaviour unchanged.
                  className="inline-flex items-center gap-1 text-[13px] font-bold px-4 rounded-full transition-all active:scale-95"
                  style={{
                    background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
                    color: C.white,
                    minHeight: 40,
                    boxShadow: `0 2px 10px ${C.gold}40`,
                  }}
                >
                  Suivant
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              );
            })()}
          </div>
        </div>

        {/* Step progress bar */}
        {step !== 'success' && (
          <div className="h-1 relative" style={{ background: `${C.gold}10` }}>
            <div
              className="absolute inset-y-0 left-0 transition-all duration-500"
              style={{
                background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`,
                width: `${Math.max(5, (STEP_ORDER.indexOf(step) / (STEP_ORDER.length - 1)) * 100)}%`,
              }}
            />
          </div>
        )}
      </header>

      {/* ── Lock Banner ── */}
      {isLocked && (
        <div
          className="shrink-0 px-4 py-3 text-center"
          style={{ background: '#FEF3C7', borderBottom: '1px solid #FDE68A' }}
        >
          <p className="text-sm font-medium" style={{ color: '#92400E' }}>
            Votre album est en cours de production — aucune modification n'est possible.
          </p>
        </div>
      )}

      {/* ── Content ── */}
      {/* Native document scroll. An earlier version made this an INNER scroll
          container (overflow-y-auto + overscroll-contain) inside an
          overflow-hidden / 100dvh root to stop the iOS URL-bar collapsing
          mid-scroll. That traded a cosmetic address-bar shift for a far worse
          bug: on iOS the inner container "catches" at its top/bottom
          boundaries (the first swipe at a boundary is eaten as a rubber-band
          gesture the overflow-hidden ancestor can't satisfy), so the grid felt
          stuck until you swiped twice. Letting the body scroll natively — with
          a sticky header and a fixed CTA — has none of that boundary jank. */}
      <main className="flex-1">
        {step === 'design' && (
          <StepDesign
            covers={covers}
            categories={categories}
            selectedCover={selectedCover}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            onSelectCover={handleSelectCover}
            destination={destination}
            year={year}
            shopifyOrderId={shopifyOrderId}
            albumIndex={currentAlbum}
            totalAlbums={totalAlbums}
            customerName={order?.customer_name ?? null}
            customerPhone={order?.customer_phone ?? null}
            savedCoverId={session?.cover_id ?? null}
            autoSelectReady={sessionResolved}
          />
        )}

        {step === 'upload' && (
          <StepUpload
            photos={photos}
            uploadFiles={uploadFiles}
            uploadProgress={uploadProgress}
            isUploading={isUploading}
            fileInputRef={fileInputRef}
            onUpload={handleUpload}
            onRetry={retryFailedUploads}
            notice={addNotice}
            onDeletePhoto={handleDeletePhoto}
            onDeletePhotos={handleDeletePhotos}
            totalPhotos={totalPhotos}
            onNext={() => goToStep('mode')}
            localPreviews={localPreviews}
          />
        )}

        {step === 'mode' && (
          <StepMode
            totalPhotos={totalPhotos}
            onAiMagic={handleAiMagic}
            onManual={() => navigate(`/editor/${shopifyOrderId}/${currentAlbum}/edit`)}
          />
        )}

        {step === 'magic' && <StepMagic />}

        {step === 'preview' && (
          <StepPreview
            layout={layout}
            photos={photos}
            selectedCover={selectedCover}
            session={session}
            order={order}
            extraPages={extraPages}
            pricePerPage={pricePerPage}
            destination={destination}
            year={year}
            logoUrl={settingsData?.settings?.logo_url ?? null}
            editTo={`/editor/${shopifyOrderId}/${currentAlbum}/edit`}
            onEdit={() => navigate(`/editor/${shopifyOrderId}/${currentAlbum}/edit`)}
            onAddPages={async () => {
              if (!layout) return;
              // Append two blank 1-up pages at the end of the layout, persist
              // to the session so the editor sees them on load, then deep-link
              // into the editor with ?addedPages=2 so it scrolls to the new
              // spread and flashes the "Tap to add photos" hint.
              const last = layout.pages[layout.pages.length - 1];
              const startPage = (last?.page ?? 0) + 1;
              const newPages = [
                { page: startPage,     layout: '1-up' as const, photos: [] as string[] },
                { page: startPage + 1, layout: '1-up' as const, photos: [] as string[] },
              ];
              const nextLayout = { ...layout, pages: [...layout.pages, ...newPages] };
              setLayout(nextLayout);
              try {
                if (shopifyOrderId && session) {
                  await sessionsApi.upsert(shopifyOrderId, currentAlbum, {
                    pages: { pages: nextLayout.pages, unassigned_photos: nextLayout.unassigned_photos } as any,
                  });
                }
              } catch (e) {
                console.warn('[onAddPages] persist failed:', e);
              }
              // Land the customer on the editor at the freshly-added spread.
              // startPage is 1-indexed; addedPages tells the editor how many
              // pages to flash a hint on.
              navigate(`/editor/${shopifyOrderId}/${currentAlbum}/edit?focusPage=${startPage}&addedPages=2`);
            }}
            onConfirm={() => {
              // Multi-album aware Confirmer.
              //   - For albums 1..N-1: just save the album session and
              //     advance state to the next album's design step. No PDFs,
              //     no Shopify submit, no checkout redirect.
              //   - For the LAST album: save current session, then render
              //     every album's PDFs (1..N) by loading their state from
              //     DB, then submit the order, then redirect to checkout.
              // PDF failures are non-blocking: the customer still proceeds
              // to checkout; the admin can regenerate any missing PDFs.
              (async () => {
                // Always save the current album's session (covers + pages)
                // before advancing or rendering, so the PDF loop below can
                // read it back from DB.
                if (shopifyOrderId && session && layout) {
                  const existingCust = (session.cover_customizations as Record<string, unknown> | null) ?? {};
                  try {
                    await sessionsApi.upsert(shopifyOrderId, currentAlbum, {
                      cover_id: selectedCover?.id === '__blank__' ? null : (selectedCover?.id ?? null),
                      cover_destination_text: titlePrefill(session, destination) || null,
                      cover_year_text:        (session.cover_year_text as string | null)        ?? year ?? null,
                      cover_customizations: {
                        ...existingCust,
                        validated_at: new Date().toISOString(),
                      } as any,
                      pages: { pages: layout.pages, unassigned_photos: layout.unassigned_photos } as any,
                    });
                  } catch (e) {
                    console.error('[Confirmer] session save failed:', e);
                  }
                }

                // Not the last album → advance and stop. The next album's
                // design step opens with a fresh photo upload + cover pick.
                if (currentAlbum < totalAlbums) {
                  setCurrentAlbum((prev) => prev + 1);
                  setStep('design');
                  setSelectedCover(null);
                  setSession(null);
                  setPhotos([]);
                  setLayout(null);
                  return;
                }

                // Last album → IN-APP CHECKOUT STEP (2026-07-08). No blocking
                // originals gate here anymore: the customer reviews their
                // order + picks a payment mode while the background print
                // uploads keep draining underneath (that dwell time used to
                // be wasted on the storefront checkout page AFTER the wait).
                // The gate + /submit + draft-completion all run at the final
                // "Confirmer ma commande" click — see handleCheckoutConfirm.
                setPdfProgress(null);
                goToStep('checkout');
              })();
            }}
          />
        )}

        {step === 'confirm' && (
          <StepConfirm
            order={order}
            totalPhotos={totalPhotos}
            totalPages={layoutPages.length}
            extraPages={extraPages}
            extraCost={extraCost}
            pricePerPage={pricePerPage}
            selectedCover={selectedCover}
            destination={destination}
            currentAlbum={currentAlbum}
            totalAlbums={totalAlbums}
            onSubmit={handleSubmit}
            onBack={() => window.history.back()}
            isLocked={isLocked}
          />
        )}

        {step === 'checkout' && (
          <StepCheckout
            order={order}
            totalPages={layoutPages.length}
            extraPages={extraPages}
            pricePerPage={pricePerPage}
            destination={destination}
            upload={bgUpload}
            submitting={checkoutSubmitting}
            error={checkoutError}
            onConfirm={handleCheckoutConfirm}
            onBack={() => goToStep('preview')}
            isLocked={isLocked}
          />
        )}

        {step === 'success' && (
          <StepSuccess
            order={order}
            nextUrl={nextUrl}
          />
        )}
      </main>

      {/* ── Fixed bottom CTA for design picker step ── */}
      {/* pointer-events-none on the gradient wrapper so a swipe that STARTS
          over this overlay (the bottom ~110px of the screen) passes straight
          through to the page and scrolls it — otherwise the overlay eats the
          touch and the grid feels "stuck at the bottom".
          BUT: the inner button container is pointer-events-auto AND carries a
          tall top padding (pt-10) so the transparent zone DIRECTLY above the
          button absorbs taps instead of passing them through to the cover tile
          behind it — fixes "tapping Continuer selected the cover behind it".
          Only the ~40px directly above the button stops swipe-through; the rest
          of the overlay still scrolls. */}
      {step === 'design' && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-6 pointer-events-none"
          style={{ background: `linear-gradient(to top, ${C.beige} 70%, transparent)` }}
        >
          <div className="max-w-5xl mx-auto pointer-events-auto pt-10">
            <button
              disabled={!selectedCover}
              onClick={handleDesignContinue}
              className="w-full py-4 rounded-2xl text-base font-bold tracking-wide transition-all disabled:opacity-40"
              style={{
                background: selectedCover ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : `${C.gold}40`,
                color: C.white,
                boxShadow: selectedCover ? `0 4px 20px ${C.gold}40` : 'none',
              }}
            >
              {selectedCover
                ? (funnelV2 && currentAlbum < totalAlbums ? `Album ${currentAlbum + 1} : choisir la couverture →` : 'Continuer →')
                : 'Choisissez un design'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── Step 0: Design Picker ───────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

// Lightweight static cover image for the design-picker grid. Replaces the
// live <CoverThumbnail> (which mounts a ResizeObserver + per-zone TextOverlay
// + animated box-shadow per card). Multiplied across the dozens-to-hundreds
// of covers the picker renders at once, that live paint/layout work competed
// with the scroll thread and made the grid stutter ("it starts stopping").
// A single lazy <img> has near-zero scroll cost. The interactive CoverThumbnail
// is still used in the editor, where live text/colour editing is required.
const PickerCoverImage = memo(function PickerCoverImage({
  cover,
  destination,
  year,
}: {
  cover: Cover;
  destination: string;
  year: string;
}) {
  const zones = cover.text_zones ?? [];
  return (
    <div
      className="relative w-full aspect-[3/4] overflow-hidden"
      // `containerType: inline-size` makes this card a CSS query container so
      // the text overlay below can size itself in cqw units (1cqw = 1% of the
      // card width) — it scales with the card WITHOUT any JS. It also adds
      // inline-size containment, isolating each card's layout from the grid.
      style={{ background: cover.dominant_color ?? C.beigeLight, containerType: 'inline-size' }}
    >
      <img
        src={pickerThumbUrl(cover.thumbnail_url ?? cover.design_url) ?? ''}
        alt={cover.name}
        className={`absolute inset-0 w-full h-full ${cover.thumbnail_url ? 'object-cover' : 'object-contain'}`}
        loading="lazy"
        decoding="async"
        draggable={false}
      />
      {/* Static destination/year overlay. Pure CSS — no ResizeObserver, no
          text-measuring reflow, no drag handlers. Font-size in cqw mirrors
          the editor's `font_size / 1860` law so this preview matches the
          eventual cover. */}
      {pickerZoneStyles(zones, destination, year).map((s, i) => (
          <div
            key={i}
            className="absolute flex items-center pointer-events-none select-none"
            style={{
              left: `${s.leftPct}%`,
              top: `${s.topPct}%`,
              width: `${s.widthPct}%`,
              height: `${s.heightPct}%`,
              justifyContent: s.justify,
              color: s.color,
              fontFamily: s.fontFamily,
              fontSize: `${s.fontSizeCqw}cqw`,
              fontWeight: 400,
              lineHeight: 1.1,
              letterSpacing: '0.01em',
              overflow: 'hidden',
              // Title paints IMMEDIATELY (fallback font first, real webfont
              // swaps in via display:swap). Never gate visibility on font load —
              // a slow/failed font must never leave a card title blank (the
              // "some covers show no title on load" bug). Preloading the fonts
              // (effect below) keeps the swap tiny.
              opacity: 1,
            }}
          >
            <span style={{ whiteSpace: 'pre-wrap', textAlign: s.textAlign, width: '100%' }}>
              {s.text}
            </span>
          </div>
      ))}
    </div>
  );
});

// ── Auto-match the customer's typed destination/event to a cover design ──
// The tolerant normalizer + scorer now live in @/lib/coverIcons (shared with
// the editor's icon picker) — imported at the top of this file.

// Title-case the destination for the badge (echoes what the customer typed).
const titleCaseDest = (s: string) =>
  s.trim().replace(/\s+/g, ' ').split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export function StepDesign({
  covers,
  categories,
  selectedCover,
  selectedCategory,
  onCategoryChange,
  onSelectCover,
  destination,
  year,
  shopifyOrderId,
  albumIndex,
  totalAlbums,
  customerName,
  customerPhone,
  savedCoverId = null,
  autoSelectReady = false,
}: {
  covers: Cover[];
  categories: CoverCategory[];
  selectedCover: Cover | null;
  selectedCategory: string | null;
  onCategoryChange: (c: string | null) => void;
  onSelectCover: (c: Cover) => void;
  destination: string;
  year: string;
  shopifyOrderId?: string;
  albumIndex?: number;
  /** FUNNEL V2 × MULTI-ALBUM: total albums in the bundle — shows the
   *  "Album N sur M" badge when > 1. Omitted by Solo / the V2 picker. */
  totalAlbums?: number;
  customerName?: string | null;
  customerPhone?: string | null;
  /** This album's saved cover_id (session), or null. A saved choice always
   *  beats the destination auto-match. */
  savedCoverId?: string | null;
  /** True once the saved session state is KNOWN — the auto-select waits for
   *  it so it can never race (and overwrite) a saved cover. */
  autoSelectReady?: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState('');

  // ── Preload the cover-title webfonts (do NOT gate visibility on them) ──
  // Browsers download a webfont only on first use — which is this grid. We
  // preload the exact families the zones use so the overlay title (rendered
  // immediately with a fallback font) swaps to the real font ASAP. Visibility
  // is NEVER gated on this: the title paints from first paint and `display:
  // swap` handles the swap, so a slow/failed font can never leave a card title
  // blank (the "some covers show no title on load" bug). Fire-and-forget.
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts?.load) return;
    const specs = pickerFontLoadSpecs(covers.flatMap((c) => c.text_zones ?? []));
    specs.forEach((s) => { void document.fonts.load(s).catch(() => {}); });
  }, [covers]);

  const filtered = useMemo(() => {
    let list = covers.filter(c => c.is_visible);
    if (selectedCategory) list = list.filter(c => c.categories.includes(selectedCategory));
    const q = searchQuery.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    if (q) {
      list = list.filter(c => {
        const haystack = [c.name, ...c.categories, c.description, ...(c.search_tags ?? [])]
          .filter(Boolean).join(' ')
          .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
        return haystack.includes(q);
      });
    }
    // Preserve the incoming order: `covers` already arrives popularity-ranked
    // (usage_count desc) from the worker, with any per-order pinned cover moved
    // to the front (Task 4 A+E). Re-sorting by sort_order here would undo both.
    return list;
  }, [covers, selectedCategory, searchQuery]);

  // ── Auto-surface the cover matching the customer's typed destination ──
  // DEFAULT VIEW ONLY (no active search / category). A strong local match floats
  // to the front + gets the ✦ badge so the customer doesn't search again. With
  // NO local match, we float the existing Gemini "closest" covers first so they
  // see relevant alternatives instead of being stranded. Search / category
  // browsing is left exactly as-is. Per-album: `destination` is this album's.
  const destNorm = normDest(destination);
  const isDefaultView = !searchQuery.trim() && !selectedCategory;
  const [destSuggestions, setDestSuggestions] = useState<Cover[]>([]);
  useEffect(() => {
    if (!isDefaultView || destNorm.length < 2) { setDestSuggestions([]); return; }
    const hasLocal = covers.some((c) => c.is_visible && destinationMatchScore(c, destination) >= 70);
    if (hasLocal) { setDestSuggestions([]); return; }
    let cancelled = false;
    coversApi.suggestSimilar(destination)
      .then((res) => { if (!cancelled) setDestSuggestions(res.covers ?? []); })
      .catch(() => { if (!cancelled) setDestSuggestions([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destNorm, isDefaultView, covers.length]);

  const { displayed, matchedId } = useMemo<{ displayed: Cover[]; matchedId: string | null }>(() => {
    if (!isDefaultView || destNorm.length < 2) return { displayed: filtered, matchedId: null };
    const scored = filtered.map((c, i) => ({ c, i, score: destinationMatchScore(c, destination) }));
    if (scored.some((s) => s.score > 0)) {
      scored.sort((a, b) => b.score - a.score || a.i - b.i); // score desc, popularity tiebreak
      const best = scored[0]!;
      return { displayed: scored.map((s) => s.c), matchedId: best.score >= 70 ? best.c.id : null };
    }
    if (destSuggestions.length) {
      // TITLE FIX (2026-07-24): /covers/suggest-similar returns SLIM rows
      // (no text_zones, no design_url) — rendering them directly left the
      // suggested tiles without their destination/year title ("some covers
      // don't show the text, sometimes"). Map each suggestion back to the
      // FULL local cover by id so the overlay always has its zones; keep the
      // slim row only if the id isn't in the local list (shouldn't happen —
      // same table, same visibility filter).
      const sug = destSuggestions
        .map((s) => covers.find((c) => c.id === s.id) ?? s)
        .filter((c) => c.is_visible !== false);
      const sugIds = new Set(sug.map((c) => c.id));
      return { displayed: [...sug, ...filtered.filter((c) => !sugIds.has(c.id))], matchedId: null };
    }
    return { displayed: filtered, matchedId: null };
  }, [filtered, isDefaultView, destNorm, destSuggestions, destination, covers]);

  // ── C1/C3: AUTO-SELECT the matched cover ──────────────────────────────────
  // The ✦ badge used to be decoration only — the customer still had to tap the
  // matched cover to enable Continue. Now a strong match (matchedId) is
  // actually SELECTED through the same path a tap uses (onSelectCover →
  // handleSelectCover), which also persists cover_id so Duo/Trio album 2+ and
  // resumed sessions keep it. Guards, in order: wait until the saved session
  // state is known; a saved cover always wins; never override a live
  // selection; fire once per (match, album destination).
  const autoSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoSelectReady || savedCoverId || selectedCover || !matchedId) return;
    const key = `${matchedId}|${destNorm}`;
    if (autoSelectedRef.current === key) return;
    const c = displayed.find((cv) => cv.id === matchedId);
    if (!c) return;
    autoSelectedRef.current = key;
    onSelectCover(c);
  }, [autoSelectReady, savedCoverId, selectedCover, matchedId, destNorm, displayed, onSelectCover]);

  // ── Auto-scroll + gentle one-time "pop" on the matched cover ──────────────
  // When a strong match exists for the customer's typed destination, bring it
  // into view (it's sorted to the front but sits below the search/categories,
  // so the customer otherwise stays parked at the top and misses it). We scroll
  // AFTER a short delay so the grid/thumbnails have laid out, land the card
  // centred (clear of the sticky header), and play a single calm scale+glow.
  // Runs once per (match + album destination); respects reduced-motion; never
  // forces a scroll when there's no match. Per-album: `destNorm` changes for
  // Duo/Trio so each album re-triggers on its own matched cover.
  const matchedCardRef = useRef<HTMLButtonElement | null>(null);
  const [matchPulse, setMatchPulse] = useState(false);
  const scrolledMatchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!matchedId || !isDefaultView || covers.length === 0) return;
    const key = `${matchedId}|${destNorm}`;
    if (scrolledMatchRef.current === key) return; // already done for this album/match
    scrolledMatchRef.current = key;
    const reduce = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    let popTimer: ReturnType<typeof setTimeout> | undefined;
    const scrollTimer = setTimeout(() => {
      const el = matchedCardRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });
      if (!reduce) {
        setMatchPulse(true);
        popTimer = setTimeout(() => setMatchPulse(false), 900);
      }
    }, 240); // let the cover grid settle so we land on the right element
    return () => { clearTimeout(scrollTimer); if (popTimer) clearTimeout(popTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedId, destNorm, isDefaultView, covers.length]);

  // ── Zero-match suggestion path ──────────────────────────────────────────
  //   When the local substring filter returns NO results for the customer's
  //   typed query, ask the worker for visually-similar covers via Gemini
  //   (~$0.001/call, capped at 5 results). Debounced 600ms so we don't fire
  //   on every keystroke. Skipped while a category is active or while the
  //   query is too short to be meaningful.
  const [suggestions, setSuggestions] = useState<Cover[]>([]);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const lastSuggestedQueryRef = useRef<string | null>(null);
  useEffect(() => {
    const q = searchQuery.trim();
    // Only kick in when the standard filter is empty AND the customer has
    // typed at least 2 chars (avoids firing on initial "a").
    if (filtered.length > 0 || q.length < 2 || selectedCategory) {
      if (suggestions.length > 0) setSuggestions([]);
      lastSuggestedQueryRef.current = null;
      return;
    }
    if (lastSuggestedQueryRef.current === q) return;
    const handle = setTimeout(async () => {
      lastSuggestedQueryRef.current = q;
      setIsSuggesting(true);
      try {
        const res = await coversApi.suggestSimilar(q);
        // TITLE FIX (2026-07-24): same slim-row issue as destSuggestions —
        // rehydrate each suggestion from the full local cover so the tile
        // title (text_zones) always renders.
        setSuggestions((res.covers ?? []).map((s) => covers.find((c) => c.id === s.id) ?? s));
      } catch {
        setSuggestions([]);
      } finally {
        setIsSuggesting(false);
      }
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filtered.length, selectedCategory]);

  // ── Search capture (Task 4 A+B) ─────────────────────────────────────────
  //   When the customer has typed ≥2 chars and paused (~700ms), log ONE search
  //   row: found_cover_id = the top local match's id (a hit), or null when the
  //   local filter matched nothing (a no-result — a destination/event we don't
  //   have a cover for). Powers the popularity signal and the missing-cover
  //   dashboard warning. Fire-and-forget, never blocks; de-duped per query so a
  //   pause doesn't re-log the same term. Skipped when no order context.
  const lastRecordedQueryRef = useRef<string | null>(null);
  const topMatchId = filtered.length > 0 ? filtered[0]!.id : null;
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { lastRecordedQueryRef.current = null; return; }
    if (lastRecordedQueryRef.current === q) return;
    const handle = setTimeout(() => {
      lastRecordedQueryRef.current = q;
      // The blank-theme synthetic cover ('__blank__') is never a real match.
      const found = topMatchId && topMatchId !== '__blank__' ? topMatchId : null;
      coverSearchesApi
        .record({
          shopify_order_id: shopifyOrderId,
          album_index: albumIndex,
          query_text: q,
          found_cover_id: found,
          customer_name: customerName ?? null,
          customer_phone: customerPhone ?? null,
        })
        .catch(() => { /* non-blocking */ });
    }, 700);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, topMatchId, shopifyOrderId, albumIndex]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        {/* FUNNEL V2 × MULTI-ALBUM (2026-07-24): Duo/Trio loop this step per
            album — the badge tells the customer WHICH album they're choosing
            for. Hidden for Solo (totalAlbums 1 / undefined). */}
        {(totalAlbums ?? 1) > 1 && (
          <span
            className="inline-block mb-2 px-3 py-1 rounded-full text-xs font-bold tracking-wide"
            style={{ background: `${C.gold}15`, color: C.goldDark, border: `1px solid ${C.gold}40` }}
          >
            Album {albumIndex ?? 1} sur {totalAlbums}
          </span>
        )}
        <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: C.dark }}>
          Choisissez votre design
        </h1>
        <p className="text-sm" style={{ color: C.muted }}>
          {destination && <span className="font-semibold" style={{ color: C.goldDark }}>{destination}</span>}
          {destination && year && ' · '}
          {year && <span>{year}</span>}
        </p>
      </div>

      {/* Search bar — case + accent insensitive against name, category,
          description and search_tags. Case-insensitive normalize handles
          'voyage' / 'Voyage' / 'voyäge' alike. */}
      <div className="max-w-lg mx-auto mb-5 relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
          style={{ color: C.muted }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher un design..."
          className="w-full pl-10 pr-9 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2"
          style={{
            background: C.white,
            border: `1px solid ${C.gold}25`,
            color: C.dark,
          }}
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
            style={{ color: C.muted }}
            aria-label="Effacer la recherche"
          >
            ✕
          </button>
        )}
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 justify-center mb-8">
        <button
          onClick={() => onCategoryChange(null)}
          className="px-4 py-2 rounded-full text-xs font-semibold transition-all"
          style={{
            background: !selectedCategory ? C.gold : `${C.gold}10`,
            color: !selectedCategory ? C.white : C.goldDark,
          }}
        >
          Tous
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => onCategoryChange(cat.slug)}
            className="px-4 py-2 rounded-full text-xs font-semibold transition-all"
            style={{
              background: selectedCategory === cat.slug ? C.gold : `${C.gold}10`,
              color: selectedCategory === cat.slug ? C.white : C.goldDark,
            }}
          >
            {cat.icon && <span className="mr-1">{cat.icon}</span>}
            {cat.label_fr}
          </button>
        ))}
      </div>

      {/* Blank theme option */}
      <div className="mb-6">
        <button
          onClick={() => onSelectCover({
            id: '__blank__',
            name: 'Vierge',
            categories: [],
            product_types: [],
            storage_path: null,
            thumbnail_url: null,
            design_url: null,
            format: null,
            text_zones: [
              { type: 'destination' as const, x: 0.1, y: 0.3, width: 0.8, height: 0.12, font_size: 64, font_family: 'Londrina Solid', color: '#1A1A1A', align: 'center' as const, default_text: 'DESTINATION' },
              { type: 'year' as const, x: 0.1, y: 0.45, width: 0.8, height: 0.08, font_size: 32, font_family: 'Londrina Solid', color: '#8A8578', align: 'center' as const, default_text: '2025' },
            ],
            dominant_color: '#FFFFFF',
            width_px: 1860,
            height_px: 2480,
            back_cover_style: 'blank',
            description: 'Couverture vierge',
            search_tags: [],
            usage_count: 0,
            is_visible: true,
            sort_order: -1,
            created_at: '',
            updated_at: '',
          } as Cover)}
          className="w-full p-4 rounded-xl border-2 border-dashed transition-all flex items-center gap-4"
          style={{
            borderColor: selectedCover?.id === '__blank__' ? C.gold : `${C.gold}30`,
            background: selectedCover?.id === '__blank__' ? `${C.gold}08` : C.white,
          }}
        >
          <div className="w-16 h-20 rounded-lg flex items-center justify-center" style={{ background: C.beigeLight, border: `1px dashed ${C.gold}40` }}>
            <svg className="w-6 h-6" style={{ color: `${C.gold}60` }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v12m6-6H6" />
            </svg>
          </div>
          <div className="text-left">
            <p className="font-semibold text-sm" style={{ color: C.dark }}>Couverture vierge</p>
            <p className="text-xs" style={{ color: C.muted }}>Commencez de zéro — couleur, icônes, votre photo</p>
          </div>
        </button>
      </div>

      {/* Zero-match suggestions — when the local filter returns nothing
          AND the customer is actively typing, show Gemini-ranked similar
          covers. Falls through silently if the worker call fails. */}
      {filtered.length === 0 && searchQuery.trim().length >= 2 && !selectedCategory && (
        <div className="mb-8">
          {isSuggesting ? (
            <p className="text-sm text-center" style={{ color: C.muted }}>
              Recherche de designs similaires…
            </p>
          ) : suggestions.length > 0 ? (
            <>
              <p className="text-sm text-center mb-4" style={{ color: C.muted }}>
                Aucun résultat exact pour
                {' '}
                <strong style={{ color: C.dark }}>« {searchQuery} »</strong>
                {' '}
                — vous cherchiez peut-être :
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {suggestions.map((cover) => {
                  const isSelected = cover.id === selectedCover?.id;
                  return (
                    <button
                      key={cover.id}
                      onClick={() => onSelectCover(cover)}
                      className="rounded-xl overflow-hidden transition-transform hover:scale-[1.02] text-left group [content-visibility:auto] [contain-intrinsic-size:auto_360px]"
                      style={{
                        border: isSelected ? `3px solid ${C.gold}` : `1px solid ${C.gold}20`,
                        background: cover.dominant_color ?? C.beigeLight,
                        boxShadow: isSelected ? `0 4px 20px ${C.gold}30` : '0 2px 8px rgba(0,0,0,0.06)',
                      }}
                    >
                      {cover.thumbnail_url ? (
                        // PickerCoverImage is a lightweight static <img> with a
                        // CSS-only text overlay — no ResizeObserver, no drag
                        // handlers. Replaces live <CoverThumbnail> in the
                        // picker grid to keep the scroll thread free.
                        <PickerCoverImage cover={cover} destination="" year="" />
                      ) : (
                        <div className="w-full aspect-[3/4] flex items-center justify-center" style={{ background: cover.dominant_color ?? C.beige }}>
                          <span className="text-xs" style={{ color: C.muted }}>{cover.name}</span>
                        </div>
                      )}
                      <div className="px-3 py-2">
                        <p className="text-xs font-semibold truncate" style={{ color: isSelected ? C.gold : C.dark }}>
                          {cover.name}
                        </p>
                        {cover.categories.length > 0 && (
                          <p className="text-[10px] mt-0.5 truncate" style={{ color: C.muted }}>{cover.categories.join(' · ')}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-center" style={{ color: C.muted }}>
              Aucun design trouvé pour « {searchQuery} ».
            </p>
          )}
        </div>
      )}

      {/* One-time gentle pop + soft gold glow for the destination-matched cover —
          calm/premium, plays once when the match is scrolled into view. */}
      <style>{`
        @keyframes coverMatchPop {
          0%   { transform: scale(1);    box-shadow: 0 4px 18px ${C.gold}26; }
          30%  { transform: scale(1.05); box-shadow: 0 10px 34px ${C.gold}66, 0 0 0 3px ${C.gold}55; }
          100% { transform: scale(1);    box-shadow: 0 4px 18px ${C.gold}26; }
        }
      `}</style>
      {/* Cover grid — extra bottom padding so content isn't hidden behind fixed CTA */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 pb-28">
        {displayed.map(cover => {
          const isSelected = cover.id === selectedCover?.id;
          const isMatch = cover.id === matchedId;
          return (
            <button
              key={cover.id}
              ref={isMatch ? matchedCardRef : undefined}
              onClick={() => onSelectCover(cover)}
              className="relative rounded-xl overflow-hidden transition-transform hover:scale-[1.02] text-left group [content-visibility:auto] [contain-intrinsic-size:auto_360px]"
              style={{
                border: isSelected ? `3px solid ${C.gold}` : isMatch ? `2px solid ${C.gold}` : `1px solid ${C.gold}20`,
                background: cover.dominant_color ?? C.beigeLight,
                boxShadow: isSelected ? `0 4px 20px ${C.gold}30` : isMatch ? `0 4px 18px ${C.gold}26` : '0 2px 8px rgba(0,0,0,0.06)',
                animation: isMatch && matchPulse ? 'coverMatchPop 760ms cubic-bezier(0.22,1,0.36,1) both' : undefined,
                willChange: isMatch && matchPulse ? 'transform' : undefined,
              }}
            >
              {isMatch && (
                <div
                  className="absolute top-2 left-2 z-10 flex items-center gap-1 px-2 py-[3px] rounded-full text-[10px] font-semibold pointer-events-none"
                  style={{ background: C.gold, color: C.white, maxWidth: 'calc(100% - 16px)', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
                >
                  <span aria-hidden>✦</span>
                  <span className="truncate">{titleCaseDest(destination)}</span>
                </div>
              )}
              {cover.thumbnail_url ? (
                // PickerCoverImage shows each zone's `default_text` (the
                // cover's sample copy — e.g. "London GENEVA 2026") via a static
                // CSS overlay. No ResizeObserver, no drag handlers — keeps the
                // scroll thread free across dozens-to-hundreds of cards. The
                // editor (after the customer picks) is where their typed
                // destination overrides the defaults via <CoverThumbnail>.
                <PickerCoverImage cover={cover} destination="" year="" />
              ) : (
                <div className="w-full aspect-[3/4] flex items-center justify-center" style={{ background: cover.dominant_color ?? C.beige }}>
                  <span className="text-xs" style={{ color: C.muted }}>{cover.name}</span>
                </div>
              )}
              <div className="px-3 py-2">
                <p className="text-xs font-semibold truncate" style={{ color: isSelected ? C.gold : C.dark }}>
                  {cover.name}
                </p>
                {cover.categories.length > 0 && (
                  <p className="text-[10px] mt-0.5 truncate" style={{ color: C.muted }}>{cover.categories.join(' · ')}</p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── Step 1: Upload ──────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

export function StepUpload({
  photos,
  uploadFiles,
  uploadProgress,
  isUploading,
  fileInputRef,
  onUpload,
  onRetry,
  notice,
  onDeletePhoto,
  onDeletePhotos,
  totalPhotos,
  onNext,
  localPreviews,
}: {
  photos: (Photo & { signed_url: string | null; tray_signed_url?: string | null })[];
  uploadFiles: { name: string; progress: number; status: string }[];
  uploadProgress: number;
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onUpload: (files: FileList) => void;
  onRetry: () => void;
  notice: string | null;
  onDeletePhoto: (photoId: string) => void;
  onDeletePhotos: (photoIds: string[]) => void;
  totalPhotos: number;
  onNext: () => void;
  localPreviews: Map<string, string>;
}) {
  const canProceed = totalPhotos >= MIN_PHOTOS && !isUploading;
  const failedCount = uploadFiles.filter((f) => f.status === 'error').length;
  const onIos = isIosDevice();

  // ── Scroll-to-progress on batch start ───────────────────────────────────
  // Real-iPhone feedback: pinning the scroll to where the customer was left
  // them stuck at the top dropzone, unable to see "X/N" without scrolling past
  // the whole 60vh gallery — and it fought their manual scroll on every
  // optimistic per-photo add. Replaced with a single effect: when a batch
  // STARTS (isUploading false → true), bring the dropzone + (now-above-gallery)
  // progress area into view so "Envoi… {done}/{total}" + the bar are visible
  // right where they already are. No pinning, no per-photo re-apply — the
  // gallery is 60vh-capped so its growth scrolls INTERNALLY and nothing above
  // it grows (the moved progress block is fixed-size), so there's no viewport
  // yank to correct for.
  const topRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef<HTMLDivElement | null>(null);
  const prevUploadingRef = useRef(isUploading);
  useEffect(() => {
    if (isUploading && !prevUploadingRef.current) {
      // Scroll to the LOADING section (the progress bar + the new tiles, which
      // now prepend to the TOP of the grid) — NOT the dropzone — so the client
      // SEES the import happening instead of being stuck at the top while the new
      // photos load hidden below the earlier ones. rAF lets the just-shown
      // progress block lay out first; fall back to the top container.
      requestAnimationFrame(() => {
        (loadingRef.current ?? topRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    prevUploadingRef.current = isUploading;
  }, [isUploading]);

  // ── Multi-select delete ─────────────────────────────────────────────────
  // "Sélectionner" mode lets the customer tick several already-imported photos
  // and delete them in one go, instead of the one-by-one × chips. Tapping a
  // tile toggles its selection while in this mode; the × chips are hidden.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const allSelected = photos.length > 0 && selectedIds.size === photos.length;
  const toggleSelect = (id: string) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllToggle = () => setSelectedIds(allSelected ? new Set() : new Set(photos.map((p) => p.id)));
  const exitSelect = () => { setSelectMode(false); setSelectedIds(new Set()); };
  const handleBulkDelete = () => { const ids = [...selectedIds]; exitSelect(); onDeletePhotos(ids); };

  // Build the gallery tile list (FIX #5). Successfully-added photos render with
  // their instant local preview (falling back to the server tiers); a tile with
  // NO image yet is flagged `loading` so the grid shows a shimmer/spinner, never
  // a dead blank box. Failed uploads (status:'error') — which never became real
  // `photos` rows and are NOT in the count — are appended as explicit error
  // tiles so the customer sees WHICH ones failed (the banner's Réessayer retries
  // them). De-duped by filename against added photos so a retried-then-succeeded
  // file doesn't show twice.
  const galleryTiles = useMemo(() => {
    const addedTiles = photos.map((p) => {
      const thumbUrl = localPreviews.get(p.id) ?? p.tray_signed_url ?? p.signed_url ?? null;
      return {
        id: p.id,
        thumbUrl,
        selected: selectedIds.has(p.id),
        loading: !thumbUrl, // no local preview AND cold server transform → shimmer
      };
    });
    const addedNames = new Set(photos.map((p) => (p.original_filename ?? '').trim().toLowerCase()).filter(Boolean));
    const failedTiles = uploadFiles
      .map((f, i) => ({ f, i }))
      .filter(({ f }) => f.status === 'error' && !addedNames.has((f.name ?? '').trim().toLowerCase()))
      .map(({ f, i }) => ({
        id: `failed-${i}-${f.name}`,
        thumbUrl: null,
        selected: false,
        badge: 'error' as const,
      }));
    return [...addedTiles, ...failedTiles];
  }, [photos, uploadFiles, localPreviews, selectedIds]);

  // ── "Préparation de vos photos…" overlay ──────────────────────────────
  // The native file picker fires NO event when cancelled — the browser only
  // fires `change` when files are actually selected. So the overlay is shown
  // ONLY once files arrive (see the input's onChange), NEVER on picker-open.
  // That alone means a cancelled picker can't leave it stuck. On top of that
  // we wire three independent safety nets so it can never hang indefinitely:
  //   1. `cancel` event on the input (modern iOS Safari / Chrome / Firefox).
  //   2. focus / visibility return with the picker still unresolved (older
  //      browsers that fire no `cancel`).
  //   3. a hard timeout backstop.
  const SAFETY_TIMEOUT_MS = 6000;
  const [isPickingFiles, setIsPickingFiles] = useState(false);
  // True from picker-open until the browser resolves it (change or cancel).
  // Lets the focus/visibility fallback tell a real cancellation from a normal
  // selection without racing the change event.
  const pickerArmedRef = useRef(false);
  // FIX #3 — set true the moment a NON-EMPTY `change` lands. Lets the focus/
  // visibility cancel net avoid firing while a pick is still resolving on
  // Samsung (empty change → populated change moments later).
  const sawNonEmptyChangeRef = useRef(false);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Single teardown path for every exit: upload took over, cancel, focus
  // fallback, or timeout. Always disarms and clears the pending timer.
  const clearPicking = useCallback(() => {
    pickerArmedRef.current = false;
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
    setIsPickingFiles(false);
  }, []);

  // Once handleUpload populates uploadFiles, the real progress UI takes over
  // and the bridging overlay should clear.
  useEffect(() => {
    if (isPickingFiles && uploadFiles.length > 0) clearPicking();
  }, [isPickingFiles, uploadFiles.length, clearPicking]);

  // Safety net 1 — the standard `cancel` event. Fires the instant the picker
  // is dismissed with no selection on modern browsers.
  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) return;
    const onCancel = () => clearPicking();
    input.addEventListener('cancel', onCancel);
    return () => input.removeEventListener('cancel', onCancel);
  }, [fileInputRef, clearPicking]);

  // Safety net 2 — older devices fire no `cancel`. When the window regains
  // focus / the tab becomes visible again and the picker is still armed (no
  // `change` fired), the user dismissed it.
  // FIX #3c — DO NOT race a still-resolving Samsung pick. The first `change` can
  // arrive empty (focus returns) and the populated `change` follows a beat later;
  // a short 250ms fallback used to cancel in that gap and lose the pick. So we (1)
  // wait a longer quiet window (700ms) and (2) only treat it as a cancel if the
  // picker is STILL armed AND no non-empty `change` has arrived in the meantime.
  useEffect(() => {
    const onReturn = () => {
      if (document.hidden) return;
      setTimeout(() => {
        if (pickerArmedRef.current && !sawNonEmptyChangeRef.current) clearPicking();
      }, 700);
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [clearPicking]);

  // Clean up a pending safety timer if the step unmounts mid-pick.
  useEffect(() => () => {
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
  }, []);

  return (
    <div ref={topRef} className="max-w-3xl mx-auto px-4 py-8">
      {/* Per-step back button retired — the header's ← Retour / Suivant →
          pair owns navigation across the wizard now. Keeping a second
          back button here duplicated the gesture and confused users. */}

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: C.dark }}>
          Ajoutez vos photos
        </h1>
        <p className="text-sm" style={{ color: C.muted }}>
          Minimum {MIN_PHOTOS} photos · Maximum {MAX_PHOTOS} · HEIC supporté
        </p>
      </div>

      {/* Full-screen "Préparation de vos photos…" overlay. Shown ONLY once
          files actually arrive (never on picker-open), so a cancelled picker
          can't leave it stuck. Bridges the gap from file-selection until the
          upload progress UI takes over. */}
      {isPickingFiles && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-6"
             style={{ background: 'rgba(31, 41, 55, 0.55)', backdropFilter: 'blur(6px)' }}
             onClick={(e) => e.stopPropagation()}
        >
          <div className="w-full max-w-xs rounded-2xl text-center px-6 py-8"
               style={{ background: C.white, boxShadow: '0 20px 50px rgba(0,0,0,0.18)' }}
          >
            <div className="w-10 h-10 mx-auto mb-4 rounded-full border-2 border-t-transparent animate-spin"
                 style={{ borderColor: C.gold, borderTopColor: 'transparent' }} />
            <p className="text-sm font-semibold mb-1" style={{ color: C.dark }}>
              Préparation de vos photos…
            </p>
            <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
              Quelques secondes le temps que votre iPhone exporte les images.
            </p>
          </div>
        </div>
      )}


      {/* Upload area */}
      <div
        className="rounded-2xl border-2 border-dashed p-12 text-center transition-all cursor-pointer hover:border-opacity-100 group"
        style={{ borderColor: `${C.gold}50`, background: `${C.gold}04` }}
        onClick={() => {
          // Arm the cancel fallbacks, then open the picker. The overlay is
          // NOT shown here — only once files actually arrive (onChange) — so
          // a cancelled picker can never leave it stuck.
          // FIX #3a — reset the input value HERE (on open), not inside the
          // change handler. Clearing it on open guarantees a same-file re-pick
          // still fires a `change`, while NOT clearing it inside `change` means
          // Samsung's first (sometimes empty) `change` no longer wipes a pick
          // that's about to be delivered by the next, populated `change`.
          pickerArmedRef.current = true;
          sawNonEmptyChangeRef.current = false;
          if (fileInputRef.current) fileInputRef.current.value = '';
          fileInputRef.current?.click();
        }}
        onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.background = `${C.gold}10`; }}
        onDragLeave={e => { e.currentTarget.style.borderColor = `${C.gold}50`; e.currentTarget.style.background = `${C.gold}04`; }}
        onDrop={e => {
          e.preventDefault();
          e.currentTarget.style.borderColor = `${C.gold}50`;
          e.currentTarget.style.background = `${C.gold}04`;
          if (e.dataTransfer.files.length) onUpload(e.dataTransfer.files);
        }}
      >
        <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center transition-all group-hover:scale-110" style={{ background: `${C.gold}15` }}>
          <svg className="w-8 h-8" style={{ color: C.gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="text-base font-semibold mb-1" style={{ color: C.dark }}>
          Glissez vos photos ici
        </p>
        <p className="text-sm" style={{ color: C.muted }}>
          ou cliquez pour sélectionner
        </p>
        {/* Phase 4 — persistent iOS batching helper. iPhone RAM caps a single
            pick at 30 photos; this tells the customer up front to add in lots. */}
        {onIos && (
          <p className="text-[11px] mt-2 font-medium" style={{ color: C.goldDark }}>
            Choisissez jusqu'à 20 photos à la fois.
          </p>
        )}
        <input
          ref={fileInputRef}
          type="file"
          // Device-aware accept (galleryAccept): bare image/* on Android/desktop
          // so the gallery/Photos opens with multi-select (an explicit HEIC+ext
          // list hid the gallery → camera-only chooser), and the explicit HEIC
          // list on iOS so Safari hands HEIC through without a slow pre-transcode.
          accept={galleryAccept()}
          multiple
          className="hidden"
          onChange={e => {
            if (e.target.files?.length) {
              // Files arrived → NOW show the overlay and hand off to the
              // upload pipeline. Arm the hard timeout so the overlay can't
              // hang even on the all-duplicates path (handleUpload returns
              // early there and never populates the progress UI).
              pickerArmedRef.current = false;
              sawNonEmptyChangeRef.current = true;
              setIsPickingFiles(true);
              if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
              safetyTimerRef.current = setTimeout(clearPicking, SAFETY_TIMEOUT_MS);
              onUpload(e.target.files);
            }
            // FIX #3b — EMPTY `change` is NOT a cancel. Samsung's first `change`
            // can fire with a not-yet-populated FileList; tearing down here (the
            // old `clearPicking()` + `value=''`) discarded the pick before the
            // next, populated `change` could deliver it. So on an empty change we
            // do NOTHING: stay armed, keep the input value intact, and let the
            // follow-up `change` (or a real cancel via the focus/visibility net)
            // resolve it. The value is reset on OPEN now (see onClick), not here.
          }}
        />
      </div>

      {/* iOS-only iCloud import reassurance — directly under the upload-step
          dropzone, deepens while an import is in flight. Self-hides off iOS. */}
      <IosUploadNote active={isUploading} style={{ marginTop: 8 }} />

      {/* Phase 3 — customer-facing notice (dedup / iOS cap / aggregate failure).
          Replaces the old silent console.log + bare return. */}
      {notice && (
        <div className="mt-3 flex items-center gap-3 rounded-xl px-4 py-3 text-xs font-medium"
             style={{ background: failedCount > 0 ? '#FEF2F2' : `${C.gold}10`, color: failedCount > 0 ? '#B91C1C' : C.dark, border: `1px solid ${failedCount > 0 ? '#FCA5A5' : `${C.gold}25`}` }}>
          <span className="flex-1">{notice}</span>
          {failedCount > 0 && !isUploading && (
            <button
              type="button"
              onClick={onRetry}
              className="shrink-0 px-3 py-1.5 rounded-lg text-white font-bold"
              style={{ background: '#DC2626' }}
            >
              Réessayer
            </button>
          )}
        </div>
      )}

      {/* Phase 3 — standalone aggregate-failure banner when there's no active
          notice but some files errored (e.g. failures surfaced after the batch
          finished). Always offers a retry so a stalled batch is never a dead end. */}
      {!notice && failedCount > 0 && !isUploading && (
        <div className="mt-3 flex items-center gap-3 rounded-xl px-4 py-3 text-xs font-medium"
             style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FCA5A5' }}>
          <span className="flex-1">{failedCount} photo{failedCount > 1 ? 's' : ''} échouée{failedCount > 1 ? 's' : ''}.</span>
          <button type="button" onClick={onRetry} className="shrink-0 px-3 py-1.5 rounded-lg text-white font-bold" style={{ background: '#DC2626' }}>
            Réessayer
          </button>
        </div>
      )}

      {/* Upload progress — MOVED ABOVE the 60vh gallery. Real-iPhone feedback:
          when it sat below the gallery, the customer (at the top dropzone after
          a pick) couldn't see "X/N" without scrolling past every tile. Now it
          renders right after the dropzone + notices so "Envoi… {done}/{total}"
          + the bar appear immediately where they already are. The orbiting/
          vortex animation is the eye-candy; the bar is the hard number. This
          block is fixed-size (no growing list — the file list is internally
          max-h scrolled), so nothing above the gallery grows → no viewport yank. */}
      {/* Scroll anchor: on batch-start the view jumps here so the dropzone scrolls
          off-top and the customer lands on the live progress + the new tiles. */}
      <div ref={loadingRef} className="scroll-mt-3" />
      {isUploading && uploadFiles.length > 0 && (
        <div className="mt-6 rounded-xl p-4" style={{ background: C.white, border: `1px solid ${C.gold}20` }}>
          <UploadVortex done={doneCount(uploadFiles)} total={uploadFiles.length} />
          <UploadTip />
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold" style={{ color: C.dark }}>
              Envoi de vos photos…
            </span>
            <span className="text-xs tabular-nums font-medium" style={{ color: C.gold }}>
              {doneCount(uploadFiles)}/{uploadFiles.length}
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: `${C.gold}15` }}>
            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%`, background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})` }} />
          </div>
          {/* Mini status list (last 5) — shown collapsed by default; tap to expand */}
          <details className="mt-3">
            <summary className="text-[10px] cursor-pointer select-none" style={{ color: C.muted }}>
              Voir les fichiers en cours
            </summary>
            <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
              {uploadFiles.slice(-5).map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px]">
                  <span className={cn(
                    'w-1.5 h-1.5 rounded-full shrink-0',
                    f.status === 'done' ? 'bg-green-400' :
                    f.status === 'converting' ? 'bg-purple-400 animate-pulse' :
                    f.status === 'uploading' ? 'bg-amber-400 animate-pulse' :
                    f.status === 'error' ? 'bg-red-400' : 'bg-gray-300'
                  )} />
                  <span className="truncate flex-1" style={{ color: C.muted }}>{f.name}</span>
                  <span className="tabular-nums shrink-0" style={{ color: C.gold }}>{f.progress}%</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Resumed-photo strip — shown whenever there are already photos on
          the server for this session (customer returning after a phone
          lock / refresh). Makes it visually obvious their work is saved
          and lets them decide if they still want to add more. */}
      {/* Gallery is now shown DURING the batch too — the optimistic per-photo
          adds grow it live (with instant local-preview tiles) so the customer
          watches their photos land instead of a frozen "X / N" for ~2 min.
          Select-to-delete is hidden while uploading (deleting an in-flight
          photo is a foot-gun); it returns at batch end. */}
      {galleryTiles.length > 0 && (
        <div className="mt-6 rounded-xl p-4" style={{ background: C.white, border: `1px solid ${C.gold}20` }}>
          <div className="flex items-center justify-between mb-3 gap-2">
            <span className="text-xs font-semibold" style={{ color: C.dark }}>
              {selectMode
                ? `${selectedIds.size} sélectionnée${selectedIds.size > 1 ? 's' : ''}`
                : isUploading
                  ? `${photos.length} photo${photos.length > 1 ? 's' : ''} importée${photos.length > 1 ? 's' : ''}…`
                  : `${photos.length} photo${photos.length > 1 ? 's' : ''} déjà importée${photos.length > 1 ? 's' : ''}`}
            </span>
            {isUploading ? null : selectMode ? (
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={selectAllToggle} className="text-[11px] font-medium" style={{ color: C.gold }}>
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={selectedIds.size === 0}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-lg text-white disabled:opacity-40"
                  style={{ background: '#DC2626' }}
                >
                  Supprimer ({selectedIds.size})
                </button>
                <button type="button" onClick={exitSelect} className="text-[11px] font-medium" style={{ color: C.muted }}>
                  Annuler
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setSelectMode(true)} className="text-[11px] font-semibold shrink-0" style={{ color: C.gold }}>
                Sélectionner
              </button>
            )}
          </div>
          {/* Shared, VIRTUALIZED iPhone-gallery grid. thumbUrl prefers the
              INSTANT local objectURL preview (built at upload time) so a
              just-landed tile renders immediately instead of a blank box while
              the cold `tray_signed_url` server transform warms up. Delete is
              disabled during upload (no × chip, no select-tap). */}
          <UploadedTileGrid
            photos={galleryTiles}
            selectMode={selectMode && !isUploading}
            onToggleSelect={isUploading ? () => {} : toggleSelect}
            onDelete={(selectMode || isUploading) ? undefined : onDeletePhoto}
          />
          {!selectMode && !isUploading && (
            <p className="mt-2 text-[10px]" style={{ color: C.muted }}>
              Vos précédentes photos sont conservées · « Sélectionner » pour en supprimer plusieurs.
            </p>
          )}
        </div>
      )}

      {/* Photos count badge */}
      {totalPhotos > 0 && (
        <div className="mt-6 flex items-center justify-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: `${C.gold}10` }}>
            <span className="text-2xl font-bold tabular-nums" style={{ color: C.gold }}>{totalPhotos}</span>
            <span className="text-xs" style={{ color: C.muted }}>photo{totalPhotos > 1 ? 's' : ''} ajoutée{totalPhotos > 1 ? 's' : ''}</span>
          </div>
          {totalPhotos < MIN_PHOTOS && (
            <span className="text-xs font-medium" style={{ color: C.danger }}>
              Minimum {MIN_PHOTOS} requis
            </span>
          )}
        </div>
      )}

      {/* (Duplicate non-virtualized preview grid removed — the single shared
          UploadedTileGrid above is now the only "déjà importée" gallery.) */}

      {/* Bottom CTA */}
      <div className="sticky bottom-0 pt-6 pb-4 -mx-4 px-4" style={{ background: `linear-gradient(to top, ${C.beige} 60%, transparent)` }}>
        <button
          disabled={!canProceed}
          onClick={onNext}
          className="w-full py-4 rounded-2xl text-base font-bold tracking-wide transition-all disabled:opacity-40"
          style={{
            background: canProceed ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : `${C.gold}40`,
            color: C.white,
            boxShadow: canProceed ? `0 4px 20px ${C.gold}40` : 'none',
          }}
        >
          {isUploading
            ? `Import en cours… (${doneCount(uploadFiles)}/${uploadFiles.length})`
            : canProceed ? 'Continuer →' : `Ajoutez encore ${Math.max(0, MIN_PHOTOS - totalPhotos)} photos`}
        </button>
      </div>
    </div>
  );
}

function doneCount(files: { status: string }[]) {
  return files.filter(f => f.status === 'done').length;
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── Step 2: AI vs Manual ────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function StepMode({
  totalPhotos,
  onAiMagic,
  onManual,
}: {
  totalPhotos: number;
  onAiMagic: () => void;
  onManual: () => void;
}) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      {/* Per-step back button retired (see StepUpload comment). */}

      <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: C.dark }}>
        Comment créer votre album ?
      </h1>
      <p className="text-sm mb-12" style={{ color: C.muted }}>
        {totalPhotos} photos prêtes à être arrangées
      </p>

      <div className="grid md:grid-cols-2 gap-6 max-w-xl mx-auto">
        {/* AI Magic — primary CTA */}
        <button
          onClick={onAiMagic}
          className="rounded-2xl p-8 text-center transition-all hover:scale-[1.02] group"
          style={{
            background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
            boxShadow: `0 8px 30px ${C.gold}40`,
          }}
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-white/20">
            <span className="text-3xl">✨</span>
          </div>
          <h3 className="text-lg font-bold mb-2 text-white">Magie AI</h3>
          <p className="text-xs text-white/80 leading-relaxed">
            On s'occupe de tout ! L'IA arrange vos photos chronologiquement avec des mises en page variées.
          </p>
          <span className="inline-block mt-4 px-4 py-1.5 rounded-full text-xs font-bold bg-white/20 text-white">
            Recommandé
          </span>
        </button>

        {/* Manual */}
        <button
          onClick={onManual}
          className="rounded-2xl p-8 text-center transition-all hover:scale-[1.02]"
          style={{ background: C.white, border: `2px solid ${C.gold}25`, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: `${C.gold}10` }}>
            <svg className="w-8 h-8" style={{ color: C.gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold mb-2" style={{ color: C.dark }}>Mode manuel</h3>
          <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
            Contrôle total. Arrangez chaque page et photo vous-même dans l'éditeur.
          </p>
        </button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── Step 3: AI Magic Loading ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

// Rotating travel/memory quotes shown during long async steps. French to
// match the rest of the customer flow. Keep short — they rotate every 4s.
const FLOW_TIPS = [
  '« Voyager, c\'est apprendre à se rappeler. »',
  '« Chaque photo raconte une histoire à ne jamais oublier. »',
  '« Les souvenirs s\'effacent, les albums restent. »',
  '« Un voyage commence à la première image. »',
  '« Vos plus belles aventures méritent d\'être imprimées. »',
  '« La mémoire est une seconde vie, capturez-la. »',
  '« Le bonheur, c\'est d\'avoir des photos à raconter. »',
  '« Un album, mille émotions. »',
];

function useRotatingTip(intervalMs = 4000) {
  const [i, setI] = useState(() => Math.floor(Math.random() * FLOW_TIPS.length));
  useEffect(() => {
    const t = setInterval(() => setI(prev => (prev + 1) % FLOW_TIPS.length), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return FLOW_TIPS[i];
}

// Small reused component — rotating italic quote shown above the upload bar
// so the customer has something engaging to read while photos transfer.
function UploadTip() {
  const tip = useRotatingTip(3500);
  return (
    <p
      key={tip}
      className="text-xs italic mb-3 leading-relaxed text-center animate-[fadeInUp_0.6s_ease-out]"
      style={{ color: C.goldDark }}
    >
      {tip}
    </p>
  );
}

// Visual loading animation — orbiting photo tiles converging toward a central
// pulsing album icon with the live progress fraction overlaid. Purely CSS
// (transform + opacity keyframes); no Lottie/dep added. The user's original
// brief was "photos being dragged into a black hole, or any good animation".
function UploadVortex({ done, total }: { done: number; total: number }) {
  // 8 tiles arranged on a circle, each delayed so they cycle into the center
  // staggered. The keyframes are inlined here so the component is self-
  // contained — no global CSS dependency.
  const tiles = [0, 1, 2, 3, 4, 5, 6, 7];
  return (
    <div className="relative w-full h-40 mb-2 flex items-center justify-center overflow-hidden">
      <style>{`
        @keyframes storiz-vortex {
          0%   { transform: translate(var(--ux), var(--uy)) scale(1)   rotate(0deg);   opacity: 0; }
          15%  { opacity: 1; }
          85%  { transform: translate(0, 0) scale(0.15) rotate(180deg); opacity: 0.9; }
          100% { transform: translate(0, 0) scale(0)    rotate(220deg); opacity: 0; }
        }
        @keyframes storiz-vortex-pulse {
          0%, 100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(201, 162, 74, 0.45); }
          50%      { transform: scale(1.08); box-shadow: 0 0 0 14px rgba(201, 162, 74, 0); }
        }
      `}</style>

      {/* Orbiting / vortexing photo tiles */}
      {tiles.map((i) => {
        const angle = (i / tiles.length) * Math.PI * 2;
        const r = 70;
        const ux = Math.cos(angle) * r;
        const uy = Math.sin(angle) * r;
        return (
          <div
            key={i}
            className="absolute rounded-md"
            style={{
              width: 22,
              height: 28,
              left: '50%',
              top: '50%',
              marginLeft: -11,
              marginTop: -14,
              background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
              boxShadow: `0 2px 6px ${C.gold}30`,
              ['--ux' as string]: `${ux}px`,
              ['--uy' as string]: `${uy}px`,
              animation: `storiz-vortex 2.4s cubic-bezier(.55,.0,.55,1) ${(i * 0.3).toFixed(2)}s infinite`,
            }}
          />
        );
      })}

      {/* Central pulsing puck with the progress count */}
      <div
        className="relative rounded-full flex items-center justify-center"
        style={{
          width: 72,
          height: 72,
          background: `radial-gradient(circle at 30% 30%, ${C.goldLight}, ${C.gold})`,
          animation: 'storiz-vortex-pulse 1.6s ease-in-out infinite',
        }}
      >
        <div className="text-white text-center leading-tight">
          <div className="text-xl font-bold tabular-nums">{done}</div>
          <div className="text-[9px] opacity-90 -mt-0.5">/ {total}</div>
        </div>
      </div>
    </div>
  );
}

// Asymptotic progress bar — fills smoothly toward 95% and stops there.
// We can't get real progress events from the AI layout endpoint (one-shot
// fetch), but a moving bar reassures the customer that work is happening.
// The parent unmounts this component when the actual API call resolves,
// which feels indistinguishable from a real "100% done".
function useFakeProgress(targetCap = 0.95, intervalMs = 200, easing = 0.04) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setPct((p) => Math.min(targetCap, p + (targetCap - p) * easing));
    }, intervalMs);
    return () => clearInterval(t);
  }, [targetCap, intervalMs, easing]);
  return pct;
}

function StepMagic() {
  const tip = useRotatingTip(4500);
  const progress = useFakeProgress();
  const pctLabel = Math.round(progress * 100);

  // Stack of 5 polaroid-style cards. Each one rotates from the back of the
  // pile to the front in turn, with a subtle scale + tilt. Premium with
  // personality — feels like the AI is "shuffling" through your photos to
  // build the album, not staring at a generic spinner.
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <style>{`
        @keyframes storiz-card-shuffle {
          0%, 100% {
            transform: translate(var(--rest-x), var(--rest-y)) rotate(var(--rest-r)) scale(0.92);
            opacity: 0.55;
            z-index: 0;
          }
          25% {
            transform: translate(0, -8px) rotate(0deg) scale(1.04);
            opacity: 1;
            z-index: 5;
          }
          50% {
            transform: translate(0, 0) rotate(0deg) scale(1);
            opacity: 1;
            z-index: 5;
          }
          75% {
            transform: translate(calc(var(--rest-x) * 0.6), calc(var(--rest-y) * 0.6)) rotate(calc(var(--rest-r) * 0.7)) scale(0.96);
            opacity: 0.85;
            z-index: 2;
          }
        }
        @keyframes storiz-pulse-shadow {
          0%, 100% { box-shadow: 0 6px 28px rgba(46, 36, 24, 0.10); }
          50%      { box-shadow: 0 10px 40px rgba(46, 36, 24, 0.18); }
        }
      `}</style>

      <div className="relative h-44 mb-10 flex items-center justify-center">
        {[
          { rx: -32, ry:  6, rr: -10, delay: '0s'   },
          { rx:  18, ry: -4, rr:   8, delay: '1.2s' },
          { rx: -10, ry: 10, rr:  -3, delay: '2.4s' },
          { rx:  28, ry:  2, rr:  12, delay: '3.6s' },
          { rx: -24, ry:  -6, rr:  -7, delay: '4.8s' },
        ].map((c, i) => (
          <div
            key={i}
            className="absolute w-24 h-32 rounded-md"
            style={{
              background: i % 2 === 0
                ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`
                : `linear-gradient(135deg, ${C.goldDark}, ${C.gold})`,
              ['--rest-x' as string]: `${c.rx}px`,
              ['--rest-y' as string]: `${c.ry}px`,
              ['--rest-r' as string]: `${c.rr}deg`,
              animation: `storiz-card-shuffle 6s ease-in-out ${c.delay} infinite, storiz-pulse-shadow 6s ease-in-out ${c.delay} infinite`,
              transform: `translate(${c.rx}px, ${c.ry}px) rotate(${c.rr}deg) scale(0.92)`,
              border: `1px solid ${C.white}40`,
            }}
          />
        ))}
      </div>

      <h2
        className="text-2xl font-normal mb-3 tracking-tight"
        style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: C.dark }}
      >
        Création de votre album
      </h2>

      <p className="text-xs mb-5" style={{ color: C.muted }}>
        L'IA assemble vos photos en une mise en page éditoriale
      </p>

      <p
        key={tip}
        className="text-sm italic max-w-xs mx-auto leading-relaxed animate-[fadeInUp_0.8s_ease-out]"
        style={{ color: C.goldDark }}
      >
        {tip}
      </p>

      {/* Minimalist progress bar — thin cream track, gold gradient fill,
          small percentage in the brand serif underneath. Smooth easing
          so it never appears to jump, never reaches 100% on the fake
          timer (parent unmount handles the final flip). */}
      <div className="mt-8 max-w-xs mx-auto">
        <div
          className="relative h-1 rounded-full overflow-hidden"
          style={{ background: `${C.gold}1a` }}
          role="progressbar"
          aria-valuenow={pctLabel}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Création de l'album en cours"
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width: `${progress * 100}%`,
              background: `linear-gradient(90deg, ${C.goldLight}, ${C.gold}, ${C.goldDark})`,
              transition: 'width 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          />
        </div>
        <div
          className="mt-2 text-[11px] tabular-nums tracking-wide text-center"
          style={{
            color: C.goldDark,
            fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
            fontFeatureSettings: '"tnum"',
          }}
        >
          {pctLabel}%
        </div>
      </div>
    </div>
  );
}

// (MagicStep removed alongside the fake-checklist redesign — StepMagic now
// shows a single minimal ring + rotating tip instead of staged checkpoints.)


// ═══════════════════════════════════════════════════════════════════════════════
// ─── Step 4: Scrolling Album Preview ─────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function StepPreview({
  layout,
  photos,
  selectedCover,
  session,
  order,
  extraPages,
  pricePerPage,
  destination,
  year,
  logoUrl,
  editTo,
  onEdit,
  onAddPages,
  onConfirm,
}: {
  layout: LayoutPlan | null;
  photos: (Photo & { signed_url: string | null })[];
  selectedCover: Cover | null;
  session: AlbumSession | null;
  /** Order row + live extra-page count so the preview can show the up-to-date
   *  price (same computeOrderPrice the checkout step uses — never drifts). */
  order: Order | null;
  extraPages: number;
  pricePerPage: number;
  destination: string;
  year: string;
  logoUrl: string | null;
  /** FIX 1 — base editor path; enables double-tap/click on the cover + pages
   *  to open the editor at that page (cover lands on the default cover view). */
  editTo?: string;
  onEdit: () => void;
  onAddPages: () => void;
  onConfirm: () => void;
}) {
  const navigate = useNavigate();
  // Up-to-date price — mirrors the checkout summary exactly (base bundle price
  // while within the order's included page allowance; + pricePerPage per page
  // beyond it, across Solo/Duo/Trio).
  const priceInfo = order
    ? computeOrderPrice({
        total_price: order.total_price,
        extra_pages_cost: order.extra_pages_cost,
        relief_3d_cost: order.relief_3d_cost,
        extraPages,
        pricePerPage,
      })
    : null;
  // Order label for the WhatsApp escape-hatch messages so the team knows which
  // order instantly (e.g. "#D307").
  const orderRef = order?.shopify_order_name || null;
  // FIX 1 — double-tap/click the cover preview → open the editor (defaults to
  // the cover view on a fresh mount).
  const coverTap = useTapToOpen(editTo ? () => navigate(editTo) : undefined);
  const pages = layout?.pages ?? [];
  // « PAGE DE FIN » (lib/outroPage.ts) — the brand outro image those pages
  // draw. Same cached settings row the flow already reads.
  const { data: previewSettings } = useQuery({ queryKey: ['app-settings'], queryFn: () => settingsApi.get() });
  const outroUrl = previewSettings?.settings?.outro_page_2_url ?? null;
  const photoUrlMap = useMemo(() => {
    const m = new Map<string, string>();
    photos.forEach(p => { if (p.signed_url) m.set(p.id, p.signed_url); });
    return m;
  }, [photos]);

  // The spreads AS THE BOOK PRINTS THEM (bookStructure.ts). A plain book:
  // (1, 2), (3, 4)… as always. A themed book opens on its S1 page alone on the
  // right, then (2, 3), (4, 5)… — the preview must not pair pages the printed
  // book never shows side by side.
  const themedBook = isThemedBook(pages as ReadonlyArray<BookPageLike>);
  const spreads = useMemo(() => printedSpreads(pages, themedBook), [pages, themedBook]);
  // The album's gap, with the spine side of the book these pages make — so a
  // base-rules frame sits on the binding side of each page.
  const bookGap = useMemo(() => withThemedBook(photoGapOf(session), themedBook), [session, themedBook]);

  // Fetch templates once so PreviewPageCard can resolve page.template_id
  // → background_url and overlay the same PNG the editor shows.
  const { data: templatesData } = useQuery({
    queryKey: ['preview-templates'],
    queryFn: () => templatesApi.list(),
    staleTime: 60_000,
  });
  const templatesById = useMemo(() => {
    const m = new Map<string, LayoutTemplate>();
    for (const t of templatesData?.templates ?? []) m.set(t.id, t);
    return m;
  }, [templatesData]);

  // Cover spread data from the editor's saved session.cover_customizations.
  // We pass these straight to <CoverSpreadPreview> — the same shape the
  // editor's CoverView consumes, so the customer sees their design 1:1.
  const cust = (session?.cover_customizations as Record<string, unknown> | null) ?? {};
  const spineCfg = (cust.spineConfig as Record<string, unknown> | undefined) ?? undefined;
  const backBg  = (cust.backCoverBgColor as string | null | undefined) ?? null;
  const frontBg = (cust.frontCoverBgColor as string | null | undefined) ?? null;
  const hideFrontCover = (cust.hideFrontCoverImage as boolean | undefined) ?? false;
  // Task 14/15 — per-slot photo fit (scale + offset) for templated pages,
  // saved by the editor into cover_customizations. Threaded into
  // PreviewPageCard so the preview's Canva-style compositing matches the
  // editor 1:1.
  const templatePhotoFits =
    (cust.templatePhotoFits as Record<number, Record<number, PhotoFitInSlot>> | undefined) ?? undefined;
  const coverDesignTransform =
    (cust.coverDesignTransform as { x: number; y: number; scale: number } | undefined) ?? undefined;
  const coverDestText = titlePrefill(session, destination);
  const coverYearText = (session?.cover_year_text as string | null) ?? year ?? '';
  const coverZoneOverrides = (cust.zoneOverrides as Record<number, TextZoneOverride> | undefined) ?? {};
  const coverExtraZones = (cust.extraZones as ExtraTextZone[] | undefined) ?? [];
  const savedBackImgs = (cust.backCoverImages as { storage_path?: string; signed_url?: string | null }[] | undefined) ?? [];
  // Client's own front-cover image on a blank cover — { storage_path } only;
  // re-sign on mount (same RLS-protected prefix as the back-cover images).
  const blankCoverPath =
    (cust.blankCoverImage as { storage_path?: string } | null | undefined)?.storage_path ?? null;
  // Display-only pan/zoom of that photo (parity with the editor + PDF).
  const blankCoverTransform =
    (cust.blankCoverTransform as { x: number; y: number; scale: number } | undefined) ?? undefined;

  // Re-sign back-cover URLs through the worker on every mount. The saved
  // signed_url has a 1h TTL and is often expired by the time the customer
  // returns to preview; the browser's anon-key client can't sign these
  // because of bucket RLS, hence the 400s we saw in production.
  const [backImgs, setBackImgs] = useState(savedBackImgs);
  useEffect(() => {
    const paths = savedBackImgs.map((i) => i.storage_path).filter((p): p is string => !!p);
    if (paths.length === 0) { setBackImgs(savedBackImgs); return; }
    let cancelled = false;
    (async () => {
      try {
        const { urls } = await photosApi.signBackCoverUrls(paths);
        if (cancelled || !urls?.length) return;
        const byPath = new Map(urls.map((u) => [u.storage_path, u.signed_url]));
        setBackImgs(savedBackImgs.map((i) => ({
          ...i,
          signed_url: i.storage_path ? byPath.get(i.storage_path) ?? i.signed_url ?? null : i.signed_url ?? null,
        })));
      } catch (e) {
        console.warn('[Preview] back-cover URL refresh failed:', (e as Error).message);
        setBackImgs(savedBackImgs);
      }
    })();
    return () => { cancelled = true; };
    // savedBackImgs identity changes each render but its meaningful content
    // is captured by joined storage_paths — depend on that instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedBackImgs.map((i) => i.storage_path).join('|')]);

  // Re-sign the blank-cover front image on mount (1h TTL, RLS-blocked from the
  // browser — route through the worker like the back-cover images).
  const [blankCoverImageUrl, setBlankCoverImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blankCoverPath) { setBlankCoverImageUrl(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { urls } = await photosApi.signBackCoverUrls([blankCoverPath]);
        if (!cancelled) setBlankCoverImageUrl(urls?.[0]?.signed_url ?? null);
      } catch (e) {
        console.warn('[Preview] blank-cover URL refresh failed:', (e as Error).message);
        if (!cancelled) setBlankCoverImageUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [blankCoverPath]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-56">
      <div className="text-center mb-6">
        <h1 className="text-2xl md:text-3xl font-normal mb-1" style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: C.dark }}>
          Aperçu de votre album
        </h1>
        <p className="text-xs" style={{ color: C.muted }}>
          {pages.length} pages · {photos.length} photos
        </p>
        {priceInfo && (
          <div className="mt-1.5 flex flex-col items-center gap-0.5">
            <span
              className="text-lg font-semibold tabular-nums"
              style={{ fontFamily: "'Fraunces', Georgia, serif", color: C.goldDark }}
            >
              {priceInfo.total} MAD
            </span>
            {extraPages > 0 && (
              <span className="text-[11px]" style={{ color: C.muted }}>
                dont {extraPages} page{extraPages > 1 ? 's' : ''} sup. · +{priceInfo.extraCost} MAD
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Full cover spread — same component the editor uses. Height trimmed
            (was 60vh/460px) so the first interior spread peeks above the fold
            on a phone, signalling there's more below the cover.
            NO fixed wrapper height: the spread is width-constrained on a phone
            (its real height = (100cqi - 56px) / 1.414, shorter than 440px), so
            reserving a tall fixed box made CoverSpreadPreview's centring layout
            leave equal empty bands above + below. Instead we pass the cap into
            heightStyle and let the auto-height wrapper hug the spread — the
            empty bands collapse and the interior spreads rise toward the cover. */}
      <div className="mb-3" {...coverTap}>
        <CoverSpreadPreview
          selectedCover={selectedCover}
          destination={destination}
          year={year}
          coverDestText={coverDestText}
          coverYearText={coverYearText}
          coverZoneOverrides={coverZoneOverrides}
          coverExtraZones={coverExtraZones}
          spineConfig={spineCfg}
          logoUrl={logoUrl}
          backCoverImages={backImgs}
          backCoverBgColor={backBg}
          frontCoverBgColor={frontBg}
          hideFrontCoverImage={hideFrontCover}
          coverDesignTransform={coverDesignTransform}
          blankCoverImageSrc={blankCoverImageUrl}
          blankCoverTransform={blankCoverTransform}
          heightStyle="min(52vh, 440px)"
          coverElements={cust.coverElements}
          photoUrl={(pid) => photoUrlMap.get(pid) ?? null}
        />
      </div>

      {/* ── Interior spreads — open-book presentation ─────────────────────
            Each spread is ONE card (soft drop-shadow, rounded outer corners)
            with a centre gutter: a fold gradient + a thin highlight so it reads
            as an open book, not two adjacent photos. Pure CSS — no images/JS,
            zero added load. Double-tap-to-edit still works per card. */}
      <div className="space-y-5">
        {spreads.map((spread, idx) => (
          // STABLE IDENTITY KEY (preview fix 2): keyed by the spread's first
          // photo, NOT the array index. Photo ids are unique per album; blank
          // added pages fall back to idx.
          <div
            key={spread.left?.photos?.[0] ?? spread.right?.photos?.[0] ?? `empty-${idx}`}
            className="relative mx-auto flex overflow-hidden"
            // width:fit-content so the shadow hugs the actual page edges (no cream
            // band inside the frame). Crisp 3px corners read as a physical book,
            // not a 14px UI card. The book sits ON the cream, casting a real
            // drop-shadow beneath it.
            style={{
              width: 'fit-content',
              maxWidth: '100%',
              borderRadius: 3,
              boxShadow: '0 18px 36px rgba(30,22,14,0.26), 0 5px 12px rgba(30,22,14,0.16)',
            }}
          >
            <PreviewPageCard photoGap={bookGap} page={spread.left} side="left" photoUrlMap={photoUrlMap} label={`${spread.leftNo ?? ''}`} templatesById={templatesById} templatePhotoFits={templatePhotoFits} editTo={editTo} outroUrl={outroUrl} />
            <PreviewPageCard photoGap={bookGap} page={spread.right} side="right" photoUrlMap={photoUrlMap} label={`${spread.rightNo}`} templatesById={templatesById} templatePhotoFits={templatePhotoFits} editTo={editTo} outroUrl={outroUrl} />
            {/* Centre gutter — the binding. A soft shadow dipping into both inner
                page edges (pages curving into the spine) + a crisp dark crease so
                the two pages read as ONE open spread, not two adjacent tiles. */}
            <div
              className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2"
              style={{
                width: '12%',
                background:
                  'linear-gradient(90deg, rgba(30,22,14,0) 0%, rgba(30,22,14,0.06) 28%, rgba(30,22,14,0.24) 46%, rgba(30,22,14,0.44) 50%, rgba(30,22,14,0.24) 54%, rgba(30,22,14,0.06) 72%, rgba(30,22,14,0) 100%)',
              }}
            />
            {/* crease line + a thin highlight beside it (paper catching light) */}
            <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2" style={{ width: 1.5, background: 'rgba(20,14,8,0.55)' }} />
            <div className="pointer-events-none absolute inset-y-0" style={{ left: 'calc(50% + 0.75px)', width: 1, background: 'rgba(255,255,255,0.28)' }} />
            {/* Outer page-edge shadows — the thickness of the book's closed pages
                curving away at each side, so the spread reads as a solid object. */}
            <div className="pointer-events-none absolute inset-y-0 left-0" style={{ width: '5%', background: 'linear-gradient(90deg, rgba(30,22,14,0.24) 0%, rgba(30,22,14,0) 100%)' }} />
            <div className="pointer-events-none absolute inset-y-0 right-0" style={{ width: '5%', background: 'linear-gradient(270deg, rgba(30,22,14,0.24) 0%, rgba(30,22,14,0) 100%)' }} />
          </div>
        ))}
      </div>

      {/* ── Sticky bottom action bar ──────────────────────────────────────
            Confirmer is the single primary (full-width gold). Modifier +
            Ajouter are demoted to quiet text links. A WhatsApp escape hatch
            sits underneath — each chip opens WhatsApp pre-filled AND tags the
            order number so the team knows the order instantly. */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4 pt-5 pb-4 z-30"
        style={{ background: `linear-gradient(to top, ${C.beige} 72%, transparent)` }}
      >
        <div className="max-w-md mx-auto">
          {/* Primary */}
          <button
            onClick={onConfirm}
            className="w-full py-3.5 rounded-xl text-[15px] font-bold transition-all flex items-center justify-center gap-2 active:scale-[0.99]"
            style={{
              background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
              color: C.white,
              boxShadow: `0 6px 20px ${C.gold}45`,
            }}
          >
            <span className="text-base leading-none">✓</span>
            Confirmer mon album
          </button>

          {/* Demoted secondary actions — text links */}
          <div className="mt-2.5 flex items-center justify-center gap-5 text-[13px]">
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-1 font-medium underline underline-offset-2 decoration-1 active:opacity-70"
              style={{ color: C.muted }}
            >
              <span aria-hidden>✏️</span> Modifier
            </button>
            <button
              onClick={onAddPages}
              className="inline-flex items-center gap-1 font-medium underline underline-offset-2 decoration-1 active:opacity-70"
              style={{ color: C.muted }}
            >
              <span aria-hidden>＋</span> Ajouter 2 pages
            </button>
          </div>

        </div>
      </div>

      {/* Floating WhatsApp widget — overlays (doesn't take content space).
          Tap → a small popup "drops up" with quick messages, each pre-filled
          and order-tagged. */}
      <PreviewWhatsAppWidget orderRef={orderRef} />
    </div>
  );
}

// ── Floating WhatsApp escape-hatch widget (preview page) ─────────────────────
// A single green bubble that sits above the sticky action bar. Tapping it opens
// a compact popover of quick-pick messages — each opens WhatsApp pre-filled AND
// tags the order number so the team knows the order instantly. Reuses the
// shared wa.me deep-link (waSupportLink) — no second integration.
function PreviewWhatsAppWidget({ orderRef }: { orderRef: string | null }) {
  const [open, setOpen] = useState(false);
  const tag = orderRef ? ` (commande ${orderRef})` : '';
  // ONE dominant option — the escape hatch that saves the sale — and one quiet
  // secondary link for everything else. Both pre-filled + order-tagged.
  const handoffMsg = `Bonjour 👋 je préfère que vous vous occupiez de mon album à ma place${tag}.`;
  const questionMsg = `Bonjour 👋 j'ai une question sur mon album${tag}.`;
  return (
    <div className="fixed right-4 z-40" style={{ bottom: 'calc(148px + env(safe-area-inset-bottom, 0px))' }}>
      {open && (
        <>
          {/* click-away catcher */}
          <div className="fixed inset-0 -z-10" onClick={() => setOpen(false)} />
          {/* Popup — slides UP out of the bubble (transform-origin bottom-right),
              compact so it barely touches the album. The escape-hatch is the
              loud green pill; "j'ai une question" sits under it as a quiet link. */}
          <div
            className="absolute bottom-full right-0 mb-3 w-60"
            style={{ transformOrigin: 'bottom right', animation: 'waSlide 200ms cubic-bezier(0.22,1,0.36,1)' }}
          >
            <a
              href={waSupportLink(handoffMsg)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 rounded-2xl px-4 py-3.5 transition-transform active:scale-95"
              style={{ background: '#25D366', color: '#fff', boxShadow: '0 12px 28px rgba(37,211,102,0.42), 0 3px 8px rgba(0,0,0,0.16)' }}
            >
              <svg width="20" height="20" viewBox="0 0 32 32" fill="#fff" aria-hidden="true" className="shrink-0">
                <path d="M16 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.2 28.8l6.55-1.71A12.74 12.74 0 0 0 16 28.8c7.06 0 12.8-5.73 12.8-12.8S23.06 3.2 16 3.2zm5.83 15.4c-.32-.16-1.88-.93-2.17-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1 1.25-.18.21-.37.24-.69.08-.32-.16-1.34-.49-2.55-1.57-.94-.84-1.58-1.88-1.77-2.2-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.55.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.55-.08-.16-.72-1.73-.99-2.37-.26-.62-.53-.53-.72-.54h-.61c-.21 0-.55.08-.84.4-.29.32-1.1 1.08-1.1 2.63 0 1.55 1.13 3.05 1.29 3.26.16.21 2.22 3.39 5.39 4.76.75.32 1.34.52 1.8.67.76.24 1.45.21 2 .13.61-.09 1.88-.77 2.14-1.51.26-.74.26-1.37.18-1.51-.08-.13-.29-.21-.61-.37z" />
              </svg>
              <span className="font-bold leading-tight" style={{ fontSize: 14 }}>Occupez-vous-en pour moi</span>
            </a>
            <div className="mt-2 flex justify-center">
              <a
                href={waSupportLink(questionMsg)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="inline-block rounded-full px-3 py-1 text-[12px] underline underline-offset-2 decoration-1 transition-opacity active:opacity-70"
                style={{ background: 'rgba(255,255,255,0.9)', color: C.muted, boxShadow: '0 2px 8px rgba(30,22,14,0.12)' }}
              >
                J'ai juste une question
              </a>
            </div>
          </div>
        </>
      )}
      {/* the bubble */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Contacter Storiz sur WhatsApp"
        className="w-13 h-13 rounded-full flex items-center justify-center transition-transform active:scale-90 hover:scale-105"
        style={{ width: 52, height: 52, background: '#25D366', boxShadow: '0 6px 20px rgba(37,211,102,0.45), 0 2px 6px rgba(0,0,0,0.2)' }}
      >
        <svg width="27" height="27" viewBox="0 0 32 32" fill="#fff" aria-hidden="true" style={{ transition: 'transform 160ms', transform: open ? 'rotate(8deg)' : 'none' }}>
          <path d="M16 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.2 28.8l6.55-1.71A12.74 12.74 0 0 0 16 28.8c7.06 0 12.8-5.73 12.8-12.8S23.06 3.2 16 3.2zm0 23.31a10.5 10.5 0 0 1-5.36-1.47l-.38-.23-3.89 1.02 1.04-3.79-.25-.39a10.55 10.55 0 0 1-1.62-5.65c0-5.84 4.75-10.59 10.6-10.59 2.83 0 5.49 1.1 7.49 3.1a10.53 10.53 0 0 1 3.1 7.5c0 5.84-4.75 10.5-10.71 10.5zm5.83-7.91c-.32-.16-1.88-.93-2.17-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1 1.25-.18.21-.37.24-.69.08-.32-.16-1.34-.49-2.55-1.57-.94-.84-1.58-1.88-1.77-2.2-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.55.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.55-.08-.16-.72-1.73-.99-2.37-.26-.62-.53-.53-.72-.54h-.61c-.21 0-.55.08-.84.4-.29.32-1.1 1.08-1.1 2.63 0 1.55 1.13 3.05 1.29 3.26.16.21 2.22 3.39 5.39 4.76.75.32 1.34.52 1.8.67.76.24 1.45.21 2 .13.61-.09 1.88-.77 2.14-1.51.26-.74.26-1.37.18-1.51-.08-.13-.29-.21-.61-.37z" />
        </svg>
      </button>
      <style>{`@keyframes waSlide{from{opacity:0;transform:translateY(14px) scale(.94)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}

// D76 render-parity fix (2026-05-20): the preview used to size the <img>
// at 100% × 100% of the slot and apply CSS `transform: translate(tx%, ty%)`
// on top. When the face-aware translate was non-zero, the slot-sized image
// shifted off-slot exposing the slot bg as a "cream halo" on the opposite
// edge — the exact pattern the storiz-render-parity skill warns against.
// PreviewPhotoSlot now mirrors PhotoSlot: cover-fit overflow sizing via
// pdfMirrorBox + a defensive clamp + maxWidth:'none' so Tailwind preflight
// can't clamp our intentional > 100% widths back to 100%.
function PreviewPhotoSlot({
  url, slotAR, translateX, translateY, scale, rotation, layout, fill, crop, eager = false,
}: {
  url: string | null;
  slotAR: number;
  /** REAL CROP (2026-07-24) — kept window of the original photo (0..1). The
   *  cover-fit runs on the cropped dims and the <img> sits inside a clipping
   *  wrapper, exactly like the editor + PDF (render parity). */
  crop?: { x: number; y: number; w: number; h: number } | null;
  translateX: number;
  translateY: number;
  scale: number;
  /** Canva-style manual rotation (clockwise degrees). Mirrors PhotoSlot:
   *  rotates the <img> about its own centre, with the cover box grown by
   *  rotationCoverGrowth (inside pdfMirrorBox) so the slot stays covered. */
  rotation: number;
  /** Page layout (e.g. '1-up') — drives the solo-landscape fit-width rule. */
  layout?: string;
  /** FILL page (soloFit.ts) — a 1-up landscape cover-fits. */
  fill?: boolean;
  /** Preview fix 1 — true once the card is near the viewport: load+decode NOW
   *  so scroll-back never catches an undecoded (white) image. */
  eager?: boolean;
}) {
  const [naturalDims, setNaturalDims] = useState<{ w: number; h: number } | null>(null);
  // Preview fix 3 — soft beige pulse while the image hasn't decoded yet, so a
  // still-loading slot reads as "loading", never as broken white.
  const skeleton = !naturalDims && (
    <div className="absolute inset-0 animate-pulse" style={{ background: `${C.gold}12` }} />
  );

  if (!url) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="w-4 h-4 rounded-full border border-dashed" style={{ borderColor: `${C.gold}30` }} />
      </div>
    );
  }

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const t = e.currentTarget;
    if (t.naturalWidth && t.naturalHeight) {
      setNaturalDims({ w: t.naturalWidth, h: t.naturalHeight });
    }
  };

  // Solo (1-up) landscape → fit page width (full image, cream letterbox),
  // matching the editor + PDF via soloFit.ts + pdfMirrorBox's fitWidth branch.
  // REAL CROP: the kept window IS the photo for the fit + cover math.
  const cropRect = crop && crop.w > 0 && crop.h > 0 ? crop : null;
  const srcW = naturalDims ? (cropRect ? naturalDims.w * cropRect.w : naturalDims.w) : null;
  const srcH = naturalDims ? (cropRect ? naturalDims.h * cropRect.h : naturalDims.h) : null;
  const fitWidth = soloLandscapeFitWidth(layout, srcW ?? undefined, srcH ?? undefined, fill);
  const box = pdfMirrorBox(
    srcW,
    srcH,
    slotAR,
    translateX,
    translateY,
    scale,
    rotation,
    fitWidth,
  );

  // Before naturalDims arrive (first render), fall back to slot-fill +
  // object-fit:cover. Still covers the slot, just without the translate.
  // Once onLoad fires the next render computes the real overflow box.
  if (!box.valid) {
    return (
      <>
        {skeleton}
        <img
          src={url}
          alt=""
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          draggable={false}
          onLoad={handleLoad}
          style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </>
    );
  }

  // B1/B2 fix (2026-06-09): honor ANY deliberate positioning (pan / rotation /
  // shrink) by rendering the RAW cover box — identical to the editor resting
  // render and the PDF — so a panned photo isn't re-centred and a rotated photo
  // pivots about its true centre. The coverage clamp applies ONLY to the
  // untouched default slot (cream-halo guard for stale dims). Editor + preview
  // MUST stay in lockstep here (render parity).
  const userPositioned = scale < 1 || translateX !== 0 || translateY !== 0 || rotation !== 0;
  const safeWidth = userPositioned ? box.width : Math.max(100, box.width);
  const safeHeight = userPositioned ? box.height : Math.max(100, box.height);
  const safeLeft = userPositioned ? box.left : Math.max(100 - safeWidth, Math.min(0, box.left));
  const safeTop = userPositioned ? box.top : Math.max(100 - safeHeight, Math.min(0, box.top));

  // Clipping crop-window wrapper (mirrors the editor). Without a crop the
  // wrapper is a plain box the image fills → unchanged rendering.
  const inner = cropRect
    ? {
        left: `${(-cropRect.x / cropRect.w) * 100}%`,
        top: `${(-cropRect.y / cropRect.h) * 100}%`,
        width: `${(1 / cropRect.w) * 100}%`,
        height: `${(1 / cropRect.h) * 100}%`,
      }
    : { left: '0%', top: '0%', width: '100%', height: '100%' };
  return (
    <div
      style={{
        position: 'absolute',
        left: `${safeLeft}%`,
        top: `${safeTop}%`,
        width: `${safeWidth}%`,
        height: `${safeHeight}%`,
        overflow: 'hidden',
        // Manual rotation — mirrors PhotoSlot exactly (rotates crop + image
        // together about the window centre). At 0° this is a no-op.
        transform: `rotate(${rotation}deg)`,
        transformOrigin: 'center center',
      }}
    >
      <img
        src={url}
        alt=""
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        onLoad={handleLoad}
        style={{
          position: 'absolute',
          ...inner,
          maxWidth: 'none',
          maxHeight: 'none',
          display: 'block',
          objectFit: 'cover',
        }}
      />
    </div>
  );
}

// ── Preview fix 1: pre-decode window ("overscan" for a fully-mounted list) ──
// Every spread is in the DOM, so the white-on-scroll-back came from
// `loading="lazy"` images being RE-DECODED from scratch each time they
// re-entered the viewport. This hook latches `true` once the card comes
// within ~2 viewport-heights of the screen; its images then switch to eager
// and stay decoded, so scroll-back always finds pixels ready. One-way latch —
// far-away pages stay lazy until first approached (no memory blowup on
// 100-page albums), and there's no eager↔lazy thrash while scrolling.
function useNearViewport(ref: React.RefObject<HTMLElement | null>, margin = '200%'): boolean {
  const [near, setNear] = useState(false);
  useEffect(() => {
    if (near) return; // latched
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setNear(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setNear(true); },
      { rootMargin: margin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, near, margin]);
  return near;
}

const PreviewPageCard = memo(function PreviewPageCard({
  page,
  photoUrlMap,
  label,
  templatesById,
  templatePhotoFits,
  editTo,
  photoGap,
  outroUrl,
}: {
  page: LayoutPage | null;
  photoUrlMap: Map<string, string>;
  /** « Page de fin » pages draw this brand image (outro_page_2_url), full page. */
  outroUrl?: string | null;
  /** B5 — the album's gap; rendered at this card's width from the same fraction the print uses. */
  photoGap: PhotoGap;
  label: string;
  templatesById: Map<string, LayoutTemplate>;
  /** Which half of the open-book spread this page is. Rounds ONLY the outer
   *  corners and drops the per-card shadow so the two pages read as one book
   *  (the spread container owns the shadow + centre fold). */
  side?: 'left' | 'right';
  /** Task 14/15 — per-slot photo fit keyed by page number → slot index.
   *  Threaded into TemplatedPhotoSlot so the preview matches the editor's
   *  saved framing 1:1 (Canva-style compositing parity). */
  templatePhotoFits?: Record<number, Record<number, PhotoFitInSlot>>;
  /** FIX 1 — base editor path (`/editor/{id}/{album}/edit`). When set, the
   *  card is tappable: double-tap (touch) / click (desktop) opens the editor
   *  at THIS page via the existing ?focusPage deep link. */
  editTo?: string;
}) {
  // Pixel dimensions of the card — TemplatedPhotoSlot needs these to scale
  // unit-space clip-paths into pixel space. ResizeObserver mirrors the
  // pattern Task 13 added to AlbumEditorPage's PageView. Hooks must be
  // declared before the early `!page` return.
  const pageRef = useRef<HTMLDivElement | null>(null);
  const [pageDims, setPageDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setPageDims({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // FIX 1 — double-tap (touch) / double-click (desktop) a preview page to open
  // the editor AT this page. The how-to is communicated by one subtle hint line
  // in StepPreview, so the card itself carries no per-card affordance.
  const navigate = useNavigate();
  const openProps = useTapToOpen(
    editTo && page ? () => navigate(`${editTo}?focusPage=${page.page}`) : undefined,
  );

  // Preview fix 1 — this card's images go eager (pre-decoded) once the card is
  // within ~2 screens of the viewport. See useNearViewport above.
  const eager = useNearViewport(pageRef);

  // Pages are square-cornered tiles that butt flush together; the spread
  // container owns the 3px outer corner-clip, the drop-shadow and the centre
  // gutter, so each page here carries no rounding or shadow of its own — that's
  // what made them read as separate floating cards before.
  const PAGE_W = 'min(44vw, 300px)';

  if (!page) {
    return (
      <div className="flex items-center justify-center" style={{ width: PAGE_W, aspectRatio: '200/297', background: C.white }}>
        <span className="text-xs" style={{ color: C.muted }}>—</span>
      </div>
    );
  }

  // « PAGE DE FIN » (lib/outroPage.ts) — the brand outro image cover-fitted to
  // the page, as the PDF draws it (embedBookendPage). No image → a white page,
  // as the print does.
  if (isOutroPage(page)) {
    return (
      <div ref={pageRef} {...openProps} data-outro-page="" className="overflow-hidden relative" style={{ width: PAGE_W, aspectRatio: '200/297', background: C.white }}>
        {outroUrl && !/\.pdf(\?|$)/i.test(outroUrl) && (
          <img src={outroUrl} alt="Page de fin" className="absolute inset-0 w-full h-full object-cover" loading={eager ? 'eager' : 'lazy'} decoding="async" />
        )}
      </div>
    );
  }

  const slots = getSlots(page.layout);
  // Same slot_positions the editor + PDF read. Apply them here so the
  // preview shows the customer's actual framing (face-centered crops,
  // manual pans/zooms) — without this the preview rendered cover-fit
  // default while the editor showed the saved positions, which read as
  // "preview ≠ editor". Format matches `SlotPosition` from the editor:
  // {x, y} are translate offsets in % of slot dimensions, scale is a
  // cover-fit multiplier.
  const slotPositions = (page.slot_positions ?? {}) as Record<
    string,
    { x?: number; y?: number; scale?: number; rotation?: number; _v?: 2 }
  >;
  // WIDE-TOP derives its two slot ARs (and row heights) from `top_h_frac`
  // exactly like the editor + PDF, so the three pipelines render the same
  // geometry. Other layouts use the static SLOT_AR_BY_LAYOUT table.
  const wideTopFrac =
    page.layout === 'wide-top'
      ? (page.top_h_frac ?? computeWideTopFrac(WIDE_TOP_AR_MIN))
      : null;
  const slotARs =
    wideTopFrac !== null
      ? wideTopSlotARs(wideTopFrac)
      : photoGapSlotArs(page.layout, photoGap.mm, page.top_h_frac, photoGap.baseRules);
  // Resolve the page's template (if any) so we can use its decorative
  // overlay SVG and slot rectangles. After the Phase 1.5 model swap the
  // decorative overlay renders ON TOP of the photos (top layer); the photos
  // are positioned at template.slots[i] instead of the grid.
  const pageWithTemplate = page as LayoutPage & { template_id?: string | null };
  const template = pageWithTemplate.template_id ? templatesById.get(pageWithTemplate.template_id) : null;
  const isTemplated = !!template;
  // STEP 3 — a THEMED page (paper + photos, pieces and text as layers): the
  // same layers and rules as the editor and the print (ThemedPageView).
  const themed = (page as { theme?: string | null }).theme ? page as unknown as Parameters<typeof ThemedPageView>[0]['page'] : null;
  if (themed) {
    return (
      <div ref={pageRef} {...openProps} className="overflow-hidden relative" style={{ width: PAGE_W, aspectRatio: '200/297', background: C.white }}>
        <ThemedPageView page={themed} photoUrl={(id) => photoUrlMap.get(id) ?? null} />
      </div>
    );
  }
  return (
    <div
      ref={pageRef}
      {...openProps}
      className="overflow-hidden relative"
      style={{ width: PAGE_W, aspectRatio: '200/297', background: C.white }}
    >
      {/* B5 — no padding and no fixed 2 px: the gap is the album's, at this
          card's width, and the edges stay flush like the print. */}
      {!isTemplated && (
        <div className="w-full h-full flex flex-col" style={{ gap: photoGapCss(photoGap.mm, PAGE_W), background: photoGap.mm > 0 ? photoGap.color : undefined, padding: framePaddingCss(photoGap, (Number(page.page) || 1) - 1, PAGE_W), ...(page.layout === '2-up-even' ? EVEN_CONTAINER : {}) }}>
          {slots.map((row, ri) => {
            // PARITY FIX (2026-07-14, #1102): 3-up prints top 60% / bottom 40%
            // (pdfGenerator getSlots: topH = H*0.6) but this preview rendered
            // equal flex-1 rows (50/50), so photos — and any text element laid
            // over them — appeared shifted vs the PDF/3D preview. Same
            // fractional-row mechanism as wide-top, now fed by the layout.
            const topFrac = wideTopFrac ?? (page.layout === '3-up' ? 0.6 : page.layout === '5-up' ? FIVE_UP_TOP_FRAC : page.layout === '4-up-main' ? FOUR_MAIN_TOP_FRAC : null);
            const even = page.layout === '2-up-even';
            return (
            <div
              key={ri}
              className={cn('flex', topFrac === null && !even && 'flex-1')}
              style={
                even ? { flex: `0 0 ${evenRowPct(page.top_h_frac)}%`, minHeight: 0 }
                : topFrac !== null
                  ? { ...twoRowFlexCss(topFrac, ri, photoGapCss(photoGap.mm, PAGE_W)), gap: photoGapCss(photoGap.mm, PAGE_W) }
                  : { gap: photoGapCss(photoGap.mm, PAGE_W), minHeight: 0 }
              }
            >
              {row.map(si => {
                const pid = page.photos[si];
                const url = (pid ? photoUrlMap.get(pid) : null) ?? null;
                const pos = slotPositions[`s${si}`];
                const tx = pos?.x ?? 0;
                const ty = pos?.y ?? 0;
                const scale = pos?.scale ?? 1;
                const rotation = pos?.rotation ?? 0;
                const slotAR = slotARs[si] ?? slotARs[0];
                return (
                  <div
                    key={si}
                    className="flex-1 rounded-sm overflow-hidden relative"
                    style={{ background: `${C.gold}08`, minHeight: 0 }}
                  >
                    <PreviewPhotoSlot
                      url={url}
                      slotAR={slotAR}
                      translateX={tx}
                      translateY={ty}
                      scale={scale}
                      rotation={rotation}
                      layout={page.layout}
                      fill={(page as { fill?: boolean }).fill}
                      crop={(pos as { crop?: { x: number; y: number; w: number; h: number } } | undefined)?.crop ?? null}
                      eager={eager}
                    />
                  </div>
                );
              })}
            </div>
            );
          })}
        </div>
      )}
      {/* Phase 2 / Task 15 — templated pages render via TemplatedPhotoSlot
          for editor/preview/PDF parity. Reads the customer's saved photoFit
          (scale + offset) from cover_customizations.templatePhotoFits via
          the templatePhotoFits prop. Non-interactive in preview. The
          Phase 1.5 decorative overlay is rendered on TOP (below) so it
          sits ABOVE the photos in the z-stack.
          Task 8 — when the template has an original SVG uploaded AND every
          slot carries a `dom_index`, the preview uses the SVG-href-swap
          render path (TemplatedPageWithSvgFetch) for pixel-perfect Canva
          reproduction. Non-interactive — no onSlotSelect / onSlotDrop /
          onFitChange. Otherwise it falls back to the legacy per-slot
          TemplatedPhotoSlot grid + overlay PNG. */}
      {/* ISSUE 6 (2026-07-05) — template cards used to fire their SVG fetch +
          photo composition ON MOUNT (all cards at once, no skeleton beyond a
          flat gray box) — the remaining "white pages on fast scroll". They now
          wait for the same near-viewport gate as normal cards and show the
          beige pulse until composed. */}
      {isTemplated && template && !eager && (
        <div className="absolute inset-0 animate-pulse" style={{ background: `${C.gold}12` }} />
      )}
      {isTemplated && template && eager && (
        template.original_svg_url && template.slots.every((s) => typeof s.dom_index === 'number') ? (
          <TemplatedPageWithSvgFetch
            svgUrl={template.original_svg_url}
            slots={template.slots}
            photoUrlsBySlotIndex={Object.fromEntries(
              template.slots.map((_, si) => {
                const photoId = page.photos[si];
                return [si, photoId ? (photoUrlMap.get(photoId) ?? null) : null];
              })
            )}
            photoFitsBySlotIndex={Object.fromEntries(
              template.slots.map((_, si) => [si, templatePhotoFits?.[page.page]?.[si]])
            )}
            pageWidthPx={pageDims.w}
            pageHeightPx={pageDims.h}
            // Read-only preview: no interactive, no onSlotSelect,
            // no onSlotDrop, no onFitChange.
          />
        ) : (
          <>
            <div className="absolute inset-0 pointer-events-none">
              {template.slots.map((s, si) => {
                const photoId = page.photos[si];
                const url = (photoId ? photoUrlMap.get(photoId) : null) ?? null;
                const fit = templatePhotoFits?.[page.page]?.[si];
                return (
                  <TemplatedPhotoSlot
                    key={si}
                    slot={s}
                    photoUrl={url}
                    photoFit={fit}
                    interactive={false}
                    pageWidthPx={pageDims.w}
                    pageHeightPx={pageDims.h}
                    // Read-only preview: droppableId is required by the prop
                    // contract but never wired to a drop handler here.
                    droppableId={`slot-preview:${page.page}:${si}`}
                  />
                );
              })}
            </div>
            {/* Phase 1.5 — decorative overlay rendered on TOP, not background.
                Contains only the template's decorative parts (borders, frames,
                captions); photo placeholders were stripped. Drawn last so it
                sits above the photo layer in the z-stack. */}
            {template.overlay_print_png_url && (
              <img
                src={template.overlay_print_png_url}
                alt=""
                className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                draggable={false}
                loading="lazy"
              />
            )}
          </>
        )
      )}
      <div className="absolute bottom-1 left-0 right-0 text-center">
        <span className="text-[8px] font-medium px-1.5 py-0.5 rounded" style={{ background: `${C.dark}60`, color: C.white }}>{label}</span>
      </div>
    </div>
  );
});



// ═══════════════════════════════════════════════════════════════════════════════
// ─── Step 7: Confirm & Submit ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function StepConfirm({
  order,
  totalPhotos,
  totalPages,
  extraPages,
  extraCost,
  pricePerPage,
  selectedCover,
  destination,
  currentAlbum,
  totalAlbums,
  onSubmit,
  onBack,
  isLocked,
}: {
  order: Order;
  totalPhotos: number;
  totalPages: number;
  extraPages: number;
  extraCost: number;
  pricePerPage: number;
  selectedCover: Cover | null;
  destination: string;
  currentAlbum: number;
  totalAlbums: number;
  onSubmit: () => void;
  onBack: () => void;
  isLocked?: boolean;
}) {
  const [submitting, setSubmitting] = useState(false);
  // Pages included in the base price for THIS order (migration 082). Shown to
  // the customer, so it must be the order's frozen figure and not today's
  // default — quoting "36 pages incluses" on an order sold with 50 would be a
  // visible bait-and-switch.
  const basePagesPerAlbum = resolveBasePagesPerAlbum(order.base_pages_per_album);

  const handleSubmit = async () => {
    if (isLocked) return;
    setSubmitting(true);
    await onSubmit();
    setSubmitting(false);
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <button onClick={onBack} className="flex items-center gap-1 text-xs font-medium mb-8 transition-colors hover:opacity-70" style={{ color: C.muted }}>
        ← Retour à l'aperçu
      </button>

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: C.dark }}>
          Confirmer votre album
        </h1>
        <p className="text-sm" style={{ color: C.muted }}>
          {totalAlbums > 1 ? `Album ${currentAlbum} sur ${totalAlbums}` : 'Vérifiez les détails avant de soumettre'}
        </p>
      </div>

      {/* Summary card */}
      <div className="rounded-2xl p-6 mb-6" style={{ background: C.white, border: `1px solid ${C.gold}20`, boxShadow: '0 2px 16px rgba(0,0,0,0.04)' }}>
        <div className="space-y-3">
          <SummaryRow label="Commande" value={order.shopify_order_name} />
          <SummaryRow label="Titre de l’album" value={destination || '—'} />
          <SummaryRow label="Design" value={selectedCover?.name ?? 'Vierge'} />
          <SummaryRow label="Photos" value={String(totalPhotos)} />
          <SummaryRow label="Pages" value={String(totalPages)} />
          <div className="h-px my-2" style={{ background: `${C.gold}15` }} />
          {/* Up-to-date price reflecting the album's CURRENT page count.
              `order.total_price` may already include extras the worker
              persisted on a prior submit, so we unwind them (via
              computeOrderPrice) before re-applying the live extras — never
              double-counting. Mirrors worker/src/routes/orders.ts. */}
          {(() => {
            const price = computeOrderPrice({
              total_price: order.total_price,
              extra_pages_cost: order.extra_pages_cost,
              relief_3d_cost: order.relief_3d_cost,
              extraPages,
              pricePerPage,
            });
            return (
              <>
                <SummaryRow label={`Prix de base (${basePagesPerAlbum} pages incluses)`} value={`${price.basePrice} MAD`} />
                {extraPages > 0 && (
                  <>
                    <SummaryRow label={`Pages supplémentaires (${extraPages} × ${pricePerPage} MAD)`} value={`+${price.extraCost} MAD`} highlight />
                    <div className="h-px my-1" style={{ background: `${C.gold}20` }} />
                    <SummaryRow label="Total" value={`${price.total} MAD`} bold />
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Extra pages notice */}
      {extraPages > 0 && (
        <div className="rounded-xl p-4 mb-6 text-sm" style={{ background: `${C.gold}10`, border: `1px solid ${C.gold}30` }}>
          <p className="font-semibold mb-1" style={{ color: C.goldDark }}>Pages supplémentaires</p>
          <p style={{ color: C.muted }}>
            Votre album contient <strong style={{ color: C.dark }}>{totalPages} pages</strong> dont <strong style={{ color: C.dark }}>{extraPages} pages au-delà des {basePagesPerAlbum} incluses</strong>.
            Chaque page supplémentaire est facturée <strong style={{ color: C.dark }}>{pricePerPage} MAD</strong>, soit un supplément de <strong style={{ color: C.goldDark }}>{extraCost} MAD</strong>.
          </p>
        </div>
      )}

      {/* Submit */}
      <button
        disabled={submitting || isLocked}
        onClick={handleSubmit}
        className="w-full py-4 rounded-2xl text-base font-bold tracking-wide transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: isLocked
            ? '#9CA3AF'
            : `linear-gradient(135deg, ${C.success}, #2ecc71)`,
          color: C.white,
          boxShadow: isLocked ? 'none' : `0 4px 20px ${C.success}40`,
        }}
      >
        {isLocked ? (
          'Album en cours de production'
        ) : submitting ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Soumission...
          </span>
        ) : (
          currentAlbum < totalAlbums
            ? `Confirmer & passer à l'album ${currentAlbum + 1} →`
            : '✓ Confirmer & soumettre'
        )}
      </button>
    </div>
  );
}

function SummaryRow({ label, value, highlight, bold }: { label: string; value: string; highlight?: boolean; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={cn('text-xs', bold ? 'font-bold' : 'font-medium')} style={{ color: bold ? C.dark : C.muted }}>
        {label}
      </span>
      <span className={cn('text-xs tabular-nums', bold ? 'font-bold text-sm' : 'font-semibold')} style={{ color: highlight ? C.gold : bold ? C.dark : C.dark }}>
        {value}
      </span>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── Step: Checkout (in-app, 2026-07-08) ─────────────────────────────────────
// Replaces the storefront /pages/checkout-storiz hop. The customer verifies
// the order + picks a payment mode HERE while the background print-original
// uploads keep draining — that dwell time used to be wasted staring at the
// blocking "Préparation de votre album" overlay first. The originals gate +
// /submit + draft-completion all run at the final button (handleCheckoutConfirm).
// ═══════════════════════════════════════════════════════════════════════════════

function StepCheckout({
  order,
  totalPages,
  extraPages,
  pricePerPage,
  destination,
  upload,
  submitting,
  error,
  onConfirm,
  onBack,
  isLocked,
}: {
  order: Order;
  totalPages: number;
  extraPages: number;
  pricePerPage: number;
  destination: string;
  upload: { doneCount: number; totalCount: number; doneBytes: number; totalBytes: number };
  submitting: boolean;
  error: string | null;
  onConfirm: (method: 'cod' | 'virement') => void;
  onBack: () => void;
  isLocked?: boolean;
}) {
  const [method, setMethod] = useState<'cod' | 'virement'>('cod');
  const shortRef = String(order.shopify_order_id ?? '').split('/').pop()?.slice(-6) ?? '——';
  const price = computeOrderPrice({
    total_price: order.total_price,
    extra_pages_cost: order.extra_pages_cost,
    relief_3d_cost: order.relief_3d_cost,
    extraPages,
    pricePerPage,
  });
  const uploading = upload.totalCount > 0 && upload.doneCount < upload.totalCount;
  const uploadPct = upload.totalBytes > 0 ? Math.min(99, Math.round((upload.doneBytes / upload.totalBytes) * 100)) : 0;

  // THE ADVANCE, SAID BEFORE SHE ORDERS (owner, 2026-09-29, « 1a »): every
  // client pays at least an advance, cash on delivery included. The figure is
  // the one the payment request will quote (the saved « RIB paiment » reply's
  // first), capped at this page's total; unknown → « une petite avance ».
  const { t } = useT();
  const { data: advanceData } = useQuery({
    queryKey: ['client-advance'],
    queryFn: () => settingsApi.advance(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const cod = codOptionCopy(t, advanceSplit(price.total, advanceData?.min_advance ?? null));

  const PayOption = ({ value, title, subtitle }: { value: 'cod' | 'virement'; title: string; subtitle: string }) => (
    <label
      className="flex items-start gap-3 rounded-xl p-4 cursor-pointer transition-all"
      style={{
        background: method === value ? `${C.gold}0D` : C.white,
        border: `2px solid ${method === value ? C.gold : `${C.gold}25`}`,
      }}
    >
      <input
        type="radio"
        name="storiz_payment_method"
        value={value}
        checked={method === value}
        onChange={() => setMethod(value)}
        className="mt-1 accent-current shrink-0"
        style={{ accentColor: C.gold }}
      />
      <span className="min-w-0">
        <span className="block text-sm font-bold" style={{ color: C.dark }}>{title}</span>
        <span className="block text-xs mt-0.5" style={{ color: C.muted }}>{subtitle}</span>
      </span>
    </label>
  );

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <button onClick={onBack} className="flex items-center gap-1 text-xs font-medium mb-8 transition-colors hover:opacity-70" style={{ color: C.muted }}>
        ← Retour à l&rsquo;aperçu
      </button>

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: C.dark }}>
          Confirmer votre commande
        </h1>
        <p className="text-sm" style={{ color: C.muted }}>
          Vérifiez les détails et choisissez votre mode de paiement
        </p>
      </div>

      {/* Order summary */}
      <div className="rounded-2xl p-6 mb-6" style={{ background: C.white, border: `1px solid ${C.gold}20`, boxShadow: '0 2px 16px rgba(0,0,0,0.04)' }}>
        <div className="space-y-3">
          <SummaryRow label="Référence" value={`#${shortRef}`} />
          {order.customer_name && <SummaryRow label="Nom" value={order.customer_name} />}
          {order.customer_phone && <SummaryRow label="Téléphone" value={order.customer_phone} />}
          {order.bundle && <SummaryRow label="Formule" value={order.bundle} />}
          {order.layout && <SummaryRow label="Mise en page" value={order.layout} />}
          <SummaryRow label="Pages" value={`${totalPages} pages`} />
          <SummaryRow label="Titre de l’album" value={destination || '—'} />
          {extraPages > 0 && (
            <>
              <SummaryRow label="Pages supplémentaires" value={`+${extraPages} pages`} />
              <SummaryRow label="Coût pages suppl." value={`${price.extraCost} MAD`} highlight />
            </>
          )}
          <SummaryRow label="Livraison" value="Gratuite 🎁" />
          <div className="h-px my-2" style={{ background: `${C.gold}15` }} />
          <SummaryRow label="Total" value={`${price.total} MAD`} bold />
        </div>
      </div>

      {/* Background print-upload reassurance — only while still draining */}
      {uploading && (
        <div className="rounded-xl p-4 mb-6" style={{ background: `${C.gold}0D`, border: `1px solid ${C.gold}30` }}>
          <p className="text-xs font-semibold mb-2" style={{ color: C.goldDark }}>
            Vos photos s&rsquo;envoient en qualité impression pendant que vous vérifiez votre commande&hellip; {uploadPct}%
          </p>
          <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: `${C.gold}15` }}>
            <div className="h-full transition-all" style={{ width: `${uploadPct}%`, background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})` }} />
          </div>
        </div>
      )}

      {/* Payment mode */}
      <p className="text-sm font-bold mb-3" style={{ color: C.dark }}>Mode de paiement</p>
      <div className="space-y-3 mb-6">
        <PayOption value="cod" title={cod.title} subtitle={cod.subtitle} />
        <PayOption value="virement" title="Virement bancaire" subtitle="Notre équipe vous contacte pour vous communiquer les coordonnées bancaires." />
      </div>

      {error && (
        <div className="rounded-xl p-4 mb-4 text-sm" role="alert" style={{ background: '#FEF2F2', border: `1px solid ${C.danger}40`, color: '#B91C1C' }}>
          {error}
        </div>
      )}

      <button
        disabled={submitting || isLocked}
        onClick={() => onConfirm(method)}
        className="w-full py-4 rounded-2xl text-base font-bold tracking-wide transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: isLocked ? '#9CA3AF' : `linear-gradient(135deg, ${C.gold}, ${C.goldDeep})`,
          color: C.white,
          boxShadow: isLocked ? 'none' : `0 4px 20px ${C.gold}40`,
        }}
      >
        {isLocked ? 'Album en cours de production' : submitting ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Confirmation&hellip;
          </span>
        ) : (
          'Confirmer ma commande →'
        )}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Step: Success ───────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function StepSuccess({
  order,
  nextUrl,
}: {
  order: Order;
  nextUrl: string | null;
}) {
  const [countdown, setCountdown] = useState(6);

  // Where to send the customer after the success countdown: always the Shopify
  // checkout page (via the COD form's `?next=…` when present, or the canonical
  // /pages/checkout-storiz URL otherwise). Never the storefront homepage —
  // the customer's confirmation must land them on payment, not on a marketing
  // page where it gets silently dropped.
  const finalRedirect = (() => {
    try { sessionStorage.removeItem('storiz_post_submit_next'); } catch { /* ignore */ }
    return getCheckoutRedirect(nextUrl, order);
  })();

  useEffect(() => {
    if (countdown <= 0) {
      window.location.href = finalRedirect;
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, finalRedirect]);

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      {/* Animated checkmark */}
      <div className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center"
        style={{ background: `${C.success}15` }}>
        <svg className="w-12 h-12" style={{ color: C.success }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", color: C.dark }}>
        Album prêt — finalisez la commande !
      </h1>
      <p className="text-sm mb-6 max-w-sm mx-auto leading-relaxed" style={{ color: C.muted }}>
        Votre album <strong style={{ color: C.dark }}>{order.shopify_order_name}</strong> est prêt.
        Dernière étape : confirmer votre commande et choisir le mode de paiement.
      </p>

      {/* Countdown redirect notice */}
      <div className="rounded-2xl px-5 py-4 mb-6 text-sm" style={{ background: `${C.gold}10`, border: `1px solid ${C.gold}25` }}>
        <p style={{ color: C.goldDark }}>
          Redirection vers le paiement dans <strong>{countdown}s</strong>...
        </p>
      </div>

      <div className="flex flex-col gap-3 max-w-xs mx-auto">
        <a
          href={finalRedirect}
          className="w-full py-3 rounded-xl text-sm font-bold transition-all text-center"
          style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.white, boxShadow: `0 4px 20px ${C.gold}40` }}
        >
          Continuer vers le paiement
        </a>
        {/* Storefront link kept as a quiet secondary so customers who got
            here mid-distraction can bail out — but it's no longer the
            prominent CTA. The big gold action above is the only path that
            takes the album to checkout. */}
        <a
          href="https://storiz.ma"
          className="w-full py-3 rounded-xl text-sm font-medium transition-all text-center"
          style={{ background: `${C.gold}10`, color: C.goldDark, border: `1px solid ${C.gold}20` }}
        >
          Retourner sur storiz.ma
        </a>
      </div>
    </div>
  );
}
